import ConfigurableTournament from './ConfigurableTournament';
import { Fragment, useState, useCallback, useMemo, useRef, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";
import { teamArtworkSrc, teamAvatarSrc, teamDisplayMode } from "../tournamentTeamArtwork";
import { useAuth } from "../AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faGear, faPen, faTimes, faTrophy, faUser } from "@fortawesome/free-solid-svg-icons";
import { getDefaultTournamentAccess, hasTournamentEntitlement, hasTournamentTier, loadTournamentAccess, TOURNAMENT_FORMATS, TOURNAMENT_PACKAGE_OPTIONS } from "../tournamentAccess";

/* ─── THEME ──────────────────────────────────────────────────────────── */
const C = {
  navy:     "var(--tour-bg)",
  navy2:    "var(--tour-bg-soft)",
  navy3:    "var(--tour-bg-elevated)",
  card:     "var(--tour-card)",
  border:   "var(--tour-border)",
  border2:  "var(--tour-border-strong)",
  gold:     "var(--tour-accent)",
  gold2:    "var(--tour-accent-strong)",
  goldDim:  "var(--tour-accent-soft)",
  goldMed:  "var(--tour-accent-border)",
  green:    "var(--tour-success)",
  greenDim: "var(--tour-success-soft)",
  red:      "var(--tour-danger)",
  blue:     "var(--tour-accent)",
  white:    "var(--tour-text)",
  w80:      "var(--tour-text-secondary)",
  w55:      "var(--tour-text-muted)",
  w35:      "var(--tour-text-faint)",
  w15:      "var(--tour-line)",
  w08:      "var(--tour-hover)",
};

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800;900&family=DM+Sans:wght@300;400;500;600;700&family=Space+Mono:wght@700&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html {
  scroll-behavior: smooth;
  background: #06101F;
  overscroll-behavior-y: none;
}
body {
  font-family: 'DM Sans', sans-serif;
  background: #06101F;
  color: #FFFFFF;
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
  overscroll-behavior-y: none;
}
#root { min-height: 100vh; background: #06101F; }
::-webkit-scrollbar { width: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 99px; }

@keyframes pulse  { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.35;transform:scale(0.65);} }
@keyframes fadeUp { from{opacity:0;transform:translateY(16px);} to{opacity:1;transform:translateY(0);} }
@keyframes toast  { from{opacity:0;transform:translate(-50%,-10px);} to{opacity:1;transform:translate(-50%,0);} }
@keyframes spin   { to { transform: rotate(360deg); } }

.anim-1 { animation: fadeUp 0.5s cubic-bezier(0.16,1,0.3,1) both; }
.anim-2 { animation: fadeUp 0.5s 0.07s cubic-bezier(0.16,1,0.3,1) both; }
.anim-3 { animation: fadeUp 0.5s 0.14s cubic-bezier(0.16,1,0.3,1) both; }
.spin   { animation: spin 0.85s linear infinite; }

.score-input::-webkit-outer-spin-button,
.score-input::-webkit-inner-spin-button { -webkit-appearance: none; }
.score-input { -moz-appearance: textfield; }
.score-input:focus {
  border-color: #0C69C8 !important;
  box-shadow: 0 0 0 3px rgba(12,105,200,0.22);
  outline: none;
}
.save-btn { transition: all 0.2s; }
.save-btn:hover  { opacity: 0.85; transform: translateY(-1px); }
.save-btn:active { transform: translateY(0); }
.team-row { transition: background 0.15s; }
.team-row:hover { background: rgba(255,255,255,0.03); }
.result-row { transition: background 0.15s; }
.result-row:hover { background: rgba(255,255,255,0.03); }
.tournament-view-tab {
  border: 1px solid var(--tour-border);
  background: var(--tour-card);
  color: var(--tour-text-muted);
  min-height: 42px;
  border-radius: 12px;
  padding: 0 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.18s ease;
  touch-action: manipulation;
}
.tournament-view-tab:hover {
  border-color: rgba(12,105,200,0.48);
  color: var(--tour-text);
}
.tournament-view-tab[aria-selected="true"] {
  background: linear-gradient(135deg,rgba(12,105,200,0.28),rgba(74,168,255,0.14));
  border-color: rgba(12,105,200,0.58);
  color: #4AA8FF;
}
.public-bracket-grid {
  display: grid;
  grid-template-columns: minmax(420px,1.35fr) minmax(300px,.95fr) minmax(260px,.75fr);
  gap: 16px;
  align-items: center;
}
.public-live-arena {
  width: 100vw;
  min-height: 100vh;
  overflow-x: hidden;
  color: #FFFFFF;
  background:
    radial-gradient(circle at 42% 34%, rgba(255,255,255,0.11), transparent 34%),
    linear-gradient(180deg, #0C69C8 0%, #0A1D44 100%);
}
.public-live-topbar {
  position: sticky;
  top: 0;
  z-index: 100;
  height: 56px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(245,247,250,0.95);
  border-bottom: 1px solid rgba(10,29,68,0.08);
  box-shadow: none;
  backdrop-filter: blur(20px) saturate(1.2);
  -webkit-backdrop-filter: blur(20px) saturate(1.2);
}
.public-live-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  color: #0F1629;
  padding: 0;
  cursor: pointer;
}
.public-live-brand:hover {
  transform: none;
}
.public-live-brand-mark {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #FFFFFF;
  border: 1px solid rgba(10,29,68,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(10,29,68,0.08);
}
.public-live-stage {
  width: 100%;
  padding: 24px clamp(24px, 4vw, 64px) 72px;
}
.public-live-title-block {
  margin-bottom: 28px;
}
.public-live-kicker {
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #F5B82E;
}
.public-live-title-block h1 {
  margin-top: 10px;
  font-family: 'Sora', sans-serif;
  font-size: clamp(34px, 4vw, 64px);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 0.95;
  color: #FFFFFF;
  text-transform: uppercase;
}
.public-live-meta {
  margin-top: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  color: rgba(255,255,255,0.72);
  font-size: 14px;
  font-weight: 800;
}
.public-live-detail-row {
  margin-top: 14px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.public-live-chip {
  min-height: 30px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0 12px;
  background: rgba(255,255,255,0.10);
  border: 1px solid rgba(255,255,255,0.16);
  color: rgba(255,255,255,0.86);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.02em;
}
.public-live-chip span {
  color: rgba(255,255,255,0.54);
  margin-right: 6px;
}
.public-live-bracket-header {
  margin-bottom: 16px;
}
.bracket-section-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #F5B82E;
}
.bracket-section-label span {
  width: 5px;
  height: 28px;
  border-radius: 999px;
  background: #F5B82E;
}
.bracket-helper {
  margin-top: 8px;
  color: rgba(255,255,255,0.62);
  font-size: 14px;
  font-weight: 700;
}
.public-live-icon-button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #FFFFFF;
  border: 1px solid rgba(10,29,68,0.08);
  color: #0F1629;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(10,29,68,0.08);
}
.tournament-app-actions { display:flex; flex-wrap:wrap; gap:12px; margin-top:20px; }
.tournament-app-actions a { display:inline-flex; align-items:center; justify-content:center; min-height:48px; padding:12px 18px; border-radius:12px; font-weight:800; text-decoration:none; color:#0C69C8; background:#fff; }
.tournament-app-actions a:last-child { background:transparent; color:#fff; border:1px solid #ffffff80; }
.tournament-app-actions a:focus-visible { outline:3px solid #F0C552; outline-offset:4px; }
.public-live-arena.tv-fullscreen {
  min-height: 100vh;
  background:
    radial-gradient(circle at 44% 28%, rgba(255,255,255,0.10), transparent 32%),
    linear-gradient(180deg, #0757AF 0%, #071A3D 100%);
}
.public-live-arena.tv-fullscreen .public-live-topbar {
  display: none;
}
.public-live-arena.tv-fullscreen .public-live-stage {
  min-height: 100vh;
  padding: clamp(18px, 2.3vw, 34px) clamp(28px, 4vw, 76px) clamp(24px, 3vw, 56px);
}
.public-live-arena.tv-fullscreen .public-live-title-block {
  margin-bottom: clamp(16px, 2vw, 26px);
}
.public-live-arena.tv-fullscreen .public-live-title-block h1 {
  font-size: clamp(38px, 5.2vw, 82px);
}
.public-live-arena.tv-fullscreen .public-live-bracket-header {
  margin-bottom: 12px;
}
.public-live-tv-exit-hint {
  display: none;
}
.public-live-arena.tv-fullscreen .public-live-tv-exit-hint {
  position: fixed;
  top: 16px;
  right: 18px;
  z-index: 160;
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.18);
  color: rgba(255,255,255,0.72);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.02em;
}
.tournament-admin-console {
  width: min(100%, 980px);
  max-width: 980px;
  margin: 0 auto;
  background: #F8FAFC;
  border: 1px solid rgba(215,225,238,0.95);
  border-radius: 24px;
  overflow: hidden;
  color: #0F172A;
  box-shadow: 0 24px 80px rgba(10,29,68,0.18);
  height: min(920px, calc(100vh - 84px));
  min-height: 700px;
  display: flex;
  flex-direction: column;
}
.tournament-admin-console * { box-sizing: border-box; }
.tournament-admin-head {
  min-height: 76px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #FFFFFF;
  border-bottom: 1px solid #D7E1EE;
}
.tournament-admin-back,
.tournament-admin-action {
  border: 1px solid #D7E1EE;
  background: #FFFFFF;
  color: #0A1D44;
  border-radius: 12px;
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-weight: 900;
  cursor: pointer;
}
.tournament-admin-back { width: 38px; }
.tournament-admin-action { padding: 0 12px; font-size: 12px; }
.tournament-admin-action.danger { color: #DC2626; border-color: rgba(220,38,38,.24); background: rgba(220,38,38,.06); }
.tournament-admin-title { flex: 1; min-width: 0; }
.tournament-admin-title h2 {
  margin: 0;
  color: #0F172A;
  font-family: 'Sora', sans-serif;
  font-size: clamp(18px, 2.4vw, 24px);
  font-weight: 900;
  line-height: 1.05;
}
.tournament-admin-title p { margin: 4px 0 0; color: #0C69C8; font-size: 12px; font-weight: 900; }
.tournament-admin-actions { display: flex; gap: 8px; align-items: center; }
.tournament-admin-tabs {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 3px;
  margin: 10px 12px;
  padding: 3px;
  background: #FFFFFF;
  border: 1px solid #D7E1EE;
  border-radius: 14px;
}
.tournament-admin-tab {
  min-width: 0;
  border: 0;
  border-radius: 10px;
  padding: 8px 6px;
  background: transparent;
  color: #64748B;
  font-size: 11px;
  font-weight: 900;
  cursor: pointer;
}
.tournament-admin-tab[aria-selected="true"] { background: #0C69C8; color: #FFFFFF; }
.tournament-admin-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 16px 22px;
}
.admin-app-hero {
  display: flex;
  gap: 12px;
  background: #EAF4FF;
  border: 1px solid #B9DDFB;
  border-radius: 18px;
  padding: 16px;
  margin-bottom: 10px;
}
.admin-app-hero-icon {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: #FFFFFF;
  border: 1px solid #B9DDFB;
  color: #0C69C8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}
.admin-app-kicker { color: #0C69C8; font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: .08em; }
.admin-app-title { margin: 4px 0 0; color: #0F172A; font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 900; }
.admin-app-body { margin: 6px 0 0; color: #64748B; font-size: 13px; font-weight: 700; line-height: 1.5; }
.admin-stat-row { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 8px; background: #FFFFFF; border: 1px solid #D7E1EE; border-radius: 16px; padding: 4px; margin-bottom: 14px; }
.admin-stat { padding: 12px 8px; text-align: center; border-radius: 12px; }
.admin-stat strong { display: block; color: #0F172A; font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 900; }
.admin-stat span { display: block; margin-top: 3px; color: #64748B; font-size: 10px; font-weight: 900; text-transform: uppercase; }
.admin-section-title { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin: 14px 0 8px; color: #0F172A; font-size: 13px; font-weight: 900; }
.admin-card-list { display: grid; gap: 8px; }
.admin-white-card { background: #FFFFFF; border: 1px solid #D7E1EE; border-radius: 16px; padding: 14px; color: #0F172A; }
.admin-blue-card { background: #EAF4FF; border: 1px solid #B9DDFB; border-radius: 16px; padding: 14px; color: #0F172A; }
.admin-muted { color: #64748B; font-size: 12px; font-weight: 700; line-height: 1.45; }
.admin-avatar {
  width: 46px;
  height: 46px;
  border-radius: 999px;
  background: #EEF2F7;
  border: 2px solid #D7E1EE;
  color: #0C69C8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  overflow: hidden;
  font-size: 14px;
  font-weight: 950;
}
.admin-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.admin-avatar.winner { border-color: #22A36F; box-shadow: 0 0 0 3px rgba(34,163,111,0.14); }
.admin-player-card,
.admin-person-card {
  position: relative;
  min-height: 72px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #D7E1EE;
  border-radius: 14px;
  background: #FFFFFF;
  padding: 12px;
  color: #0F172A;
}
.admin-player-control-card {
  border: 1px solid #D7E1EE;
  border-radius: 14px;
  background: #FFFFFF;
  overflow: hidden;
}
.admin-player-control-card .admin-player-card {
  border: 0;
  border-radius: 0;
}
.admin-player-actions {
  padding: 10px 12px 11px;
  border-top: 1px solid #D7E1EE;
  background: #F8FAFC;
}
.admin-player-actions-label {
  color: #64748B;
  font-size: 10px;
  font-weight: 950;
  text-transform: uppercase;
  letter-spacing: .08em;
  margin-bottom: 7px;
}
.admin-player-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.admin-player-action-btn {
  min-height: 36px;
  min-width: 58px;
  border: 1.5px solid #D7E1EE;
  border-radius: 10px;
  background: #FFFFFF;
  color: #0F172A;
  padding: 0 9px;
  font-size: 12px;
  font-weight: 950;
  cursor: pointer;
}
.admin-player-action-btn:disabled {
  opacity: .42;
  cursor: not-allowed;
}
.admin-person-main { flex: 1; min-width: 0; display: grid; gap: 2px; }
.admin-person-main strong {
  color: #0F172A;
  font-size: 14px;
  font-weight: 950;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-person-main span {
  color: #64748B;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-person-main small {
  color: #64748B;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.3;
}
.admin-player-stat {
  min-width: 58px;
  text-align: right;
  display: grid;
  gap: 2px;
}
.admin-player-stat strong { color: #0F172A; font-size: 14px; font-weight: 950; }
.admin-player-stat span { color: #64748B; font-size: 10px; font-weight: 850; }
.admin-rank {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 19px;
  height: 19px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  z-index: 2;
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 950;
}
.admin-rank-1 { background: #0C69C8; }
.admin-rank-2 { background: #94A3B8; }
.admin-rank-3 { background: #CD7F32; }
.admin-role-pill {
  flex: 0 0 auto;
  border: 1px solid #B9DDFB;
  background: #EAF4FF;
  color: #0C69C8;
  border-radius: 999px;
  padding: 7px 10px;
  font-size: 11px;
  font-weight: 950;
  text-transform: capitalize;
}
.admin-empty-card {
  min-height: 116px;
  display: grid;
  place-items: center;
  text-align: center;
  gap: 7px;
  border: 1px solid #D7E1EE;
  border-radius: 16px;
  background: #FFFFFF;
  padding: 22px;
  color: #64748B;
}
.admin-empty-card svg { color: #94A3B8; font-size: 24px; }
.admin-empty-card strong { color: #0F172A; font-size: 14px; font-weight: 950; }
.admin-empty-card span { max-width: 360px; color: #64748B; font-size: 12px; font-weight: 750; line-height: 1.45; }
.admin-match-card {
  border: 1px solid #D7E1EE;
  border-radius: 16px;
  background: #FFFFFF;
  padding: 13px;
  color: #0F172A;
}
.admin-match-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}
.admin-match-head strong {
  color: #0C69C8;
  font-size: 11px;
  font-weight: 950;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-match-head span { color: #64748B; font-size: 11px; font-weight: 850; }
.admin-match-body {
  display: grid;
  grid-template-columns: minmax(0,1fr) auto minmax(0,1fr);
  gap: 12px;
  align-items: center;
}
.admin-match-player {
  display: grid;
  justify-items: center;
  gap: 7px;
  min-width: 0;
  text-align: center;
}
.admin-match-player strong {
  color: #0F172A;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.admin-match-score {
  min-width: 78px;
  height: 42px;
  border-radius: 999px;
  border: 1px solid #D7E1EE;
  background: #F8FAFC;
  color: #0F172A;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Space Mono', monospace;
  font-size: 18px;
  font-weight: 900;
}
.admin-match-score span { color: #94A3B8; }
.admin-score-card {
  border: 1px solid #D7E1EE;
  border-radius: 16px;
  background: #FFFFFF;
  padding: 13px;
  color: #0F172A;
}
.admin-score-body {
  display: grid;
  grid-template-columns: minmax(0,1fr) auto minmax(0,1fr);
  gap: 12px;
  align-items: end;
}
.admin-score-player {
  min-width: 0;
  display: grid;
  justify-items: center;
  gap: 6px;
  text-align: center;
}
.admin-score-player strong {
  max-width: 100%;
  color: #0F172A;
  font-size: 13px;
  font-weight: 950;
  line-height: 1.25;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.admin-score-player span {
  max-width: 100%;
  color: #64748B;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-score-label {
  color: #64748B;
  font-size: 10px;
  font-weight: 950;
  text-transform: uppercase;
}
.admin-score-input {
  width: min(100%, 92px);
  height: 44px;
  border: 1.5px solid #D7E1EE;
  border-radius: 12px;
  background: #EEF2F7;
  color: #0F172A;
  font-family: 'Space Mono', monospace;
  font-size: 18px;
  font-weight: 900;
  text-align: center;
}
.admin-score-input:focus {
  border-color: #0C69C8;
  box-shadow: 0 0 0 3px rgba(12,105,200,0.18);
  outline: none;
}
.admin-score-vs {
  align-self: center;
  min-width: 42px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid #B9DDFB;
  background: #EAF4FF;
  color: #0C69C8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 950;
}
.admin-score-save { width: 100%; margin-top: 14px; }
.admin-queue-card {
  border: 1px solid #D7E1EE;
  border-radius: 16px;
  background: #FFFFFF;
  padding: 14px;
  margin-bottom: 4px;
}
.admin-queue-side {
  margin-bottom: 16px;
}
.admin-queue-side-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.admin-queue-side-head span {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex: 0 0 auto;
}
.admin-queue-side-head strong {
  color: #0F172A;
  font-size: 13px;
  font-weight: 900;
}
.admin-queue-side-head em {
  flex: 1;
  min-width: 0;
  text-align: right;
  font-size: 12px;
  font-style: normal;
  font-weight: 850;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-queue-chip-row {
  display: flex;
  justify-content: center;
  gap: 10px;
  overflow-x: auto;
  padding: 4px 2px 8px;
}
.admin-queue-chip {
  position: relative;
  width: 78px;
  min-height: 94px;
  flex: 0 0 78px;
  display: grid;
  justify-items: center;
  align-content: start;
  gap: 6px;
  border: 1.5px solid #D7E1EE;
  border-radius: 12px;
  background: #FFFFFF;
  color: #0F172A;
  padding: 8px;
  cursor: pointer;
}
.admin-queue-chip .admin-avatar {
  width: 44px;
  height: 44px;
}
.admin-queue-chip span {
  max-width: 100%;
  color: inherit;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.15;
  text-align: center;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.admin-queue-chip:disabled {
  opacity: .22;
  cursor: not-allowed;
}
.admin-queue-add { width: 100%; }
.admin-clear-link {
  border: 1px solid rgba(220,38,38,.28);
  background: rgba(220,38,38,.08);
  color: #DC2626;
  border-radius: 10px;
  min-height: 32px;
  padding: 0 11px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}
.admin-row-delete {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(220,38,38,.24);
  background: rgba(220,38,38,.08);
  color: #DC2626;
  font-size: 20px;
  font-weight: 900;
  cursor: pointer;
}
.admin-primary-btn {
  border: 0;
  border-radius: 14px;
  background: #0C69C8;
  color: #FFFFFF;
  min-height: 46px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
}
.admin-secondary-btn {
  border: 1px solid #D7E1EE;
  border-radius: 13px;
  background: #FFFFFF;
  color: #0F172A;
  min-height: 42px;
  padding: 0 14px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}
.admin-danger-btn { border-color: rgba(220,38,38,.28); background: rgba(220,38,38,.08); color: #DC2626; }
.admin-format-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #D7E1EE;
  border-radius: 14px;
  background: #FFFFFF;
  padding: 12px;
  margin: 12px 0;
}
.admin-format-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: #EAF4FF;
  color: #0C69C8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-size: 20px;
}
.admin-format-kicker { color: #64748B; font-size: 10px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
.admin-format-current { color: #0F172A; font-size: 18px; font-weight: 950; line-height: 1.1; margin-top: 3px; }
.admin-format-help { color: #64748B; font-size: 12px; font-weight: 750; line-height: 1.4; margin-top: 4px; }
.admin-format-list { display: grid; gap: 8px; margin-top: 12px; }
.admin-format-option {
  min-height: 76px;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  border: 1px solid #D7E1EE;
  border-radius: 14px;
  background: #FFFFFF;
  padding: 12px;
  text-align: left;
  cursor: pointer;
}
.admin-format-option[data-selected="true"] {
  border-color: #0C69C8;
  background: #EAF4FF;
}
.admin-format-option:disabled {
  cursor: not-allowed;
  opacity: .52;
}
.admin-format-title { color: #0F172A; font-size: 14px; font-weight: 900; }
.admin-format-option[data-selected="true"] .admin-format-title { color: #0C69C8; }
.admin-format-meta { color: #64748B; font-size: 12px; font-weight: 700; line-height: 1.4; margin-top: 3px; }
.admin-format-state {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid #D7E1EE;
  background: #F8FAFC;
  color: #0C69C8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-weight: 900;
}
.admin-format-option[data-selected="true"] .admin-format-state {
  background: #0C69C8;
  border-color: #0C69C8;
  color: #FFFFFF;
}
.admin-sim-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; margin-bottom: 14px; }
.admin-sim-input { width: 100%; border: 1px solid #D7E1EE; background: #EEF2F7; border-radius: 12px; color: #0F172A; font-size: 22px; font-weight: 900; padding: 12px 14px; }
.admin-quick-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin: 10px 0 14px; }
.admin-sim-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 2px;
}
@media(max-width:860px){
  .tournament-admin-console { width: 100%; height: min(860px, calc(100vh - 72px)); min-height: 650px; border-radius: 18px; }
  .tournament-admin-head { align-items: flex-start; flex-wrap: wrap; }
  .tournament-admin-actions { width: 100%; }
  .tournament-admin-action { flex: 1; }
  .tournament-admin-tabs { overflow-x: auto; display: flex; }
  .tournament-admin-tab { flex: 0 0 auto; min-width: 82px; }
  .admin-stat-row, .admin-sim-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
  .admin-player-card, .admin-person-card { align-items: flex-start; }
  .admin-role-pill { max-width: 112px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .admin-match-body { gap: 8px; }
  .admin-match-score { min-width: 64px; font-size: 15px; }
  .admin-score-body { grid-template-columns: minmax(0,1fr) 38px minmax(0,1fr); gap: 8px; }
  .admin-score-vs { min-width: 36px; }
  .admin-score-input { width: 74px; }
}
.public-live-state {
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px;
}
.app-tree-shell {
  width: 100%;
  min-height: calc(100vh - 260px);
  margin: 0;
  background: rgba(5,18,48,0.18);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 32px;
  overflow: hidden;
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.08),
    0 24px 80px rgba(0,0,0,0.18);
  backdrop-filter: blur(10px);
}
.app-tree-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 14px clamp(14px,2.5vw,36px);
  border-bottom: 1px solid var(--tour-border);
  background: linear-gradient(135deg,rgba(12,105,200,0.12),rgba(10,29,68,0.05));
}
.app-tree-scroll-x {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 18px 24px 36px;
}
.app-tree-scroll-y {
  min-height: min(640px, calc(100vh - 320px));
  overflow-y: visible;
  overflow-x: visible;
  display: flex;
  align-items: center;
  justify-content: center;
}
.app-tree-canvas {
  position: relative;
  margin: 0 auto;
  flex: 0 0 auto;
}
.app-tree-node {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.app-tree-node button {
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  border-radius: 999px;
}
.app-tree-node button:focus-visible,
.participant-detail-close:focus-visible,
.app-tree-standing-button:focus-visible {
  outline: 3px solid rgba(255,255,255,0.88);
  outline-offset: 3px;
}
.app-tree-empty-node {
  border: 2px dashed rgba(255,255,255,0.24);
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.42);
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
}
.app-tree-advance-dot {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 13px;
  height: 13px;
  border-radius: 999px;
  background: #4ADE80;
  color: #06101F;
  display: flex;
  align-items: center;
  justify-content: center;
}
.app-tree-name-label {
  width: 96px;
  max-width: 116px;
  text-align: center;
  color: rgba(255,255,255,0.86);
  font-size: 11px;
  font-weight: 900;
  line-height: 1.08;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
}
.app-tree-username-label {
  width: 96px;
  max-width: 116px;
  margin-top: -3px;
  text-align: center;
  color: rgba(255,255,255,0.48);
  font-size: 9px;
  font-weight: 800;
  line-height: 1.05;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
}
.app-tree-node-start {
  gap: 8px;
}
.app-tree-node-start .app-tree-name-label {
  width: 112px;
  max-width: 112px;
  font-size: 10px;
}
.app-tree-node-start .app-tree-username-label {
  width: 112px;
  max-width: 112px;
  font-size: 8.5px;
}
.app-tree-groups {
  display: grid;
  grid-template-columns: repeat(auto-fit,minmax(min(320px,100%),1fr));
  gap: 14px;
  padding: 16px clamp(14px,2.5vw,36px);
}
.app-tree-group-card {
  background: var(--tour-bg-elevated);
  border: 1px solid var(--tour-border);
  border-radius: 18px;
  overflow: hidden;
}
.app-tree-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 14px;
  background: linear-gradient(135deg,#0C69C8,#0A3A80);
  color: #fff;
}
.app-tree-summary {
  padding: 14px;
  border-bottom: 1px solid var(--tour-border);
}
.app-tree-progress {
  height: 7px;
  border-radius: 999px;
  background: var(--tour-hover);
  overflow: hidden;
  margin-top: 10px;
}
.app-tree-progress > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg,#0C69C8,#4AA8FF);
}
.app-tree-standing-row {
  display: grid;
  grid-template-columns: 34px minmax(0,1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 11px 14px;
  border-top: 1px solid var(--tour-border);
}
.app-tree-standing-button {
  width: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.18s ease;
}
.app-tree-standing-button:hover {
  background: rgba(255,255,255,0.06);
}
.app-tree-rank {
  width: 25px;
  height: 25px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tour-hover);
  color: var(--tour-text-muted);
  font-size: 11px;
  font-weight: 900;
}
.app-tree-rank.first { background: rgba(213,164,25,0.18); color: #D5A419; }
.app-tree-rank.second { background: rgba(12,105,200,0.14); color: #4AA8FF; }
.app-tree-rank.third { background: rgba(74,222,128,0.12); color: #4ADE80; }
.app-tree-fixtures {
  display: grid;
  gap: 10px;
  padding: 14px;
  background: var(--tour-bg-soft);
}
.app-tree-fixture-row {
  display: grid;
  grid-template-columns: minmax(0,1fr) auto minmax(0,1fr);
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  border-radius: 12px;
  background: var(--tour-card);
  border: 1px solid var(--tour-border);
}
.app-tree-vs {
  border-radius: 9px;
  padding: 5px 8px;
  background: var(--tour-accent);
  color: #fff;
  font-size: 10px;
  font-weight: 900;
}
.participant-detail-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  background: rgba(4,10,22,0.62);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
.participant-detail-modal {
  position: relative;
  width: min(520px, 100%);
  max-height: min(740px, calc(100vh - 44px));
  overflow-y: auto;
  border-radius: 28px;
  background: #FFFFFF;
  color: #0F172A;
  box-shadow: 0 28px 90px rgba(0,0,0,0.36);
}
.participant-detail-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 38px;
  height: 38px;
  border-radius: 13px;
  border: 1px solid rgba(15,23,42,0.10);
  background: rgba(241,245,249,0.92);
  color: #0F172A;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.participant-detail-hero {
  padding: 44px 24px 22px;
  text-align: center;
  background:
    radial-gradient(circle at 50% 0%, rgba(0,185,255,0.18), transparent 34%),
    linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%);
}
.participant-detail-name {
  margin-top: 14px;
  color: #0F172A;
  font-family: 'Sora', sans-serif;
  font-size: 28px;
  font-weight: 900;
  line-height: 1.02;
  overflow-wrap: anywhere;
}
.participant-detail-subtitle {
  margin-top: 5px;
  color: #6B7A90;
  font-size: 13px;
  font-weight: 800;
  overflow-wrap: anywhere;
}
.participant-detail-avatar-ring {
  width: 116px;
  height: 116px;
  margin: 0 auto;
  padding: 4px;
  border-radius: 999px;
  background: linear-gradient(135deg,#00B9FF,#0C69C8,#D5A419);
}
.participant-detail-avatar-ring img,
.participant-detail-avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 999px;
  border: 4px solid #FFFFFF;
  object-fit: cover;
}
.participant-detail-avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg,#0C69C8,#0A1D44);
  color: #FFFFFF;
  font-size: 28px;
  font-weight: 900;
}
.participant-detail-group-image {
  width: min(250px, 78vw);
  aspect-ratio: 1;
  margin: 0 auto;
  border-radius: 28px;
  overflow: hidden;
  border: 4px solid #FFFFFF;
  box-shadow: 0 18px 48px rgba(12,105,200,0.18);
  background: #EAF2FF;
}
.participant-detail-group-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.participant-detail-group-image.banner { width: min(320px, 80vw); aspect-ratio: 16 / 9; }
.participant-detail-group-image.logo { width: 180px; aspect-ratio: 1; border-radius: 50%; }
.participant-detail-group-image.logo img { object-fit: contain; }
.participant-detail-member-row {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 14px auto 0;
}
.participant-detail-member-avatar {
  width: 54px;
  height: 54px;
  border-radius: 999px;
  padding: 2px;
  background: linear-gradient(135deg,#00B9FF,#0C69C8,#D5A419);
}
.participant-detail-member-avatar img,
.participant-detail-member-avatar span {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  border: 2px solid #FFFFFF;
}
.participant-detail-member-avatar img {
  object-fit: cover;
}
.participant-detail-member-avatar span {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0A1D44;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 900;
}
.participant-detail-card {
  margin: 0 18px 18px;
  padding: 17px;
  border-radius: 22px;
  background: linear-gradient(180deg,#F8FBFF,#F1F6FF);
  border: 1px solid #E1ECFA;
}
.participant-detail-card-title {
  margin-bottom: 12px;
  color: #0A3A80;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.participant-detail-stat-grid {
  display: grid;
  grid-template-columns: repeat(3,minmax(0,1fr));
  gap: 10px;
}
.participant-detail-stat {
  min-width: 0;
  padding: 12px 10px;
  border-radius: 16px;
  background: #FFFFFF;
  border: 1px solid #E4EDF8;
  text-align: center;
}
.participant-detail-stat span {
  display: block;
  color: #7A8DA8;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
}
.participant-detail-stat strong {
  display: block;
  margin-top: 4px;
  color: #0F172A;
  font-size: 20px;
  font-weight: 900;
}
.participant-detail-info-list {
  display: grid;
  gap: 10px;
}
.participant-detail-info-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #E5EEF8;
}
.participant-detail-info-row:first-child {
  border-top: 0;
  padding-top: 0;
}
.participant-detail-info-row span {
  color: #7A8DA8;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}
.participant-detail-info-row strong {
  min-width: 0;
  color: #0F172A;
  font-size: 14px;
  font-weight: 900;
  text-align: right;
  overflow-wrap: anywhere;
}
.participant-detail-members {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 3px;
}
.participant-detail-member {
  min-width: 72px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  text-align: center;
}
.participant-detail-member-name {
  max-width: 88px;
  color: #49617F;
  font-size: 11px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.join-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit,minmax(220px,1fr));
  gap: 12px;
}
.tournament-shell {
  width: 100vw;
  max-width: none;
  margin: 0;
  padding: 0;
  position: relative;
  z-index: 1;
}
.tournament-wide-card {
  width: 100%;
}

/* ── MOBILE RESPONSIVE ── */
@media (max-width: 1120px) {
  .public-bracket-grid { grid-template-columns: minmax(0,1fr) minmax(0,1fr) !important; }
}

@media (max-width: 860px) {
  .groups-grid { grid-template-columns: 1fr !important; }
  .ko-grid     { grid-template-columns: 1fr !important; }
  .stats-grid  { grid-template-columns: 1fr 1fr !important; }
  .public-bracket-grid { grid-template-columns: 1fr !important; }
  .app-tree-shell {
    width: 100%;
    margin-left: 0;
    border-left: 1px solid var(--tour-border);
    border-right: 1px solid var(--tour-border);
    border-radius: 18px;
    min-height: 0;
  }
  .app-tree-top { flex-direction: column; }
  .app-tree-scroll-x { padding: 12px; }
  .app-tree-scroll-y { min-height: 420px; max-height: 68vh; justify-content: flex-start; overflow-x: visible; overflow-y: auto; }
  .app-tree-groups { grid-template-columns: 1fr; padding: 12px; }
  .participant-detail-backdrop { align-items: flex-end; padding: 12px; }
  .participant-detail-modal { border-radius: 24px; max-height: calc(100vh - 24px); }
  .participant-detail-hero { padding: 42px 18px 20px; }
  .participant-detail-name { font-size: 24px; }
  .participant-detail-stat-grid { gap: 8px; }
  .participant-detail-stat { padding: 10px 6px; }
  .public-live-topbar { height: 56px; padding: 0 16px; }
  .public-live-stage { padding: 18px 14px 48px; }
  .public-live-title-block h1 { font-size: clamp(30px, 10vw, 44px); }
  .public-live-meta { font-size: 12px; }
  .public-live-detail-row { gap: 6px; }
  .public-live-chip { min-height: 28px; padding: 0 10px; font-size: 11px; }
  .app-tree-name-label, .app-tree-username-label,
  .app-tree-node-start .app-tree-name-label,
  .app-tree-node-start .app-tree-username-label { width: 82px; max-width: 82px; }
}

@media (max-width: 600px) {
  .tournament-shell,.tournament-shell button,.participant-detail-modal,.participant-detail-modal button,.app-tree-shell,.app-tree-shell button{font-family:-apple-system,BlinkMacSystemFont,"SF Pro Text","Helvetica Neue",sans-serif!important}
  .tournament-view-tab{font-size:9px;font-weight:800;letter-spacing:0;text-transform:none}
  .participant-detail-name{font-family:inherit;font-size:20px;font-weight:900}
  .stats-grid  { grid-template-columns: 1fr 1fr !important; }
  .tournament-view-tabs { overflow-x: auto; padding-bottom: 6px; }
  .tournament-view-tab { flex: 0 0 auto; }
  .tournament-shell { padding-left: 12px !important; padding-right: 12px !important; }

  /* Result rows - stack on very small screens */
  .result-row-inner {
    flex-wrap: wrap;
    gap: 10px !important;
  }
  .result-badges {
    width: 100%;
    justify-content: flex-start !important;
  }

  /* Match score row in group table */
  .match-score-row {
    grid-template-columns: 1fr auto 1fr !important;
    gap: 8px !important;
    padding: 12px !important;
  }

  /* KO card grid already 1 col at 860, fine */

  /* Header title shrink */
  .header-actions { gap: 8px !important; }

  /* Standing grid - compress stat columns */
  .standing-grid {
    grid-template-columns: 28px minmax(100px, 1fr) 28px 28px 28px 28px 36px 40px !important;
    padding: 10px 12px !important;
  }
  .standing-header-grid {
    grid-template-columns: 28px minmax(100px, 1fr) 28px 28px 28px 28px 36px 40px !important;
    padding: 8px 12px !important;
  }

  /* Group card padding */
  .group-score-section { padding: 16px !important; }
  .group-header { padding: 14px 16px !important; }

  /* KO section padding */
  .ko-grid { padding: 16px !important; }
  .ko-save-btn-wrap { padding: 0 16px 16px !important; }

  /* Match results */
  .match-results-body { padding: 12px !important; }
  .match-results-header { padding: 14px 16px !important; flex-direction: column !important; align-items: flex-start !important; }
  .match-tab-row { width: 100%; overflow-x: auto; padding-bottom: 4px; }

  /* Section label */
  .section-label-wrap { margin-bottom: 16px !important; }

  /* Tournament header */
  .tournament-header-inner { flex-direction: column !important; gap: 12px !important; }
  .tournament-header-actions { flex-wrap: wrap !important; }

  /* Stats banner */
  .stats-banner { margin-bottom: 24px !important; }
}

@media (max-width: 400px) {
  .stats-grid  { grid-template-columns: 1fr 1fr !important; }
  .result-score-box { padding: 6px 10px !important; }
  .result-score-box span { font-size: 15px !important; }
}
`;

const tournamentThemeVars = (isDark) => isDark ? `
:root{
  --tour-bg:#0A1D44;
  --tour-bg-soft:rgba(5,18,48,0.42);
  --tour-bg-elevated:rgba(5,18,48,0.54);
  --tour-card:rgba(5,18,48,0.24);
  --tour-border:rgba(255,255,255,0.10);
  --tour-border-strong:rgba(255,255,255,0.22);
  --tour-accent:#F5B82E;
  --tour-accent-strong:#FFD45A;
  --tour-accent-soft:rgba(245,184,46,0.12);
  --tour-accent-border:rgba(245,184,46,0.34);
  --tour-success:#28E07F;
  --tour-success-soft:rgba(40,224,127,0.14);
  --tour-danger:#F87171;
  --tour-text:#FFFFFF;
  --tour-text-secondary:rgba(255,255,255,0.80);
  --tour-text-muted:rgba(255,255,255,0.60);
  --tour-text-faint:rgba(255,255,255,0.38);
  --tour-line:rgba(126,179,255,0.42);
  --tour-hover:rgba(255,255,255,0.08);
  --tour-header:transparent;
  --tour-page-glow:rgba(12,40,90,0.9);
}
` : `
:root{
  --tour-bg:#0A1D44;
  --tour-bg-soft:rgba(5,18,48,0.42);
  --tour-bg-elevated:rgba(5,18,48,0.54);
  --tour-card:rgba(5,18,48,0.24);
  --tour-border:rgba(255,255,255,0.10);
  --tour-border-strong:rgba(255,255,255,0.22);
  --tour-accent:#F5B82E;
  --tour-accent-strong:#FFD45A;
  --tour-accent-soft:rgba(245,184,46,0.12);
  --tour-accent-border:rgba(245,184,46,0.34);
  --tour-success:#28E07F;
  --tour-success-soft:rgba(40,224,127,0.14);
  --tour-danger:#F87171;
  --tour-text:#FFFFFF;
  --tour-text-secondary:rgba(255,255,255,0.80);
  --tour-text-muted:rgba(255,255,255,0.60);
  --tour-text-faint:rgba(255,255,255,0.38);
  --tour-line:rgba(126,179,255,0.42);
  --tour-hover:rgba(255,255,255,0.08);
  --tour-header:transparent;
  --tour-page-glow:rgba(12,40,90,0.9);
}
`;

function useTournamentTheme() {
  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem("spa-theme");
      if (stored) return stored !== "light";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
      return true;
    }
  });
  const toggleTheme = useCallback(() => {
    setIsDark((value) => {
      const next = !value;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);
  return { isDark, toggleTheme };
}

/* ─── HELPERS ─────────────────────────────────────────────────────────── */

const stableSort = (arr) =>
  [...arr].sort((a, b) => {
    const aId = a.team_id ?? a.profile_id ?? a.id;
    const bId = b.team_id ?? b.profile_id ?? b.id;
    return aId < bId ? -1 : aId > bId ? 1 : 0;
  });

const splitGroups = (enrolled) => {
  const sorted = stableSort(enrolled);
  return { groupA: sorted.slice(0, 3), groupB: sorted.slice(3, 6) };
};

const pairKey = (idA, idB) => [idA, idB].sort().join("__");

const deriveStats = (group) =>
  [...group]
    .map((row) => {
      const pl    = (row.played ?? []).length;
      const pts   = row.points ?? 0;
      const gd    = row.point_difference ?? 0;
      const wins  = Math.floor(pts / 3);
      const draws = pts % 3;
      const losses = Math.max(0, pl - wins - draws);
      return {
        id:    row.team_id ?? row.id,
        rowId: row.id,
        name:  row.team_name ?? row.name ?? "—",
        img:   row.team_image ?? row.profile_img ?? null,
        pts, gd, pl, wins, draws, losses,
      };
    })
    .sort((a, b) => b.pts - a.pts || b.gd - a.gd || a.name.localeCompare(b.name));

const recomputeGroup = (sortedGroup, scoresMap) => {
  const pairs = [];
  for (let i = 0; i < sortedGroup.length; i++)
    for (let j = i + 1; j < sortedGroup.length; j++)
      pairs.push([sortedGroup[i], sortedGroup[j]]);

  const stats = sortedGroup.map((row) => ({ id: row.id, pts: 0, gd: 0, played: [] }));
  const idx = (row) => sortedGroup.findIndex((r) => r.id === row.id);

  pairs.forEach(([rowA, rowB]) => {
    const key = pairKey(rowA.id, rowB.id);
    const sc  = scoresMap[key];
    if (!sc) return;
    const ga = parseInt(sc.a), gb = parseInt(sc.b);
    if (isNaN(ga) || isNaN(gb) || sc.a === "" || sc.b === "") return;
    const iA = idx(rowA), iB = idx(rowB);
    stats[iA].gd += ga - gb;
    stats[iB].gd += gb - ga;
    stats[iA].played.push(rowB.team_id ?? rowB.id);
    stats[iB].played.push(rowA.team_id ?? rowA.id);
    if (ga > gb)      { stats[iA].pts += 3; }
    else if (gb > ga) { stats[iB].pts += 3; }
    else              { stats[iA].pts += 1; stats[iB].pts += 1; }
  });

  return stats;
};

/* ─── SMALL COMPONENTS ───────────────────────────────────────────────── */

function Spinner({ size = 16, color = C.gold }) {
  return (
    <div className="spin" style={{
      width: size, height: size, flexShrink: 0,
      border: `2px solid ${color}28`,
      borderTop: `2px solid ${color}`,
      borderRadius: "50%", display: "inline-block",
    }} />
  );
}

function LiveDot() {
  return (
    <div style={{
      width: 7, height: 7, borderRadius: "50%",
      background: C.green, flexShrink: 0,
      animation: "pulse 1.9s ease-in-out infinite",
    }} />
  );
}

function Toast({ msg, type }) {
  return (
    <div style={{
      position: "fixed", top: 24, left: "50%",
      transform: "translateX(-50%)", zIndex: 999,
      padding: "11px 24px", borderRadius: 12,
      background: type === "error" ? "rgba(248,113,113,0.13)" : "rgba(74,222,128,0.13)",
      border: `1px solid ${type === "error" ? "rgba(248,113,113,0.38)" : "rgba(74,222,128,0.38)"}`,
      color: type === "error" ? C.red : C.green,
      fontSize: 13, fontWeight: 700,
      backdropFilter: "blur(16px)",
      boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
      whiteSpace: "nowrap",
      animation: "toast 0.3s cubic-bezier(0.16,1,0.3,1) both",
      maxWidth: "90vw",
      textAlign: "center",
    }}>{msg}</div>
  );
}

function SectionLabel({ children, accent = C.gold }) {
  return (
    <div className="section-label-wrap" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
      <div style={{ width: 3, height: 20, borderRadius: 2, background: accent, flexShrink: 0 }} />
      <span style={{
        fontFamily: "'Sora',sans-serif",
        fontSize: 19, fontWeight: 900, letterSpacing: 2.5,
        textTransform: "uppercase", color: accent, flexShrink: 0,
      }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${accent}44,transparent)` }} />
    </div>
  );
}

function TeamAvatar({ src, name, size = 28, isTeam = false, mode = "banner" }) {
  const initials = (name ?? "?").slice(0, 2).toUpperCase();
  const image = src || (isTeam ? teamAvatarSrc() : null);
  return image ? (
    <img src={image} alt={name} style={{
      width: size, height: size, borderRadius: "50%",
      objectFit: !src || mode === "logo" ? "contain" : "cover", border: "1.5px solid rgba(255,255,255,0.34)", flexShrink: 0,
      boxShadow: "0 10px 28px rgba(0,0,0,0.24)",
    }} />
  ) : (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `linear-gradient(135deg,${C.blue},${C.navy3})`,
      border: "1.5px solid rgba(255,255,255,0.34)",
      boxShadow: "0 10px 28px rgba(0,0,0,0.24)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 800, color: C.w55,
    }}>{initials}</div>
  );
}

function ScoreInput({ value, onChange, disabled, size = "md" }) {
  const w  = size === "lg" ? 52 : 44;
  const h  = size === "lg" ? 48 : 42;
  const fs = size === "lg" ? 18 : 16;
  return (
    <input
      type="number"
      className="score-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="–"
      min="0"
      disabled={disabled}
      style={{
        width: w, height: h,
        background: value !== "" ? C.navy3 : C.navy2,
        border: `1.5px solid ${value !== "" ? C.goldMed : C.border2}`,
        borderRadius: 10,
        color: value !== "" ? C.gold2 : C.w35,
        fontFamily: "'Space Mono', monospace",
        fontSize: fs, fontWeight: 700, textAlign: "center",
        transition: "all 0.18s",
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "text",
        touchAction: "manipulation",
      }}
    />
  );
}

function Cell({ children, color, bold }) {
  return (
    <div style={{ textAlign: "center", fontSize: 13, fontWeight: bold ? 900 : 600, color: color ?? C.w80 }}>
      {children}
    </div>
  );
}

const tournamentDisplayName = (row) => row?.team_name ?? row?.name ?? row?.username ?? "Participant";
const tournamentUsernameLabel = (row) => {
  if (!row) return "";
  const username = row.username || row.handle || row.profile?.username;
  if (username) return String(username).startsWith("@") ? String(username) : `@${username}`;
  if (row.is_simulated || row.isSimulated) {
    const testName = row.team_name || row.name || "test-player";
    return `@${String(testName).toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 24) || "testplayer"}`;
  }
  return "";
};
const profileDisplayName = (profile, fallback = "Unknown user") =>
  [profile?.name, profile?.surname].filter(Boolean).join(" ").trim() || profile?.username || fallback;
const profileUsernameLabel = (profile) => profile?.username ? `@${profile.username}` : "Username unavailable";
const parseMemberList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "object") {
    if (Array.isArray(value.members)) return value.members.filter(Boolean);
    return [value].filter(Boolean);
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parseMemberList(parsed);
    } catch {
      return [];
    }
  }
  return [];
};
const memberIdentity = (member) => member?.id ?? member?.profile_id ?? member?.profileId ?? member?.username ?? member?.name ?? null;
const memberDisplayName = (member) =>
  [member?.name, member?.surname].filter(Boolean).join(" ").trim() || member?.username || "Member";
const memberImage = (member) => member?.profile_img || member?.profileImage || member?.image_url || member?.avatar_url || "";
const participantImage = (row) => row?.team_image || row?.captain_profile_img || row?.profile_img || row?.profile?.profile_img || "";
const formatTournamentFormatLabel = (key) => ({
  single_elimination: "Single elimination",
  double_elimination: "Double elimination",
  round_robin: "Round robin",
  double_round_robin: "Double round robin",
  group_to_knockout: "Groups into knockout",
  consolation_bracket: "Consolation bracket",
  fitness_race: "Fitness Race",
})[key] || "Tournament format";
const formatGameTypeLabel = (type) => String(type || "").trim() || "Game type";
const sideSizeForGameType = (gameType = "") => {
  const match = String(gameType || "").trim().toLowerCase().match(/^(\d+)\s*v\s*(\d+)$/);
  if (!match) return null;
  return Math.max(Number(match[1]), Number(match[2]));
};
const isTeamGameType = (gameType = "") => {
  const normalized = String(gameType || "").trim().toLowerCase();
  return normalized.includes("team") || sideSizeForGameType(normalized) > 1;
};
const isTeamTournamentFormat = (tournament) => {
  const listingType = String(tournament?.type || "").trim().toLowerCase();
  return isTeamGameType(tournament?.game_type || tournament?.gameType) || listingType === "team" || listingType === "group";
};
const getFormatParticipantRule = (format, participants, packageLimit = 16) => {
  const count = Number(participants) || 0;
  const max = Math.max(2, packageLimit || 16);
  const rule = { min: 2, max, valid: count >= 2 && count <= max, helper: `Use 2-${max} players.`, error: `Use 2-${max} players for this format.` };
  if (format === "double_elimination") {
    rule.min = 4;
    rule.helper = `Double elimination needs at least 4 players. Max ${max}.`;
  } else if (format === "round_robin" || format === "double_round_robin") {
    rule.min = 3;
    rule.helper = `${format === "double_round_robin" ? "Double round robin" : "Round robin"} needs at least 3 players. Max ${max}.`;
  } else if (format === "group_to_knockout") {
    rule.min = 4;
    rule.helper = "Groups into knockout needs 4, 8, or 16 players.";
  }
  rule.valid = count >= rule.min && count <= max;
  if (format === "group_to_knockout") {
    rule.valid = rule.valid && [4, 8, 16].includes(count);
    rule.error = "Groups into knockout needs 4, 8, or 16 players.";
  } else {
    rule.error = `Use at least ${rule.min} and no more than ${max} players for this format.`;
  }
  return rule;
};
const isProTournamentPackage = (tournament) => {
  const key = String(tournament?.package_key || "").toLowerCase();
  return key.includes("pro");
};
const readLiveDashboardEnabled = (tournament) =>
  tournament?.schedule_config?.liveDashboardEnabled === true ||
  tournament?.schedule_config?.live_dashboard_enabled === true;

function TournamentTabs({ activeTab, setActiveTab, canOpenAdmin, isProTournament }) {
  const tabs = [
    ["overview", "Overview"],
    ["bracket", "Bracket"],
    ["join", "Join"],
    ["results", "Results"],
    ["admin", canOpenAdmin ? "Admin" : isProTournament ? "Admin" : "Pro Admin"],
  ];
  return (
    <div className="tournament-view-tabs" role="tablist" aria-label="Tournament views" style={{ display: "flex", gap: 8, marginBottom: 22 }}>
      {tabs.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          className="tournament-view-tab"
          aria-selected={activeTab === key}
          onClick={() => setActiveTab(key)}
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          {label}
          {key === "admin" && !canOpenAdmin && (
            <span aria-hidden="true" style={{ opacity: .65 }}>Locked</span>
          )}
        </button>
      ))}
    </div>
  );
}

const getParticipantId = (row) => row?.team_id ?? row?.profile_id ?? row?.id;
const isSimulationRow = (row) => !!(row?.is_simulated || row?.isSimulated);
const getCompetitionRows = (rows = []) => {
  const simulated = rows.filter(isSimulationRow);
  return simulated.length > 0 ? simulated : rows;
};
const isUuidValue = (value) => typeof value === "string" && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value);
const enrolledIdentity = (row) => row?.team_id ?? row?.profile_id ?? row?.id;
const normalizeEnrolledRow = (row) => {
  const id = enrolledIdentity(row);
  return {
    ...row,
    id,
    rowId: row.id,
    profileId: row.profile_id ?? row.profileId ?? null,
    teamId: row.team_id ?? row.teamId ?? null,
    knockedOut: row.knocked_out ?? row.knockedOut ?? false,
    knockedOut2: row.knocked_out2 ?? row.knockedOut2 ?? false,
    name: row.team_name ?? row.name ?? row.username ?? null,
    username: row.username ?? null,
    teamName: row.team_name ?? row.teamName ?? null,
    profile_img: row.team_image ?? row.profile_img ?? null,
    team_image: row.team_image ?? null,
    captain_profile_img: row.captain_profile_img ?? row.captainProfile?.profile_img ?? row.captain_profile?.profile_img ?? null,
    members: parseMemberList(row.members),
    is_simulated: row.is_simulated ?? row.isSimulated ?? false,
    status: row.status ?? 0,
    points: row.points ?? 0,
    point_difference: row.point_difference ?? 0,
    played: row.played ?? [],
  };
};
const getEntryStatus = (enrolled, id) => {
  const row = enrolled.find((entry) =>
    entry.id === id ||
    entry.rowId === id ||
    entry.teamId === id ||
    entry.profileId === id ||
    entry.team_id === id ||
    entry.profile_id === id
  );
  return {
    status: row?.status ?? 0,
    knockedOut: !!(row?.knockedOut ?? row?.knocked_out),
    knockedOut2: !!(row?.knockedOut2 ?? row?.knocked_out2),
  };
};
const nextPow2 = (n) => Math.pow(2, Math.ceil(Math.log2(Math.max(n, 2))));
const participantIdentityCandidates = (row) =>
  [getParticipantId(row), row?.id, row?.rowId, row?.teamId, row?.profileId, row?.team_id, row?.profile_id]
    .filter(Boolean)
    .map((value) => String(value));
const matchWinnerIdentityCandidates = (row) =>
  [row?.winner_enrolled_id, row?.winner_team_id, row?.winner_profile_id]
    .filter(Boolean)
    .map((value) => String(value));
const getDifferenceLabel = (sport) => String(sport || "").trim().toLowerCase() === "football" ? "GD" : "PD";
const parseConfigObject = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try { return JSON.parse(value); } catch { return {}; }
};
const FITNESS_RACE_FORMAT_KEY = "fitness_race";
const FITNESS_RACE_GAME_TYPE = "Fitness Race";
const FITNESS_RACE_SPORTS = new Set(["Fitness"]);
const isFitnessRaceSport = (sport = "") => FITNESS_RACE_SPORTS.has(String(sport || "").trim());
const isFitnessRaceTournament = (tournament = {}) => {
  const config = parseConfigObject(tournament?.format_config);
  return isFitnessRaceSport(tournament?.sport) && (
    tournament?.format_key === FITNESS_RACE_FORMAT_KEY ||
    config.event_kind === FITNESS_RACE_FORMAT_KEY ||
    String(tournament?.game_mode || "").toLowerCase() === "race"
  );
};
const getRacePlacements = (tournament = {}) => {
  const config = parseConfigObject(tournament?.format_config);
  const raw = Array.isArray(config.race_placements) ? config.race_placements : [];
  const byPlace = new Map();
  raw.forEach((item) => {
    const place = Math.floor(Number(item?.place));
    const enrolledId = item?.enrolled_id ?? item?.enrollment_id ?? item?.id;
    if (!Number.isFinite(place) || place < 1 || !enrolledId) return;
    byPlace.set(place, {
      place,
      enrolled_id: String(enrolledId),
      profile_id: item?.profile_id ?? null,
      team_id: item?.team_id ?? null,
      name: typeof item?.name === "string" ? item.name.trim() : "",
      selected_at: item?.selected_at ?? null,
      selected_by: item?.selected_by ?? null,
    });
  });
  return Array.from(byPlace.values()).sort((a, b) => a.place - b.place);
};
const getOrdinal = (place) => {
  const n = Math.max(1, Math.floor(Number(place) || 1));
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  if (n % 10 === 1) return `${n}st`;
  if (n % 10 === 2) return `${n}nd`;
  if (n % 10 === 3) return `${n}rd`;
  return `${n}th`;
};
const racePlacementIdentityCandidates = (placement = {}) =>
  [placement.enrolled_id, placement.enrollment_id, placement.id, placement.profile_id, placement.team_id]
    .filter(Boolean)
    .map((value) => String(value));
const findRacePlacementForParticipant = (placements = [], participant) => {
  const ids = new Set(participantIdentityCandidates(participant));
  return placements.find((placement) => racePlacementIdentityCandidates(placement).some((id) => ids.has(id))) || null;
};
const findParticipantForRacePlacement = (participants = [], placement) => {
  const ids = new Set(racePlacementIdentityCandidates(placement));
  return participants.find((participant) => participantIdentityCandidates(participant).some((id) => ids.has(id))) || null;
};
const getRaceWinnerPlacement = (tournament = {}) => {
  const placements = getRacePlacements(tournament);
  if (placements.length > 0) return placements.find((placement) => placement.place === 1) || null;
  const config = parseConfigObject(tournament?.format_config);
  if (!config.race_winner_enrolled_id && !config.race_winner_name) return null;
  return {
    place: 1,
    enrolled_id: config.race_winner_enrolled_id ? String(config.race_winner_enrolled_id) : "",
    profile_id: config.race_winner_profile_id ?? null,
    name: config.race_winner_name || "",
  };
};
const isStageMatch = (row, stage) => {
  const value = row?.metadata?.stage ?? row?.stage;
  if (typeof value === "number") return value === stage;
  if (typeof value === "string" && /^\d+$/.test(value)) return Number(value) === stage;
  if (typeof value === "string") {
    const lower = value.toLowerCase();
    if (stage === 1) return lower.includes("group");
    if (stage === 2) return lower.includes("knockout") || lower.includes("ko") || lower.includes("semi") || lower.includes("knock");
    if (stage === 3) return lower.includes("final") || lower.includes("champ");
  }
  return false;
};
const getLayout = (count) => {
  if (count <= 4) return { avatarSize: 58, colW: 240, rowH: 124 };
  if (count <= 8) return { avatarSize: 56, colW: 226, rowH: 116 };
  if (count <= 16) return { avatarSize: 42, colW: 178, rowH: 92 };
  return { avatarSize: 34, colW: 148, rowH: 76 };
};
const getRoundLabel = (index, total) => {
  if (index === total - 1) return "";
  if (index === total - 2) return "FINAL";
  if (index === total - 3) return "SEMI";
  if (index === total - 4) return "QF";
  return `R${index + 1}`;
};
const getPointFormatPlan = (formatKey, participantCount) => {
  if (formatKey === "double_round_robin") return { legs: 2, groupSize: Math.max(participantCount, 2), singleTable: true, hasKnockout: false, label: "Double Round Robin" };
  if (formatKey === "round_robin") return { legs: 1, groupSize: Math.max(participantCount, 2), singleTable: true, hasKnockout: false, label: "Round Robin" };
  return { legs: 1, groupSize: participantCount === 6 ? 3 : 4, singleTable: false, hasKnockout: true, label: "Group To Knockout" };
};
const deriveStatsFromResults = (teamIds, results, stage = null) => {
  const stats = {};
  teamIds.forEach((id) => { stats[id] = { played: 0, wins: 0, draws: 0, losses: 0, gd: 0, pts: 0 }; });
  results
    .filter((row) => !stage || isStageMatch(row, stage))
    .forEach((row) => {
      const t1 = row.team1_id ?? row.player1_profile_id;
      const t2 = row.team2_id ?? row.player2_profile_id;
      if (!stats[t1] && !stats[t2]) return;
      const s1 = Number(row.participant1_score ?? 0);
      const s2 = Number(row.participant2_score ?? 0);
      if (stats[t1]) { stats[t1].played += 1; stats[t1].gd += s1 - s2; }
      if (stats[t2]) { stats[t2].played += 1; stats[t2].gd += s2 - s1; }
      if (row.is_draw) {
        if (stats[t1]) { stats[t1].draws += 1; stats[t1].pts += 1; }
        if (stats[t2]) { stats[t2].draws += 1; stats[t2].pts += 1; }
      } else {
        const winner = row.winner_team_id ?? row.winner_profile_id;
        if (winner === t1) {
          if (stats[t1]) { stats[t1].wins += 1; stats[t1].pts += 3; }
          if (stats[t2]) stats[t2].losses += 1;
        } else if (winner === t2) {
          if (stats[t2]) { stats[t2].wins += 1; stats[t2].pts += 3; }
          if (stats[t1]) stats[t1].losses += 1;
        }
      }
    });
  return stats;
};
const buildRoundRobinSchedule = (ids) => {
  const teams = [...ids];
  if (teams.length % 2 === 1) teams.push("__bye__");
  const fixed = teams[0];
  let rotating = teams.slice(1);
  const rounds = [];
  for (let roundIndex = 0; roundIndex < Math.max(0, teams.length - 1); roundIndex += 1) {
    const roundTeams = [fixed, ...rotating];
    for (let i = 0; i < roundTeams.length / 2; i += 1) {
      const t1 = roundTeams[i];
      const t2 = roundTeams[roundTeams.length - 1 - i];
      if (t1 !== "__bye__" && t2 !== "__bye__") rounds.push({ t1, t2, round: roundIndex + 1 });
    }
    rotating = [rotating[rotating.length - 1], ...rotating.slice(0, -1)];
  }
  return rounds;
};
const getGroupKnockoutPairs = (ids) => {
  if (ids.length === 4) return [[ids[0], ids[3]], [ids[2], ids[1]]].filter((pair) => pair.every(Boolean));
  const pairs = [];
  for (let i = 0; i < Math.floor(ids.length / 2); i += 1) {
    const pair = [ids[i], ids[ids.length - 1 - i]].filter(Boolean);
    if (pair.length === 2) pairs.push(pair);
  }
  return pairs;
};
const findPairResult = (results, stage, t1, t2) =>
  [...results].reverse().find((row) =>
    (!stage || isStageMatch(row, stage)) && (
      (row.team1_id === t1 && row.team2_id === t2) ||
      (row.team1_id === t2 && row.team2_id === t1) ||
      (row.player1_profile_id === t1 && row.player2_profile_id === t2) ||
      (row.player1_profile_id === t2 && row.player2_profile_id === t1)
    )
  ) ?? null;
const findPairResults = (results, stage, t1, t2) =>
  [...results]
    .filter((row) =>
      (!stage || isStageMatch(row, stage)) && (
        (row.team1_id === t1 && row.team2_id === t2) ||
        (row.team1_id === t2 && row.team2_id === t1) ||
        (row.player1_profile_id === t1 && row.player2_profile_id === t2) ||
        (row.player1_profile_id === t2 && row.player2_profile_id === t1)
      )
    )
    .sort((a, b) => new Date(a.played_at || a.created_at || 0).getTime() - new Date(b.played_at || b.created_at || 0).getTime());
const getDoubleElimStatus = (row) => {
  const status = row?.status ?? 0;
  if ((row?.knocked_out || row?.knockedOut) && !(row?.knocked_out2 || row?.knockedOut2)) {
    const playedCount = Array.isArray(row?.played) ? row.played.length : 0;
    const statusRound = status < 0 ? Math.abs(status) : status + 1;
    return -Math.max(1, statusRound, playedCount);
  }
  return status;
};
const buildRedemptionBracketRounds = (participants, enrolled, results = []) => {
  const redemptionRows = enrolled.filter((row) => (row.knocked_out || row.knockedOut) && (row.status ?? 0) < 0);
  if (redemptionRows.length === 0) return [];
  const findParticipant = (id) => {
    const participant = participants.find((row) => getParticipantId(row) === id);
    if (participant) return participant;
    const row = enrolled.find((entry) => [entry.id, entry.team_id, entry.profile_id].includes(id));
    return row ? {
      ...row,
      id: getParticipantId(row),
      name: row.team_name ?? row.name ?? row.username ?? null,
      profile_img: row.team_image ?? row.profile_img ?? null,
      team_image: row.team_image ?? null,
    } : null;
  };
  const seedIds = redemptionRows
    .map((row) => getParticipantId(row))
    .filter(Boolean)
    .sort((a, b) => String(a).localeCompare(String(b)));
  const size = nextPow2(Math.max(seedIds.length, 2));
  let seeds = seedIds.map(findParticipant);
  while (seeds.length < size) seeds.push(null);
  const rounds = [seeds];
  let prev = seeds;
  for (let r = 0; r < Math.log2(size); r += 1) {
    const next = [];
    for (let i = 0; i < prev.length; i += 2) {
      const p1 = prev[i];
      const p2 = prev[i + 1];
      const id1 = p1 ? getParticipantId(p1) : null;
      const id2 = p2 ? getParticipantId(p2) : null;
      const result = id1 && id2 ? findPairResult(results, null, id1, id2) : null;
      const winner = result?.winner_team_id ?? result?.winner_profile_id;
      if (winner) { next.push(findParticipant(winner)); continue; }
      if (!p1 || !p2) { next.push(null); continue; }
      const e1 = enrolled.find((row) => [row.id, row.team_id, row.profile_id].includes(id1));
      const e2 = enrolled.find((row) => [row.id, row.team_id, row.profile_id].includes(id2));
      const s1 = e1 ? getDoubleElimStatus(e1) : 99;
      const s2 = e2 ? getDoubleElimStatus(e2) : 99;
      next.push(s1 <= -(r + 2) ? p1 : s2 <= -(r + 2) ? p2 : null);
    }
    rounds.push(next);
    prev = next;
  }
  return rounds;
};
const buildConnectors = (rounds, rowH, colW, avatarSize) => {
  const labelOffset = 20;
  const lines = [];
  for (let r = 0; r < rounds.length - 1; r += 1) {
    const round = rounds[r];
    const spacingMult = Math.pow(2, r);
    const nodeSpacing = rowH * spacingMult;
    const firstOffset = rowH / 2 + (nodeSpacing - rowH) / 2 + labelOffset + avatarSize / 2;
    const nextNodeSpacing = rowH * Math.pow(2, r + 1);
    const nextFirstOffset = rowH / 2 + (nextNodeSpacing - rowH) / 2 + labelOffset + avatarSize / 2;
    for (let i = 0; i < round.length; i += 2) {
      const y1 = firstOffset + i * nodeSpacing;
      const y2 = firstOffset + (i + 1) * nodeSpacing;
      const yMid = (y1 + y2) / 2;
      const yW = nextFirstOffset + Math.floor(i / 2) * nextNodeSpacing;
      const xLeft = r * colW + avatarSize + 4;
      const xRight = (r + 1) * colW;
      const xMid = xLeft + (xRight - xLeft) * 0.55;
      const active = !!(round[i] || round[i + 1]);
      lines.push({ x1: xLeft, y1, x2: xMid, y2: y1, active });
      lines.push({ x1: xLeft, y1: y2, x2: xMid, y2: y2, active });
      lines.push({ x1: xMid, y1, x2: xMid, y2: y2, active });
      lines.push({ x1: xMid, y1: yMid, x2: xRight, y2: yMid, active });
      if (Math.abs(yMid - yW) > 2) lines.push({ x1: xRight, y1: yMid, x2: xRight, y2: yW, active });
    }
  }
  return lines;
};
const buildBracketRounds = (participants, enrolled, results) => {
  const size = nextPow2(participants.length);
  const findParticipant = (id) =>
    participants.find((row) => getParticipantId(row) === id) ||
    enrolled.find((row) => [row.id, row.rowId, row.teamId, row.profileId, row.team_id, row.profile_id].includes(id)) ||
    null;
  let seeds = [...participants].sort((a, b) => String(getParticipantId(a)).localeCompare(String(getParticipantId(b))));
  if (seeds.length < size) {
    const seededIds = new Set(seeds.map((row) => row ? getParticipantId(row) : null));
    for (const row of enrolled) {
      if (seeds.length >= size) break;
      const id = getParticipantId(row);
      if (id && !seededIds.has(id)) {
        seeds.push(row);
        seededIds.add(id);
      }
    }
    while (seeds.length < size) seeds.push(null);
  }
  const rounds = [seeds];
  let prev = seeds;
  for (let r = 0; r < Math.log2(size); r += 1) {
    const next = [];
    for (let i = 0; i < prev.length; i += 2) {
      const p1 = prev[i];
      const p2 = prev[i + 1];
      const id1 = p1 ? getParticipantId(p1) : null;
      const id2 = p2 ? getParticipantId(p2) : null;
      const result = id1 && id2 ? findPairResult(results, null, id1, id2) : null;
      const winner = result?.winner_team_id ?? result?.winner_profile_id;
      if (winner) { next.push(findParticipant(winner)); continue; }
      if (!p1 || !p2) { next.push(null); continue; }
      const s1 = id1 ? getEntryStatus(enrolled, id1).status : -99;
      const s2 = id2 ? getEntryStatus(enrolled, id2).status : -99;
      next.push(s1 > r ? p1 : s2 > r ? p2 : null);
    }
    rounds.push(next);
    prev = next;
  }
  return rounds;
};
const deriveGroupStage2Ids = (participants, matchResults) => {
  const stableIds = [...participants]
    .sort((a, b) => String(getParticipantId(a)).localeCompare(String(getParticipantId(b))))
    .map(getParticipantId)
    .filter(Boolean);
  if (stableIds.length < 4) return [];
  const groupSize = stableIds.length === 6 ? 3 : 4;
  const groups = [];
  for (let i = 0; i < stableIds.length; i += groupSize) groups.push(stableIds.slice(i, i + groupSize));
  const groupStageComplete = groups.every((group) => group.length >= 2 && group.every((id, index) =>
    group.slice(index + 1).every((oppId) => findPairResults(matchResults, 1, id, oppId).length >= 1)
  ));
  if (!groupStageComplete) return [];
  const stats = deriveStatsFromResults(stableIds, matchResults, 1);
  return groups.flatMap((group) => [...group]
    .sort((a, b) =>
      (stats[b]?.pts ?? 0) - (stats[a]?.pts ?? 0) ||
      (stats[b]?.gd ?? 0) - (stats[a]?.gd ?? 0) ||
      (stats[b]?.wins ?? 0) - (stats[a]?.wins ?? 0) ||
      String(a).localeCompare(String(b))
    )
    .slice(0, 2));
};
const deriveGroupStage3Ids = (stage2Ids, matchResults) => {
  if (stage2Ids.length !== 4) return [];
  const winners = getGroupKnockoutPairs(stage2Ids).map(([idA, idB]) => {
    const result = findPairResult(matchResults, 2, idA, idB);
    return result?.winner_team_id ?? result?.winner_profile_id ?? null;
  });
  return winners.every(Boolean) ? winners : [];
};

function ParticipantDetailModal({ participant, tournament, onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!participant) return undefined;
    const previousFocus = document.activeElement;
    window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
    };
  }, [participant, onClose]);

  if (!participant) return null;

  const gameType = tournament?.game_type || tournament?.gameType || "";
  const sideSize = sideSizeForGameType(gameType);
  const viewType = sideSize === 1 || String(gameType).trim().toLowerCase() === "1v1" || !isTeamTournamentFormat(tournament)
    ? "profile"
    : "team";
  const rawTeamType = String(participant.team_type || participant.teamType || tournament?.type || "").toLowerCase();
  const useMemberHero = viewType === "team" && (rawTeamType === "individual" || sideSize === 2 || sideSize === 3);
  const profile = participant.profile || {};
  const captainProfile = participant.captain_profile || participant.captainProfile || profile || {};
  const captainMember = {
    id: participant.team_captain_id || participant.teamCaptainId || captainProfile.id || participant.profile_id || participant.profileId,
    username: captainProfile.username || participant.username,
    name: captainProfile.name || participant.name,
    surname: captainProfile.surname,
    profile_img: participant.captain_profile_img || captainProfile.profile_img || "",
  };
  const members = parseMemberList(participant.members)
    .map((member) => ({
      ...member,
      profile_img: memberImage(member),
      username: member.username || member.handle || member.profile?.username,
      name: member.name || member.profile?.name,
      surname: member.surname || member.profile?.surname,
    }))
    .filter((member) => memberDisplayName(member) !== "Member" || memberImage(member));
  const mergedMembers = (() => {
    const seen = new Set();
    const list = [];
    [captainMember, ...members].forEach((member) => {
      const identity = memberIdentity(member);
      const key = identity ? String(identity) : `${memberDisplayName(member)}-${memberImage(member)}`;
      if (!key || seen.has(key)) return;
      if (memberDisplayName(member) === "Member" && !memberImage(member)) return;
      seen.add(key);
      list.push(member);
    });
    return list;
  })();
  const name = viewType === "team" ? (participant.team_name || participant.teamName || tournamentDisplayName(participant)) : tournamentDisplayName(participant);
  const username = viewType === "profile" ? tournamentUsernameLabel(participant) : "";
  const image = viewType === "profile"
    ? (participant.profile_img || profile.profile_img || participantImage(participant))
    : participantImage(participant);
  const initials = String(name || "?").slice(0, 2).toUpperCase();
  const statusLabel = Number(participant.status || 0) > 0 ? "Advanced" : participant.knockedOut || participant.knocked_out ? "Eliminated" : "Active";
  const captainLabel = captainProfile?.username ? `@${captainProfile.username}` : memberDisplayName(captainMember);
  const memberCount = mergedMembers.length || Number(participant.spots_in_team || participant.spotsInTeam || 0) || "-";

  return (
    <div
      className="participant-detail-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="participant-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="participant-detail-title"
      >
        <button ref={closeButtonRef} type="button" className="participant-detail-close" aria-label="Close participant details" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="participant-detail-hero">
          {viewType === "team" && !useMemberHero ? (
            <div className={`participant-detail-group-image ${teamDisplayMode(participant.team_image, participant.image_display_mode)}`}>
              <img src={teamArtworkSrc(participant.team_image, teamDisplayMode(participant.team_image, participant.image_display_mode))} alt="" />
            </div>
          ) : viewType === "team" ? (
            <div className="participant-detail-member-row" aria-label="Team member avatars">
              {(mergedMembers.length ? mergedMembers : [captainMember]).map((member, index) => {
                const img = memberImage(member);
                const memberInitials = String(memberDisplayName(member) || "?").slice(0, 2).toUpperCase();
                return (
                  <div key={`${memberIdentity(member) || index}`} className="participant-detail-member-avatar" title={memberDisplayName(member)}>
                    {img ? <img src={img} alt="" /> : <span>{memberInitials}</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="participant-detail-avatar-ring">
              {image ? <img src={image} alt="" /> : <div className="participant-detail-avatar-placeholder">{initials}</div>}
            </div>
          )}
          <h2 id="participant-detail-title" className="participant-detail-name">{name}</h2>
          <div className="participant-detail-subtitle">
            {viewType === "profile" ? (username || "Tournament player") : `${formatGameTypeLabel(gameType)} entry`}
          </div>
        </div>

        <div className="participant-detail-card">
          <div className="participant-detail-card-title">Athlete Dashboard</div>
          <div className="participant-detail-stat-grid">
            <div className="participant-detail-stat"><span>Status</span><strong>{statusLabel}</strong></div>
            <div className="participant-detail-stat"><span>Played</span><strong>{Array.isArray(participant.played) ? participant.played.length : Number(participant.played || 0) || 0}</strong></div>
            <div className="participant-detail-stat"><span>{getDifferenceLabel(tournament?.sport)}</span><strong>{Number(participant.point_difference || participant.pointDifference || 0)}</strong></div>
          </div>
        </div>

        <div className="participant-detail-card">
          <div className="participant-detail-card-title">{viewType === "profile" ? "Player Info" : "Team Info"}</div>
          <div className="participant-detail-info-list">
            <div className="participant-detail-info-row"><span>Entry</span><strong>{viewType === "profile" ? "Individual" : useMemberHero ? "Individual team" : "Team"}</strong></div>
            <div className="participant-detail-info-row"><span>Sport</span><strong>{tournament?.sport || "Tournament"}</strong></div>
            {viewType === "team" && <div className="participant-detail-info-row"><span>Captain</span><strong>{captainLabel}</strong></div>}
            {viewType === "team" && <div className="participant-detail-info-row"><span>Members</span><strong>{memberCount}</strong></div>}
          </div>
        </div>

        {viewType === "team" && !useMemberHero && mergedMembers.length > 0 && (
          <div className="participant-detail-card">
            <div className="participant-detail-card-title">Team Members</div>
            <div className="participant-detail-members">
              {mergedMembers.map((member, index) => {
                const img = memberImage(member);
                const label = member.username ? `@${member.username}` : memberDisplayName(member);
                const memberInitials = String(memberDisplayName(member) || "?").slice(0, 2).toUpperCase();
                return (
                  <div key={`${memberIdentity(member) || index}`} className="participant-detail-member">
                    <div className="participant-detail-member-avatar">
                      {img ? <img src={img} alt="" /> : <span>{memberInitials}</span>}
                    </div>
                    <div className="participant-detail-member-name" title={label}>{label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function AppTreeAvatar({ participant, size, advanced, eliminated, onOpen }) {
  if (!participant) {
    return (
      <div className="app-tree-empty-node" style={{ width: size, height: size }}>
        <FontAwesomeIcon icon={faUser} aria-label="Participant to be decided" style={{ fontSize: Math.max(15, size * 0.34) }} />
      </div>
    );
  }
  const name = tournamentDisplayName(participant);
  const username = tournamentUsernameLabel(participant);
  return (
    <>
      <button type="button" onClick={onOpen} title={username ? `${name} ${username}` : name} aria-label={username ? `View ${name} ${username}` : `View ${name}`} style={{ position: "relative", opacity: eliminated ? 0.42 : 1, filter: eliminated ? "grayscale(0.45)" : "none", transition: "opacity 0.18s ease, filter 0.18s ease" }}>
        <TeamAvatar isTeam={!!(participant?.team_name || participant?.team_id)} mode={participant?.image_display_mode} src={participantImage(participant)} name={name} size={size} />
        <span style={{
          position: "absolute",
          inset: -3,
          borderRadius: 999,
          border: `2px solid ${eliminated ? "rgba(180,188,200,0.55)" : "rgba(255,255,255,0.36)"}`,
          boxShadow: eliminated ? "none" : "0 12px 32px rgba(0,0,0,0.28)",
          pointerEvents: "none",
        }} />
        {advanced && <span className="app-tree-advance-dot" aria-label="Advanced" />}
      </button>
      <div className="app-tree-name-label" title={name}>{name}</div>
      {username && <div className="app-tree-username-label" title={username}>{username}</div>}
    </>
  );
}

function AppBracketTree({ participants, enrolled, matchResults, roundsOverride = null, title = null, winnerLabel = "Tournament Winner", isRedemption = false, onParticipantOpen }) {
  const builtRounds = useMemo(() => buildBracketRounds(participants, enrolled, matchResults), [participants, enrolled, matchResults]);
  const rounds = roundsOverride || builtRounds;
  const layout = getLayout(participants.length);
  const { avatarSize, colW, rowH } = layout;
  const canvasW = rounds.length * colW + 24;
  const canvasH = (rounds[0]?.length || 2) * rowH + rowH;
  const lines = useMemo(() => buildConnectors(rounds, rowH, colW, avatarSize), [rounds, rowH, colW, avatarSize]);
  const winner = rounds[rounds.length - 1]?.[0];
  const winnerIds = winner ? participantIdentityCandidates(winner) : [];
  const hasRecordedWinner = winnerIds.length > 0 && matchResults.some((row) =>
    matchWinnerIdentityCandidates(row).some((id) => winnerIds.includes(id))
  );

  return (
    <div>
      {title && (
        <div style={{ padding: "16px 18px 0", color: C.gold, fontSize: 12, fontWeight: 900, letterSpacing: "0.14em", textTransform: "uppercase" }}>
          {title}
        </div>
      )}
      {winner && hasRecordedWinner && (
        <div style={{ margin: 14, padding: "10px 14px", borderRadius: 14, background: "rgba(12,105,200,0.12)", border: "1px solid rgba(12,105,200,0.30)", display: "flex", alignItems: "center", gap: 10 }}>
          <TeamAvatar isTeam={!!(winner?.team_name || winner?.team_id)} mode={winner?.image_display_mode} src={participantImage(winner)} name={tournamentDisplayName(winner)} size={32} />
          <div>
            <div style={{ color: C.gold, fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: .8 }}>{winnerLabel}</div>
            <div style={{ color: C.white, fontSize: 14, fontWeight: 900 }}>{tournamentDisplayName(winner)}</div>
          </div>
        </div>
      )}
      <div className="app-tree-scroll-x">
        <div className="app-tree-scroll-y">
          <div className="app-tree-canvas" style={{ width: canvasW, height: canvasH, minWidth: canvasW }}>
            <svg width={canvasW} height={canvasH} style={{ position: "absolute", inset: 0 }}>
              {lines.map((line, index) => (
                <line
                  key={index}
                  x1={line.x1}
                  y1={line.y1}
                  x2={line.x2}
                  y2={line.y2}
                  stroke={line.active ? "rgba(126,179,255,0.50)" : "rgba(255,255,255,0.12)"}
                  strokeWidth={line.active ? 2 : 1.5}
                  strokeLinecap="round"
                />
              ))}
            </svg>
            {rounds.map((_, roundIndex) => (
              <div key={`label-${roundIndex}`} style={{ position: "absolute", top: 0, left: roundIndex * colW, width: avatarSize + 8, textAlign: "center", color: "rgba(255,255,255,0.42)", fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                {getRoundLabel(roundIndex, rounds.length)}
              </div>
            ))}
            {rounds.map((round, roundIndex) => {
              const spacingMult = Math.pow(2, roundIndex);
              const nodeSpacing = rowH * spacingMult;
              const firstOffset = rowH / 2 + (nodeSpacing - rowH) / 2 + 20;
              return round.map((participant, index) => {
                const id = participant ? getParticipantId(participant) : null;
                const advanced = !!id && roundIndex < rounds.length - 1 && rounds[roundIndex + 1]?.some((next) => next && getParticipantId(next) === id);
                const status = id ? getEntryStatus(enrolled, id) : { knockedOut: false, knockedOut2: false };
                const eliminated = isRedemption
                  ? status.knockedOut2
                  : status.knockedOut || status.knockedOut2;
                return (
                  <div key={`${roundIndex}-${index}`} className={`app-tree-node ${roundIndex === 0 ? "app-tree-node-start" : ""}`} style={{ top: firstOffset + index * nodeSpacing, left: roundIndex * colW, width: avatarSize + 8 }}>
                    <AppTreeAvatar participant={participant} size={avatarSize} advanced={advanced} eliminated={eliminated} onOpen={() => participant && onParticipantOpen?.(participant)} />
                  </div>
                );
              });
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function AppDoubleEliminationTree({ participants, enrolled, matchResults, tournament, onParticipantOpen }) {
  const redemptionRounds = useMemo(
    () => buildRedemptionBracketRounds(participants, enrolled, matchResults),
    [participants, enrolled, matchResults]
  );
  const grandFinalIds = Array.isArray(tournament?.grand_final_participants) ? tournament.grand_final_participants : [];

  return (
    <div>
      <AppBracketTree
        participants={participants}
        enrolled={enrolled}
        matchResults={matchResults}
        title="Winners bracket"
        winnerLabel="Winners Bracket Leader"
        onParticipantOpen={onParticipantOpen}
      />
      {redemptionRounds.length > 0 ? (
        <AppBracketTree
          participants={participants}
          enrolled={enrolled}
          matchResults={matchResults}
          roundsOverride={redemptionRounds}
          title="Redemption bracket"
          winnerLabel="Redemption Winner"
          isRedemption
          onParticipantOpen={onParticipantOpen}
        />
      ) : (
        <div style={{ margin: "0 18px 18px", padding: "14px 16px", borderRadius: 16, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.62)", fontSize: 13, fontWeight: 800 }}>
          Redemption bracket unlocks when a first-loss player enters the lower bracket.
        </div>
      )}
      {grandFinalIds.length > 0 && (
        <div style={{ margin: "0 18px 18px", padding: "14px 16px", borderRadius: 16, border: "1px solid rgba(245,184,46,0.28)", background: "rgba(245,184,46,0.10)", color: C.white, fontSize: 13, fontWeight: 900 }}>
          Grand final is ready.
        </div>
      )}
    </div>
  );
}

function AppRaceTree({ participants, tournament, onParticipantOpen }) {
  const placements = useMemo(() => getRacePlacements(tournament), [tournament]);
  const winnerPlacement = useMemo(() => getRaceWinnerPlacement(tournament), [tournament]);
  const winner = winnerPlacement ? findParticipantForRacePlacement(participants, winnerPlacement) : null;
  const config = parseConfigObject(tournament?.format_config);
  const configuredCapacity = Number(tournament?.spots_available || 0) || Number(config.max_participants || 0) || participants.length || 1;
  const maxParticipants = Math.max(
    1,
    configuredCapacity,
    participants.length || 0,
  );
  const lanes = useMemo(() => {
    const sorted = stableSort(participants);
    return Array.from({ length: Math.max(maxParticipants, sorted.length) }).map((_, index) => sorted[index] || null);
  }, [maxParticipants, participants]);
  const rowH = 78;
  const avatarSize = 42;
  const canvasW = 680;
  const canvasH = Math.max(360, lanes.length * rowH + 92);
  const resultX = 510;
  const resultY = canvasH / 2;
  const startX = 42;
  const startY = 74;
  const winnerName = winner ? tournamentDisplayName(winner) : winnerPlacement?.name || "";

  return (
    <div>
      {winnerPlacement && (
        <div style={{ margin: 14, padding: "10px 14px", borderRadius: 14, background: "rgba(12,105,200,0.12)", border: "1px solid rgba(12,105,200,0.30)", display: "flex", alignItems: "center", gap: 10 }}>
          <TeamAvatar isTeam={!!(winner?.team_name || winner?.team_id)} mode={winner?.image_display_mode} src={winner ? participantImage(winner) : null} name={winnerName || "Winner"} size={32} />
          <div>
            <div style={{ color: C.gold, fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: .8 }}>Winner</div>
            <div style={{ color: C.white, fontSize: 14, fontWeight: 900 }}>{winnerName || "Race winner recorded"}</div>
          </div>
        </div>
      )}
      <div className="app-tree-scroll-x">
        <div className="app-tree-scroll-y">
          <div className="app-tree-canvas" style={{ width: canvasW, height: canvasH, minWidth: canvasW }}>
            <svg width={canvasW} height={canvasH} style={{ position: "absolute", inset: 0 }}>
              {lanes.map((participant, index) => {
                const y = startY + index * rowH + avatarSize / 2;
                return (
                  <line
                    key={`race-line-${index}`}
                    x1={startX + avatarSize + 18}
                    y1={y}
                    x2={resultX - 34}
                    y2={resultY}
                    stroke={participant ? "rgba(126,179,255,0.34)" : "rgba(255,255,255,0.08)"}
                    strokeWidth={participant ? 1.8 : 1}
                    strokeLinecap="round"
                  />
                );
              })}
            </svg>
            <div style={{ position: "absolute", top: 20, left: startX, width: 120, color: "rgba(255,255,255,0.42)", fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Participants
            </div>
            <div style={{ position: "absolute", top: 20, left: resultX - 32, width: 130, color: "rgba(255,255,255,0.42)", fontSize: 12, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center" }}>
              Result
            </div>
            {lanes.map((participant, index) => {
              const placement = participant ? findRacePlacementForParticipant(placements, participant) : null;
              const name = participant ? tournamentDisplayName(participant) : `Lane ${index + 1}`;
              const username = participant ? tournamentUsernameLabel(participant) : "";
              const y = startY + index * rowH;
              return (
                <div key={participant?.id || `empty-${index}`} className="app-tree-node app-tree-node-start" style={{ top: y, left: startX, width: 138 }}>
                  {participant ? (
                    <>
                      <button type="button" onClick={() => onParticipantOpen?.(participant)} title={username ? `${name} ${username}` : name} aria-label={username ? `View ${name} ${username}` : `View ${name}`} style={{ position: "relative", margin: "0 auto" }}>
                        <TeamAvatar isTeam={!!(participant?.team_name || participant?.team_id)} mode={participant?.image_display_mode} src={participantImage(participant)} name={name} size={avatarSize} />
                      </button>
                      {placement && (
                        <div style={{ margin: "5px auto 0", width: 48, borderRadius: 999, background: placement.place === 1 ? "rgba(245,184,46,0.18)" : "rgba(255,255,255,0.10)", border: `1px solid ${placement.place === 1 ? "rgba(245,184,46,0.42)" : "rgba(255,255,255,0.16)"}`, color: placement.place === 1 ? C.gold : "rgba(255,255,255,0.82)", fontSize: 10, fontWeight: 900, textAlign: "center", lineHeight: "18px" }}>
                          {getOrdinal(placement.place)}
                        </div>
                      )}
                      <div className="app-tree-name-label" title={name}>{name}</div>
                      {username && <div className="app-tree-username-label" title={username}>{username}</div>}
                    </>
                  ) : (
                    <>
                      <div className="app-tree-empty-node" style={{ width: avatarSize, height: avatarSize, margin: "0 auto" }}>
                        <FontAwesomeIcon icon={faUser} aria-label="Open participant slot" style={{ fontSize: 14 }} />
                      </div>
                      <div className="app-tree-name-label" title={name}>{name}</div>
                    </>
                  )}
                </div>
              );
            })}
            <div className="app-tree-node" style={{ top: resultY - 62, left: resultX - 18, width: 130 }}>
              {winner ? (
                <>
                  <button type="button" onClick={() => onParticipantOpen?.(winner)} title={winnerName} aria-label={`View race winner ${winnerName}`} style={{ position: "relative", margin: "0 auto" }}>
                    <TeamAvatar isTeam={!!(winner?.team_name || winner?.team_id)} mode={winner?.image_display_mode} src={participantImage(winner)} name={winnerName} size={58} />
                    <span className="app-tree-advance-dot" aria-label="Winner" />
                  </button>
                  <div style={{ margin: "6px auto 0", width: 56, borderRadius: 999, background: "rgba(245,184,46,0.18)", border: "1px solid rgba(245,184,46,0.42)", color: C.gold, fontSize: 10, fontWeight: 900, textAlign: "center", lineHeight: "19px" }}>1st</div>
                  <div className="app-tree-name-label" title={winnerName}>{winnerName}</div>
                </>
              ) : winnerPlacement ? (
                <>
                  <div className="app-tree-empty-node" style={{ width: 58, height: 58, margin: "0 auto", borderColor: "rgba(245,184,46,0.42)", color: C.gold }}>
                    <FontAwesomeIcon icon={faTrophy} aria-label="Race winner recorded" style={{ fontSize: 18 }} />
                  </div>
                  <div className="app-tree-name-label" title={winnerName || "Winner"}>{winnerName || "Winner"}</div>
                </>
              ) : (
                <>
                  <div className="app-tree-empty-node" style={{ width: 58, height: 58, margin: "0 auto" }}>
                    <FontAwesomeIcon icon={faTrophy} aria-label="Race result pending" style={{ fontSize: 18 }} />
                  </div>
                  <div className="app-tree-name-label" title="Race result pending">Result pending</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {participants.length > 0 && placements.length === 0 && (
        <div style={{ margin: "0 18px 18px", padding: "14px 16px", borderRadius: 16, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.62)", fontSize: 13, fontWeight: 800 }}>
          Race result has not been recorded yet.
        </div>
      )}
    </div>
  );
}

function AppGroupCard({ teamIds, label, stage, advanceIds, participantsById, matchResults, formatLabel, legs, differenceLabel, onParticipantOpen }) {
  const sortStats = deriveStatsFromResults(teamIds, matchResults, stage);
  const displayStats = deriveStatsFromResults(teamIds, matchResults, null);
  const sorted = [...teamIds].sort((a, b) =>
    (sortStats[b]?.pts ?? 0) - (sortStats[a]?.pts ?? 0) ||
    (sortStats[b]?.gd ?? 0) - (sortStats[a]?.gd ?? 0) ||
    tournamentDisplayName(participantsById.get(a)).localeCompare(tournamentDisplayName(participantsById.get(b)))
  );
  const schedule = buildRoundRobinSchedule(teamIds);
  const pairs = [];
  const roundsPerLeg = Math.max(1, teamIds.length - 1);
  schedule.forEach(({ t1, t2, round }) => {
    const results = findPairResults(matchResults, stage, t1, t2);
    for (let leg = 1; leg <= legs; leg += 1) {
      pairs.push({ t1, t2, leg, scheduleRound: round + ((leg - 1) * roundsPerLeg), played: results.length >= leg });
    }
  });
  const done = pairs.filter((pair) => pair.played);
  const pending = pairs.filter((pair) => !pair.played);
  const progress = pairs.length ? Math.round((done.length / pairs.length) * 100) : 0;
  const leader = sorted[0];
  const leaderStats = leader ? displayStats[leader] : null;

  return (
    <div className="app-tree-group-card">
      <div className="app-tree-group-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 900 }}>{label}</span>
        </div>
        <span style={{ fontSize: 11, fontWeight: 900, opacity: .78 }}>{pending.length ? "In progress" : "Complete"} · {done.length}/{pairs.length} games</span>
      </div>
      <div className="app-tree-summary">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: C.gold, fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: .8 }}>{formatLabel}</div>
            <div style={{ color: C.white, fontSize: 15, fontWeight: 900, marginTop: 2 }}>{pending.length ? `${pending.length} fixture${pending.length === 1 ? "" : "s"} remaining` : "All fixtures complete"}</div>
          </div>
          {leader && (
            <div style={{ minWidth: 0, borderRadius: 999, padding: "7px 10px", background: "rgba(213,164,25,0.12)", color: C.gold, fontSize: 11, fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {tournamentDisplayName(participantsById.get(leader))} {leaderStats ? `${leaderStats.pts} pts` : ""}
            </div>
          )}
        </div>
        <div className="app-tree-progress"><span style={{ width: `${progress}%` }} /></div>
      </div>
      <div>
        {sorted.map((id, index) => {
          const participant = participantsById.get(id);
          const stats = displayStats[id] || { played: 0, wins: 0, draws: 0, losses: 0, gd: 0, pts: 0 };
          const advanced = advanceIds.includes(id);
          const username = tournamentUsernameLabel(participant);
          return (
            <button
              key={id}
              type="button"
              className="app-tree-standing-row app-tree-standing-button"
              onClick={() => participant && onParticipantOpen?.(participant)}
              aria-label={`View ${tournamentDisplayName(participant)}`}
            >
              <div className={`app-tree-rank ${index === 0 ? "first" : index === 1 ? "second" : index === 2 ? "third" : ""}`}>{index + 1}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <TeamAvatar isTeam={!!(participant?.team_name || participant?.team_id)} mode={participant?.image_display_mode} src={participantImage(participant)} name={tournamentDisplayName(participant)} size={34} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: C.white, fontSize: 13, fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tournamentDisplayName(participant)}</span>
                    {advanced && <span style={{ color: C.green, fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: .6 }}>Advanced</span>}
                  </div>
                  <div style={{ color: C.w35, fontSize: 11, fontWeight: 750 }}>
                    {username ? `${username} · ` : ""}{stats.wins}W {stats.draws}D {stats.losses}L · {stats.played} played
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ minWidth: 38, textAlign: "center" }}><div style={{ color: C.gold, fontSize: 16, fontWeight: 900 }}>{stats.pts}</div><div style={{ color: C.w35, fontSize: 9, fontWeight: 900 }}>Pts</div></div>
                <div style={{ minWidth: 38, textAlign: "center" }}><div style={{ color: stats.gd >= 0 ? C.green : C.red, fontSize: 14, fontWeight: 900 }}>{stats.gd > 0 ? `+${stats.gd}` : stats.gd}</div><div style={{ color: C.w35, fontSize: 9, fontWeight: 900 }}>{differenceLabel}</div></div>
              </div>
            </button>
          );
        })}
      </div>
      {(pending.length > 0 || done.length > 0) && (
        <div className="app-tree-fixtures">
          <div style={{ color: C.white, fontSize: 13, fontWeight: 900 }}>{pending.length ? "Next fixtures" : "Recent results"}</div>
          {(pending.length ? pending : done.slice(-4).reverse()).slice(0, 6).map((pair, index) => {
            const result = findPairResults(matchResults, stage, pair.t1, pair.t2)[pair.leg - 1] || null;
            const first = result?.team1_id ?? result?.player1_profile_id;
            const scoreA = result ? (first === pair.t1 ? result.participant1_score : result.participant2_score) : null;
            const scoreB = result ? (first === pair.t1 ? result.participant2_score : result.participant1_score) : null;
            return (
              <div key={`${pair.t1}-${pair.t2}-${pair.leg}-${index}`} className="app-tree-fixture-row">
                <span style={{ color: C.white, fontSize: 12, fontWeight: 850, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tournamentDisplayName(participantsById.get(pair.t1))}</span>
                <span className="app-tree-vs">{result ? `${scoreA}-${scoreB}` : legs > 1 ? `Leg ${pair.leg}` : "VS"}</span>
                <span style={{ color: C.white, fontSize: 12, fontWeight: 850, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "right" }}>{tournamentDisplayName(participantsById.get(pair.t2))}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AppTournamentTree({ enrolled, matchResults, tournament, onParticipantOpen }) {
  const normalizedEnrolled = useMemo(() => stableSort(getCompetitionRows(enrolled)).map(normalizeEnrolledRow), [enrolled]);
  const participants = useMemo(() => normalizedEnrolled.filter((row) => !!getParticipantId(row)), [normalizedEnrolled]);
  const participantsById = useMemo(() => new Map(participants.map((row) => [getParticipantId(row), row])), [participants]);
  const config = parseConfigObject(tournament?.format_config);
  const formatKey = tournament?.format_key || config.format_key || (String(tournament?.game_mode || "").toLowerCase() === "point_based" ? "round_robin" : "single_elimination");
  const isRace = isFitnessRaceTournament(tournament);
  const isPointBased = String(tournament?.game_mode || config.mode || "").toLowerCase() === "point_based" || ["round_robin", "double_round_robin", "group_to_knockout"].includes(formatKey);
  const plan = getPointFormatPlan(formatKey, participants.length);
  const storedStage2Ids = useMemo(
    () => Array.isArray(tournament?.stage_two_participants) ? tournament.stage_two_participants : [],
    [tournament?.stage_two_participants]
  );
  const stage2Ids = useMemo(() => {
    if (storedStage2Ids.length > 0 || formatKey !== "group_to_knockout") return storedStage2Ids;
    return deriveGroupStage2Ids(participants, matchResults);
  }, [storedStage2Ids, formatKey, participants, matchResults]);
  const storedStage3Ids = useMemo(
    () => Array.isArray(tournament?.stage_three_participants) ? tournament.stage_three_participants : [],
    [tournament?.stage_three_participants]
  );
  const stage3Ids = useMemo(() => {
    if (storedStage3Ids.length > 0 || formatKey !== "group_to_knockout") return storedStage3Ids;
    return deriveGroupStage3Ids(stage2Ids, matchResults);
  }, [storedStage3Ids, formatKey, stage2Ids, matchResults]);
  const differenceLabel = getDifferenceLabel(tournament?.sport);

  const allIds = participants.map(getParticipantId);
  const groups = useMemo(() => {
    if (!isPointBased) return [];
    const groupSize = plan.singleTable ? Math.max(allIds.length, 2) : plan.groupSize;
    const groupCount = Math.ceil(allIds.length / groupSize);
    return Array.from({ length: groupCount }).map((_, index) => allIds.slice(index * groupSize, index * groupSize + groupSize));
  }, [allIds, isPointBased, plan.groupSize, plan.singleTable]);

  return (
    <div className="app-tree-shell anim-1">
      {participants.length === 0 ? (
        <div className="public-live-state">
          <div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 30, fontWeight: 900, color: "#FFFFFF", marginBottom: 10 }}>
              {isRace ? "No racers enrolled yet" : "No teams enrolled yet"}
            </div>
            <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 15, fontWeight: 700 }}>
              {isRace ? "Once participants join, the race result view will appear here." : "Once players join, the live bracket will appear here."}
            </div>
          </div>
        </div>
      ) : isRace ? (
        <AppRaceTree participants={participants} tournament={tournament} onParticipantOpen={onParticipantOpen} />
      ) : isPointBased ? (
        <div className="app-tree-groups">
          {groups.map((groupIds, index) => (
            <AppGroupCard
              key={index}
              teamIds={groupIds}
              label={plan.singleTable ? plan.label : groups.length === 1 ? "Standings" : `Group ${String.fromCharCode(65 + index)}`}
              stage={1}
              advanceIds={stage2Ids.filter((id) => groupIds.includes(id))}
              participantsById={participantsById}
              matchResults={matchResults}
              formatLabel={plan.label}
              legs={plan.legs}
              differenceLabel={differenceLabel}
              onParticipantOpen={onParticipantOpen}
            />
          ))}
          {plan.hasKnockout && stage2Ids.length > 0 && (
            <AppGroupCard
              teamIds={stage2Ids}
              label={stage2Ids.length === 4 ? "Semi-finals" : "Knockout Matches"}
              stage={2}
              advanceIds={stage3Ids}
              participantsById={participantsById}
              matchResults={matchResults}
              formatLabel="Knockout"
              legs={1}
              differenceLabel={differenceLabel}
              onParticipantOpen={onParticipantOpen}
            />
          )}
          {plan.hasKnockout && stage3Ids.length > 0 && (
            <AppGroupCard
              teamIds={stage3Ids}
              label="Championship"
              stage={3}
              advanceIds={[]}
              participantsById={participantsById}
              matchResults={matchResults}
              formatLabel="Final"
              legs={1}
              differenceLabel={differenceLabel}
              onParticipantOpen={onParticipantOpen}
            />
          )}
        </div>
      ) : formatKey === "double_elimination" ? (
        <AppDoubleEliminationTree participants={participants} enrolled={normalizedEnrolled} matchResults={matchResults} tournament={tournament} onParticipantOpen={onParticipantOpen} />
      ) : (
        <AppBracketTree participants={participants} enrolled={normalizedEnrolled} matchResults={matchResults} onParticipantOpen={onParticipantOpen} />
      )}
    </div>
  );
}

function PublicBracketPreview(props) {
  return <AppTournamentTree {...props} />;
}

function JoinExperience({ tournament, enrolled }) {
  const openRows = enrolled.filter((row) => row.is_open && Number(row.spots_in_team || 0) > 0);
  const spotsLeft = tournament?.spots_left ?? Math.max(0, (tournament?.spots_available || enrolled.length) - enrolled.length);
  return (
    <div className="anim-1">
      <SectionLabel>Join From The App</SectionLabel>
      <div className="join-card-grid">
        <div style={{ background: C.card, border: `1px solid ${C.goldMed}`, borderRadius: 18, padding: 22 }}>
          <div style={{ color: C.gold, fontSize: 11, fontWeight: 900, letterSpacing: 1.2, textTransform: "uppercase" }}>Tournament link</div>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, lineHeight: 1, margin: "10px 0", color: C.white }}>Players join without relearning the flow.</h2>
          <p style={{ color: C.w55, lineHeight: 1.55, fontSize: 14 }}>The public website keeps spectators informed. Players still join and manage their entry from ProAm mobile so the app and web stay consistent.</p>
          <button
            type="button"
            onClick={() => window.open("https://linktr.ee/snowsproam", "_blank", "noopener,noreferrer")}
            style={{ marginTop: 18, border: "none", borderRadius: 12, padding: "12px 16px", background: `linear-gradient(135deg,${C.gold},${C.gold2})`, color: "#000", fontWeight: 900, cursor: "pointer" }}
          >
            Open ProAm App
          </button>
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22 }}>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 900, marginBottom: 14 }}>Entry status</div>
          {[
            ["Capacity", `${enrolled.length}/${tournament?.spots_available || enrolled.length || 16}`],
            ["Open tournament spots", String(spotsLeft)],
            ["Open team spots", String(openRows.reduce((sum, row) => sum + Number(row.spots_in_team || 0), 0))],
          ].map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, padding: "12px 0", color: C.w55, fontWeight: 750 }}>
              <span>{label}</span>
              <span style={{ color: C.gold, fontWeight: 900 }}>{value}</span>
            </div>
          ))}
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: 22 }}>
          <div style={{ color: C.white, fontSize: 17, fontWeight: 900, marginBottom: 14 }}>Open teams</div>
          {openRows.length ? openRows.slice(0, 5).map((row) => (
            <div key={row.id} style={{ display: "flex", alignItems: "center", gap: 10, borderTop: `1px solid ${C.border}`, padding: "11px 0" }}>
              <TeamAvatar isTeam mode={row.image_display_mode} src={row.team_image || row.profile_img} name={tournamentDisplayName(row)} size={30} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.white, fontSize: 13, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{tournamentDisplayName(row)}</div>
                <div style={{ color: C.w35, fontSize: 11, marginTop: 2 }}>{row.spots_in_team} spot{Number(row.spots_in_team) === 1 ? "" : "s"} open</div>
              </div>
            </div>
          )) : (
            <div style={{ color: C.w35, fontSize: 13 }}>No open team slots right now.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProAdminPanel({ tournament, canManageWeb, isProTournament, isCreator, isApprovedAdmin, liveDashboardEnabled, onToggleLiveDashboard, saving, engineMatches = [], queueRows = [] }) {
  return (
    <div className="anim-1" style={{ background: C.card, border: `1px solid ${canManageWeb ? C.goldMed : C.border}`, borderRadius: 20, padding: 24, boxShadow: canManageWeb ? "0 24px 70px rgba(12,105,200,0.10)" : "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <div style={{ color: canManageWeb ? C.gold : C.w35, fontSize: 11, fontWeight: 900, letterSpacing: 1.4, textTransform: "uppercase", marginBottom: 8 }}>
            Pro Admin
          </div>
          <h2 style={{ fontFamily: "'Sora',sans-serif", color: C.white, fontSize: "clamp(30px,4vw,48px)", lineHeight: 1, margin: 0 }}>
            Live dashboard broadcast
          </h2>
          <p style={{ color: C.w55, lineHeight: 1.55, maxWidth: 640, marginTop: 10 }}>
            Turn this on when the tournament should behave like a professional public scoreboard. Viewers can follow the bracket, entry status, and results in real time at this tournament URL.
          </p>
        </div>
        <div style={{ color: liveDashboardEnabled ? C.green : C.w35, background: liveDashboardEnabled ? C.greenDim : C.w08, border: `1px solid ${liveDashboardEnabled ? "rgba(74,222,128,0.35)" : C.border2}`, borderRadius: 999, padding: "8px 12px", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>
          {liveDashboardEnabled ? "Live dashboard on" : "Live dashboard off"}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 12, marginBottom: 18 }}>
        {[
          ["Tournament plan", isProTournament ? "Pro Tournament" : tournament?.package_key || "Free/Standard"],
          ["Signed-in role", isCreator ? "Creator" : isApprovedAdmin ? "Approved admin" : "Viewer"],
          ["Public URL", `/tournaments/${encodeURIComponent(tournament?.tournament_name || "")}`],
          ["Engine matches", String(engineMatches.length)],
          ["Match queue", String(queueRows.length)],
          ["Schedule config", tournament?.schedule_config?.previewSlots?.length ? `${tournament.schedule_config.previewSlots.length} slots` : "Not set"],
        ].map(([label, value]) => (
          <div key={label} style={{ background: C.navy2, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14 }}>
            <div style={{ color: C.w35, fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
            <div style={{ color: C.white, fontSize: 14, fontWeight: 850, marginTop: 7, overflowWrap: "anywhere" }}>{value}</div>
          </div>
        ))}
      </div>
      {!isProTournament && (
        <div style={{ background: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 14, padding: 14, color: C.red, fontSize: 13, fontWeight: 750, marginBottom: 16 }}>
          Live dashboard settings are only available on the Pro Tournament package.
        </div>
      )}
      {isProTournament && !canManageWeb && (
        <div style={{ background: C.w08, border: `1px solid ${C.border2}`, borderRadius: 14, padding: 14, color: C.w55, fontSize: 13, fontWeight: 750, marginBottom: 16 }}>
          Sign in with the creator account or an approved tournament admin account to manage this Pro dashboard.
        </div>
      )}
      <button
        type="button"
        disabled={!canManageWeb || saving}
        onClick={onToggleLiveDashboard}
        style={{
          border: "none",
          borderRadius: 13,
          padding: "13px 18px",
          background: canManageWeb ? (liveDashboardEnabled ? C.w08 : `linear-gradient(135deg,${C.gold},${C.gold2})`) : C.w08,
          color: canManageWeb ? (liveDashboardEnabled ? C.white : "#000") : C.w35,
          fontWeight: 900,
          cursor: canManageWeb && !saving ? "pointer" : "not-allowed",
          minHeight: 46,
        }}
      >
        {saving ? "Saving..." : liveDashboardEnabled ? "Turn Off Live Dashboard" : "Turn On Live Dashboard"}
      </button>
    </div>
  );
}

function TournamentAdminConsole({
  tournament, enrolled, matchResults, engineMatches, queueRows, adminRows,
  creatorProfile,
  activeTab, setActiveTab, canEdit, canManageWebDashboard, isCreator, isApprovedAdmin,
  liveDashboardEnabled, onRefresh, onReset,
  resetting, simulationCount, setSimulationCount, onGenerateSimulation, onClearSimulation,
  simSaving, pageFormatKey, allowedFormats, tournamentPackage, onChangeFormat, formatSaving,
  onSubmitMatch, matchSavingId, onUpdateParticipantStatus, participantSavingId,
  showToast, userId,
}) {
  const [scoreDrafts, setScoreDrafts] = useState({});
  const [queueTeamA, setQueueTeamA] = useState(null);
  const [queueTeamB, setQueueTeamB] = useState(null);
  const [addingToQueue, setAddingToQueue] = useState(false);
  const [raceSavingId, setRaceSavingId] = useState(null);
  const isRaceTournament = isFitnessRaceTournament(tournament);
  const tabs = isRaceTournament
    ? [["overview", "Overview"], ["result", "Result"], ["admins", "Admins"]]
    : [["overview", "Overview"], ["format", "Format"], ["match", "Match"], ["queue", "Queue"], ["simulate", "Sim"], ["admins", "Admins"]];
  const selectedTab = tabs.some(([key]) => key === activeTab) ? activeTab : tabs[0][0];
  const simulatedRows = enrolled.filter((row) => row.is_simulated || row.isSimulated);
  const hasSimulationRows = simulatedRows.length > 0;
  const competitionRows = hasSimulationRows ? simulatedRows : enrolled;
  const competitionRowIds = useMemo(() => new Set(competitionRows.map((row) => row.id).filter(Boolean)), [competitionRows]);
  const completedMatches = matchResults.filter((row) => {
    const hasScore = row.participant1_score != null || row.participant2_score != null;
    if (!hasScore) return false;
    if (!hasSimulationRows) return true;
    if (row.participant1_enrolled_id || row.participant2_enrolled_id) {
      return competitionRowIds.has(row.participant1_enrolled_id) && competitionRowIds.has(row.participant2_enrolled_id);
    }
    const activeIds = new Set(competitionRows.map(getParticipantId).filter(Boolean));
    const p1 = row.team1_id ?? row.player1_profile_id;
    const p2 = row.team2_id ?? row.player2_profile_id;
    return activeIds.has(p1) && activeIds.has(p2);
  });
  const teamNameMap = useMemo(() => new Map(competitionRows.map((row) => [getParticipantId(row), tournamentDisplayName(row)])), [competitionRows]);
  const enrolledById = useMemo(() => new Map(competitionRows.flatMap((row) => [
    [row.id, row],
    [row.team_id, row],
    [row.profile_id, row],
  ].filter(([key]) => !!key))), [competitionRows]);
  const formatLabel = isRaceTournament ? FITNESS_RACE_GAME_TYPE : formatTournamentFormatLabel(pageFormatKey);
  const role = isCreator ? "Creator" : isApprovedAdmin ? "Approved admin" : "Viewer";
  const racePlacements = useMemo(() => getRacePlacements(tournament), [tournament]);
  const raceWinnerPlacement = useMemo(() => getRaceWinnerPlacement(tournament), [tournament]);
  const raceConfig = parseConfigObject(tournament?.format_config);
  const raceConfiguredCapacity = Number(tournament?.spots_available || 0) || Number(raceConfig.max_participants || 0) || competitionRows.length || 1;
  const raceMaxPlaces = Math.max(1, raceConfiguredCapacity, competitionRows.length || 0);
  const racePlaceOptions = useMemo(() => Array.from({ length: raceMaxPlaces }).map((_, index) => index + 1), [raceMaxPlaces]);
  const raceWinner = raceWinnerPlacement ? findParticipantForRacePlacement(competitionRows, raceWinnerPlacement) : null;
  const raceWinnerName = raceWinner ? tournamentDisplayName(raceWinner) : raceWinnerPlacement?.name || "Not set";
  const statusTitle = isRaceTournament
    ? raceWinnerPlacement ? "Race result recorded" : competitionRows.length ? "Ready to record results" : "No racers yet"
    : tournament?.tournament_finished ? "Tournament finished" : completedMatches.length ? "Tournament in progress" : competitionRows.length ? "Ready to score" : "No players yet";
  const statusBody = isRaceTournament
    ? competitionRows.length === 0 ? "Wait for participants to join before recording places." : "Use Result to assign first place, second place, and any additional places you want to publish."
    : competitionRows.length === 0 ? "Use the Sim tab to test the format, or wait for participants to join." : completedMatches.length === 0 ? "Use Match to enter the first game result." : "Use Match for results and Overview for player progression.";
  const statItems = isRaceTournament
    ? [["Racers", competitionRows.length], ["Places", racePlacements.length], ["Winner", raceWinnerName], ["Capacity", raceMaxPlaces]]
    : [[hasSimulationRows ? "Sim Players" : "Players", competitionRows.length], ["Plan", isProTournamentPackage(tournament) ? "Pro" : tournament?.package_key || "Standard"], ["Games", completedMatches.length], ["Waiting", queueRows.length]];
  const computedNextMatches = useMemo(() => {
    if (isRaceTournament) return [];
    const dbPending = engineMatches.filter((match) =>
      match.status !== "completed" &&
      match.participant1_enrolled_id &&
      match.participant2_enrolled_id &&
      (!hasSimulationRows || (competitionRowIds.has(match.participant1_enrolled_id) && competitionRowIds.has(match.participant2_enrolled_id)))
    );
    if (dbPending.length > 0) return dbPending;
    const rows = stableSort(competitionRows).filter((row) => !!row.id);
    const formatKey = pageFormatKey;
    const mode = String(tournament?.game_mode || "").toLowerCase();
    const identityOf = (row) => getParticipantId(row);
    const byIdentity = new Map(rows.map((row) => [identityOf(row), row]));
    const makeComputed = (rowA, rowB, index, metadata = {}) => ({
      id: `computed-${metadata.stage || 1}-${rowA.id}-${rowB.id}-${metadata.leg || 1}`,
      tournament_id: tournament?.id,
      participant1_enrolled_id: rowA.id,
      participant2_enrolled_id: rowB.id,
      participant1_score: null,
      participant2_score: null,
      winner_enrolled_id: null,
      round_number: metadata.scheduleRound || metadata.bracket_round || metadata.stage || 1,
      match_number: index + 1,
      status: "pending",
      source: "dashboard",
      is_computed: true,
      metadata: {
        format_key: formatKey,
        bracket: mode === "point_based" ? "point" : metadata.bracket || "winners",
        ...metadata,
      },
    });
    if (rows.length < 2) return [];
    if (mode === "point_based" || ["round_robin", "double_round_robin", "group_to_knockout"].includes(formatKey)) {
      const participantIds = rows.map(identityOf).filter(Boolean);
      const storedStage2Ids = Array.isArray(tournament?.stage_two_participants) ? tournament.stage_two_participants : [];
      const stage2Ids = storedStage2Ids.length || formatKey !== "group_to_knockout" ? storedStage2Ids : deriveGroupStage2Ids(rows, matchResults);
      const storedStage3Ids = Array.isArray(tournament?.stage_three_participants) ? tournament.stage_three_participants : [];
      const stage3Ids = storedStage3Ids.length || formatKey !== "group_to_knockout" ? storedStage3Ids : deriveGroupStage3Ids(stage2Ids, matchResults);
      const legs = formatKey === "double_round_robin" ? 2 : 1;
      let stage = 1;
      let ids = participantIds;
      let label = formatTournamentFormatLabel(formatKey);
      let groups = [];
      if (formatKey === "group_to_knockout") {
        if (stage3Ids.length > 0) {
          stage = 3;
          ids = stage3Ids;
          label = "Final";
          groups = [stage3Ids];
        } else if (stage2Ids.length > 0) {
          stage = 2;
          ids = stage2Ids;
          label = stage2Ids.length <= 2 ? "Final" : "Semi-finals";
          groups = stage2Ids.length <= 2 ? [stage2Ids] : getGroupKnockoutPairs(stage2Ids);
        } else {
          const groupSize = participantIds.length === 6 ? 3 : 4;
          groups = [];
          for (let i = 0; i < participantIds.length; i += groupSize) groups.push(participantIds.slice(i, i + groupSize));
          label = "Group stage";
        }
      } else {
        groups = [ids];
      }
      const pending = [];
      groups.forEach((groupIds) => {
        const schedule = stage === 1 ? buildRoundRobinSchedule(groupIds) : getGroupKnockoutPairs(groupIds).map(([t1, t2], idx) => ({ t1, t2, round: idx + 1 }));
        const roundsPerLeg = Math.max(1, groupIds.length - 1);
        schedule.forEach(({ t1, t2, round }) => {
          const rowA = byIdentity.get(t1);
          const rowB = byIdentity.get(t2);
          if (!rowA || !rowB) return;
          for (let leg = 1; leg <= legs; leg += 1) {
            const existing = findPairResults(matchResults, stage, t1, t2);
            if (existing.length >= leg) continue;
            pending.push(makeComputed(rowA, rowB, pending.length, {
              stage,
              leg,
              stage_label: label,
              scheduleRound: round + ((leg - 1) * roundsPerLeg),
              bracket_round: round + ((leg - 1) * roundsPerLeg),
              bracket: "point",
            }));
          }
        });
      });
      return pending;
    }
    const buildPairs = (candidateRows, bracket) => {
      const sorted = [...candidateRows].sort((a, b) => {
        const statusA = bracket === "redemption" ? Math.abs(getDoubleElimStatus(a)) : (a.status ?? 0);
        const statusB = bracket === "redemption" ? Math.abs(getDoubleElimStatus(b)) : (b.status ?? 0);
        return statusA - statusB || String(identityOf(a)).localeCompare(String(identityOf(b)));
      });
      if (sorted.length < 2) return [];
      const targetStatus = bracket === "redemption" ? Math.abs(getDoubleElimStatus(sorted[0])) : (sorted[0].status ?? 0);
      const roundRows = sorted.filter((row) => bracket === "redemption" ? Math.abs(getDoubleElimStatus(row)) === targetStatus : (row.status ?? 0) === targetStatus);
      const pairs = [];
      for (let i = 0; i < roundRows.length; i += 2) {
        const rowA = roundRows[i];
        const rowB = roundRows[i + 1];
        if (!rowA || !rowB) continue;
        const idA = identityOf(rowA);
        const idB = identityOf(rowB);
        if ((rowA.played || []).includes(idB) || (rowB.played || []).includes(idA)) continue;
        pairs.push(makeComputed(rowA, rowB, pairs.length, { stage: 1, bracket, bracket_round: targetStatus + 1 }));
      }
      return pairs;
    };
    const winnersRows = rows.filter((row) => !(row.knocked_out ?? false) && !(row.knocked_out2 ?? false));
    const winnersPairs = buildPairs(winnersRows, "winners");
    if (formatKey !== "double_elimination") return winnersPairs;
    const redemptionRows = rows.filter((row) => (row.knocked_out ?? false) && !(row.knocked_out2 ?? false));
    return [...winnersPairs, ...buildPairs(redemptionRows, "redemption")];
  }, [competitionRowIds, competitionRows, engineMatches, hasSimulationRows, isRaceTournament, matchResults, pageFormatKey, tournament]);
  const Hero = ({ kicker, title, body }) => (
    <div className="admin-app-hero">
      <div className="admin-app-hero-icon"><FontAwesomeIcon icon={faPen} /></div>
      <div><div className="admin-app-kicker">{kicker}</div><div className="admin-app-title">{title}</div><div className="admin-app-body">{body}</div></div>
    </div>
  );
  const Stats = ({ items }) => <div className="admin-stat-row">{items.map(([label, value]) => <div className="admin-stat" key={label}><strong>{value}</strong><span>{label}</span></div>)}</div>;
  const AdminAvatar = ({ src, name, className = "" }) => (
    <div className={`admin-avatar ${className}`}>
      {src ? <img src={src} alt="" loading="lazy" /> : <span>{String(name || "U").charAt(0).toUpperCase()}</span>}
    </div>
  );
  const ParticipantCard = ({ row, index, rightSlot, meta, attachedFooter = false }) => {
    const name = tournamentDisplayName(row);
    const username = tournamentUsernameLabel(row);
    const mode = String(tournament?.game_mode || row.game_mode || "").toLowerCase();
    const isDoubleElimination = pageFormatKey === "double_elimination";
    const displayStatus = isDoubleElimination ? getDoubleElimStatus(row) : (row.status ?? 0);
    const eliminated = isDoubleElimination ? !!row.knocked_out2 : !!row.knocked_out;
    const status = meta || (mode === "point_based" ? "" : `Stage ${displayStatus}`);
    const subStatus = "";
    const stat = mode === "point_based"
      ? `${row.points ?? 0} pts`
      : eliminated ? "Out" : `S${displayStatus}`;
    return (
      <div className={`admin-player-card ${attachedFooter ? "admin-player-card-attached" : ""}`}>
        {typeof index === "number" && index < 3 && <div className={`admin-rank admin-rank-${index + 1}`}>{index + 1}</div>}
        <AdminAvatar src={participantImage(row)} name={name} />
        <div className="admin-person-main">
          <strong>{typeof index === "number" ? `${index + 1}. ${name}` : name}</strong>
          {username && <span>{username}</span>}
          {status && <small>{status}</small>}
          {subStatus && <small>{subStatus}</small>}
        </div>
        <div className="admin-player-stat">
          <strong>{stat}</strong>
          <span>{row.played?.length ?? 0} played</span>
        </div>
        {rightSlot}
      </div>
    );
  };
  const getPlayerStatusActions = (row) => {
    const disableStageStepControls = pageFormatKey === "round_robin" || pageFormatKey === "double_round_robin";
    const statusFixButtons = pageFormatKey === "double_elimination"
      ? [
          {
            label: "1 Loss",
            active: !!row.knocked_out && !row.knocked_out2,
            color: "#0C69C8",
            update: () => {
              const activating = !(row.knocked_out && !row.knocked_out2);
              return {
                status: activating ? getDoubleElimStatus({ ...row, knocked_out: true, knocked_out2: false }) : Math.max(0, Math.abs(row.status ?? 0) - 1),
                knocked_out: activating,
                knocked_out2: false,
              };
            },
          },
          {
            label: "Elim",
            active: !!row.knocked_out2,
            color: "#DC2626",
            update: () => {
              const activating = !row.knocked_out2;
              return {
                status: activating ? getDoubleElimStatus({ ...row, knocked_out: true, knocked_out2: false }) : Math.max(0, Math.abs(row.status ?? 0) - 1),
                knocked_out: activating,
                knocked_out2: activating,
              };
            },
          },
        ]
      : [
          {
            label: "Out",
            active: !!row.knocked_out,
            color: "#DC2626",
            disabled: disableStageStepControls,
            update: () => ({ knocked_out: !row.knocked_out, knocked_out2: false }),
          },
        ];
    return [
      ...statusFixButtons,
      {
        label: "Back",
        active: false,
        color: "#0F172A",
        disabled: disableStageStepControls,
        update: () => ({ status: (row.status ?? 0) - 1 }),
      },
      {
        label: "Advance",
        active: false,
        color: "#0F172A",
        disabled: disableStageStepControls,
        update: () => ({ status: (row.status ?? 0) + 1 }),
      },
    ];
  };
  const renderPlayerControls = (row) => {
    const controlsDisabled = String(tournament?.game_mode || "").toLowerCase() === "point_based" && pageFormatKey === "group_to_knockout";
    return (
      <div className="admin-player-actions">
        <div className="admin-player-actions-label">Player controls</div>
        <div className="admin-player-actions-row">
          {getPlayerStatusActions(row).map((action) => {
            const disabled = !canEdit || participantSavingId === row.id || controlsDisabled || !!action.disabled;
            return (
              <button
                key={action.label}
                type="button"
                className="admin-player-action-btn"
                data-active={action.active ? "true" : "false"}
                style={action.active ? { borderColor: action.color, background: `${action.color}18`, color: action.color } : undefined}
                disabled={disabled}
                onClick={() => onUpdateParticipantStatus(row, action.update())}
                aria-pressed={!!action.active}
                aria-label={`${action.label} ${tournamentDisplayName(row)}`}
              >
                {participantSavingId === row.id ? "..." : action.label}
              </button>
            );
          })}
        </div>
        {!canEdit && <div className="admin-muted" style={{ marginTop: 8 }}>Sign in as creator/admin to change player status.</div>}
      </div>
    );
  };
  const PersonCard = ({ profile, roleLabel, meta, action }) => {
    const name = profileDisplayName(profile, roleLabel || "User");
    return (
      <div className="admin-person-card">
        <AdminAvatar src={profile?.profile_img} name={name} />
        <div className="admin-person-main">
          <strong>{name}</strong>
          <span>{profileUsernameLabel(profile)}</span>
          {meta && <small>{meta}</small>}
        </div>
        {roleLabel && <div className="admin-role-pill">{roleLabel}</div>}
        {action}
      </div>
    );
  };
  const getMatchParticipant = (match, side) => {
    const enrolledId = side === 1 ? match.participant1_enrolled_id : match.participant2_enrolled_id;
    const profileId = side === 1 ? match.player1_profile_id : match.player2_profile_id;
    const teamId = side === 1 ? match.team1_id : match.team2_id;
    return enrolledById.get(enrolledId) || enrolledById.get(profileId) || enrolledById.get(teamId) || null;
  };
  const formatMatchBracketLabel = (match) => {
    const metadata = match?.metadata || {};
    const bracket = metadata.bracket || match?.bracket || match?.match_source || match?.source || "";
    const stage = Number(metadata.stage ?? match?.stage ?? match?.round_number ?? 1);
    if (metadata.stage_label) return metadata.stage_label;
    if (bracket === "point") {
      if (stage === 3) return "Final";
      if (stage === 2) return "Semi-finals";
      if (metadata.format_key === "group_to_knockout" || pageFormatKey === "group_to_knockout") return "Group stage";
      return metadata.format_key === "double_round_robin" || pageFormatKey === "double_round_robin" ? "Double round robin" : "Round robin";
    }
    if (bracket === "redemption") return "Redemption bracket";
    if (bracket === "winners" || bracket === "manual_queue" || bracket === "dashboard" || !bracket) return "Winners bracket";
    return `${String(bracket).replace(/_/g, " ")} bracket`;
  };
  const getPendingMatchCardLabel = (match) => match?.metadata?.bracket === "point" && Number(match?.metadata?.leg ?? 1) > 1
    ? `Leg ${match.metadata.leg}`
    : "Next game";
  const renderPreviousMatchCard = (match) => {
    const p1 = getMatchParticipant(match, 1);
    const p2 = getMatchParticipant(match, 2);
    const p1Name = p1 ? tournamentDisplayName(p1) : teamNameMap.get(match.team1_id || match.player1_profile_id) || "Player 1";
    const p2Name = p2 ? tournamentDisplayName(p2) : teamNameMap.get(match.team2_id || match.player2_profile_id) || "Player 2";
    const winnerId = match.winner_enrolled_id || match.winner_team_id || match.winner_profile_id;
    const p1Winner = winnerId && [p1?.id, p1?.team_id, p1?.profile_id, match.team1_id, match.player1_profile_id].includes(winnerId);
    const p2Winner = winnerId && [p2?.id, p2?.team_id, p2?.profile_id, match.team2_id, match.player2_profile_id].includes(winnerId);
    const bracket = formatMatchBracketLabel(match);
    return (
      <div className="admin-match-card" key={match.id || match.tournament_match_id}>
        <div className="admin-match-head">
          <strong>{String(bracket).replace(/_/g, " ")}</strong>
          <span>Round {match.fixture_round || match.round_number || match.stage || 1}</span>
        </div>
        <div className="admin-match-body">
          <div className="admin-match-player">
            <AdminAvatar src={participantImage(p1)} name={p1Name} className={p1Winner ? "winner" : ""} />
            <strong>{p1Name}</strong>
          </div>
          <div className="admin-match-score">{match.participant1_score ?? 0}<span>-</span>{match.participant2_score ?? 0}</div>
          <div className="admin-match-player">
            <AdminAvatar src={participantImage(p2)} name={p2Name} className={p2Winner ? "winner" : ""} />
            <strong>{p2Name}</strong>
          </div>
        </div>
      </div>
    );
  };
  const renderScoreMatchCard = (match, index) => {
    const p1 = getMatchParticipant(match, 1);
    const p2 = getMatchParticipant(match, 2);
    const p1Name = p1 ? tournamentDisplayName(p1) : "Player 1";
    const p2Name = p2 ? tournamentDisplayName(p2) : "Player 2";
    const draft = scoreDrafts[match.id] || { a: "", b: "" };
    const saving = matchSavingId === match.id;
    const disabled = !canEdit || saving;
    const setDraft = (side, value) => {
      setScoreDrafts((current) => ({
        ...current,
        [match.id]: {
          ...(current[match.id] || { a: "", b: "" }),
          [side]: value.replace(/[^\d-]/g, "").slice(0, 4),
        },
      }));
    };
    const canSave = draft.a !== "" && draft.b !== "" && !disabled;
    return (
      <div className="admin-score-card" key={match.id}>
        <div className="admin-match-head">
          <strong>{getPendingMatchCardLabel(match)}</strong>
          <span>Game {match.match_number || index + 1}</span>
        </div>
        <div className="admin-score-body">
          <div className="admin-score-player">
            <AdminAvatar src={participantImage(p1)} name={p1Name} />
            <strong>{p1Name}</strong>
            {tournamentUsernameLabel(p1) && <span>{tournamentUsernameLabel(p1)}</span>}
            <label className="admin-score-label" htmlFor={`score-${match.id}-a`}>Score</label>
            <input
              id={`score-${match.id}-a`}
              className="admin-score-input"
              value={draft.a}
              onChange={(event) => setDraft("a", event.target.value)}
              inputMode="numeric"
              disabled={disabled}
            />
          </div>
          <div className="admin-score-vs">VS</div>
          <div className="admin-score-player">
            <AdminAvatar src={participantImage(p2)} name={p2Name} />
            <strong>{p2Name}</strong>
            {tournamentUsernameLabel(p2) && <span>{tournamentUsernameLabel(p2)}</span>}
            <label className="admin-score-label" htmlFor={`score-${match.id}-b`}>Score</label>
            <input
              id={`score-${match.id}-b`}
              className="admin-score-input"
              value={draft.b}
              onChange={(event) => setDraft("b", event.target.value)}
              inputMode="numeric"
              disabled={disabled}
            />
          </div>
        </div>
        <button
          type="button"
          className="admin-primary-btn admin-score-save"
          disabled={!canSave}
          onClick={async () => {
            const saved = await onSubmitMatch(match, draft.a, draft.b);
            if (saved) setScoreDrafts((current) => ({ ...current, [match.id]: { a: "", b: "" } }));
          }}
        >
          {saving ? "Saving..." : "Save Result"}
        </button>
        {!canEdit && <div className="admin-muted" style={{ marginTop: 8 }}>Sign in as creator/admin to save game results.</div>}
      </div>
    );
  };
  const renderPendingMatchSection = (title, matches, startIndex = 0) => {
    if (!matches.length) return null;
    return (
      <>
        <div className="admin-section-title"><span>{title}</span><span>{matches.length}</span></div>
        <div className="admin-card-list">{matches.map((match, index) => renderScoreMatchCard(match, startIndex + index))}</div>
      </>
    );
  };
  const addToQueue = async () => {
    if (!queueTeamA || !queueTeamB) return;
    if (queueTeamA.id === queueTeamB.id) {
      showToast("Pick two different teams", "error");
      return;
    }
    if (!canEdit) {
      showToast("Creator/admin access is required to manage the queue", "error");
      return;
    }
    const mode = String(tournament?.game_mode || "").toLowerCase();
    const queuedMatch = computedNextMatches.find((match) => {
      const a = match.participant1_enrolled_id;
      const b = match.participant2_enrolled_id;
      return (a === queueTeamA.id && b === queueTeamB.id) || (a === queueTeamB.id && b === queueTeamA.id);
    });
    if (mode === "point_based" && !queuedMatch) {
      showToast("That matchup is not currently playable", "error");
      return;
    }
    if (mode === "knockout" && !queuedMatch) {
      showToast("Pick one of the current next-game matchups", "error");
      return;
    }
    setAddingToQueue(true);
    try {
      const queueStage = Number(queuedMatch?.metadata?.stage ?? 1);
      const queueRound = Number(queuedMatch?.metadata?.bracket_round ?? queuedMatch?.round_number ?? queueStage);
      const { data: engineMatch, error: engineMatchError } = await supabase
        .from("tournament_matches")
        .insert({
          tournament_id: tournament.id,
          round_number: queueRound,
          match_number: Math.max(1, Math.floor(Date.now() % 2147483647)),
          participant1_enrolled_id: queueTeamA.id,
          participant2_enrolled_id: queueTeamB.id,
          status: "ready",
          source: "dashboard_queue",
          metadata: {
            ...(queuedMatch?.metadata || {}),
            format_key: pageFormatKey,
            simulated: !!queueTeamA.is_simulated || !!queueTeamB.is_simulated,
            bracket: queuedMatch?.metadata?.bracket ?? (mode === "knockout" ? "manual_queue" : "point"),
            bracket_round: queueRound,
            stage: queueStage,
          },
        })
        .select("id")
        .single();
      if (engineMatchError) throw engineMatchError;
      const buildQueueRow = (team, opponent) => ({
        profile_id: isUuidValue(team.profile_id) ? team.profile_id : null,
        team_id: null,
        sport_id: tournament?.sport ?? "Football",
        mode1: isTeamTournamentFormat(tournament) ? "team" : "1v1",
        mode2: "tournament",
        trophies: 500,
        rating: 30,
        arrived: true,
        ready: true,
        forfeited: false,
        game_duration: tournament?.game_duration ?? 40,
        score_limit: tournament?.score_limit ?? 0,
        tournament_id: tournament.id,
        name: tournamentDisplayName(team),
        tournament_match_id: engineMatch?.id ?? null,
        team_image: participantImage(team) || null,
        team_type: isTeamTournamentFormat(tournament) ? "group" : null,
        members: team.members ?? null,
        match_initiator_id: opponent.team_id ?? opponent.profile_id ?? opponent.id ?? null,
        match_initiator_username: tournamentDisplayName(opponent),
        match_initiator_profile_img: participantImage(opponent) || null,
      });
      const [{ error: queueErrorA }, { error: queueErrorB }] = await Promise.all([
        supabase.from("matchmaking_queue").insert(buildQueueRow(queueTeamA, queueTeamB)),
        supabase.from("matchmaking_queue").insert(buildQueueRow(queueTeamB, queueTeamA)),
      ]);
      if (queueErrorA) throw queueErrorA;
      if (queueErrorB) throw queueErrorB;
      setQueueTeamA(null);
      setQueueTeamB(null);
      await onRefresh();
      showToast("Both teams added to the queue");
    } catch (err) {
      showToast(err.message || "Failed to add to queue", "error");
    } finally {
      setAddingToQueue(false);
    }
  };
  const clearQueue = async () => {
    if (!canEdit || !tournament?.id) return;
    await supabase.from("matchmaking_queue").delete().eq("tournament_id", tournament.id);
    await onRefresh();
    showToast("Queue cleared");
  };
  const deleteQueueRow = async (row) => {
    if (!canEdit || !row?.id) return;
    await supabase.from("matchmaking_queue").delete().eq("id", row.id);
    await onRefresh();
    showToast("Queue row removed");
  };
  const persistRacePlacements = async (nextPlacements, successMessage) => {
    if (!canEdit || !isRaceTournament || !tournament?.id) return;
    const sortedPlacements = [...nextPlacements].sort((a, b) => a.place - b.place);
    const winner = sortedPlacements.find((placement) => placement.place === 1) || null;
    const nextConfig = {
      ...parseConfigObject(tournament?.format_config),
      event_kind: FITNESS_RACE_FORMAT_KEY,
      race_type: FITNESS_RACE_GAME_TYPE,
      manual_winner: true,
      race_placements: sortedPlacements,
    };
    if (winner) {
      nextConfig.race_winner_enrolled_id = winner.enrolled_id || null;
      nextConfig.race_winner_profile_id = winner.profile_id || null;
      nextConfig.race_winner_name = winner.name || "";
      nextConfig.race_winner_selected_at = winner.selected_at || new Date().toISOString();
      nextConfig.race_winner_selected_by = winner.selected_by || userId || null;
    } else {
      delete nextConfig.race_winner_enrolled_id;
      delete nextConfig.race_winner_profile_id;
      delete nextConfig.race_winner_name;
      delete nextConfig.race_winner_selected_at;
      delete nextConfig.race_winner_selected_by;
    }

    const { error } = await supabase
      .from("tournaments")
      .update({
        game_type: FITNESS_RACE_GAME_TYPE,
        game_mode: "race",
        type: "individual",
        format_key: FITNESS_RACE_FORMAT_KEY,
        format_config: nextConfig,
      })
      .eq("id", tournament.id);
    if (error) throw error;
    await onRefresh();
    showToast(successMessage);
  };
  const setRacePlacement = async (row, place) => {
    if (!row || !place) return;
    const savingKey = `${row.id || getParticipantId(row)}-${place}`;
    setRaceSavingId(savingKey);
    try {
      const rowIds = new Set(participantIdentityCandidates(row));
      const entry = {
        place,
        enrolled_id: String(row.id || getParticipantId(row)),
        profile_id: row.profile_id ?? row.profileId ?? null,
        team_id: row.team_id ?? row.teamId ?? null,
        name: tournamentDisplayName(row),
        selected_at: new Date().toISOString(),
        selected_by: userId || null,
      };
      const nextPlacements = racePlacements
        .filter((placement) => placement.place !== place)
        .filter((placement) => !racePlacementIdentityCandidates(placement).some((id) => rowIds.has(id)));
      nextPlacements.push(entry);
      await persistRacePlacements(nextPlacements, `${entry.name} set as ${getOrdinal(place)}`);
    } catch (err) {
      showToast(err.message || "Could not save race result", "error");
    } finally {
      setRaceSavingId(null);
    }
  };
  const clearRacePlacement = async (row) => {
    if (!row) return;
    const savingKey = `${row.id || getParticipantId(row)}-clear`;
    setRaceSavingId(savingKey);
    try {
      const rowIds = new Set(participantIdentityCandidates(row));
      const nextPlacements = racePlacements.filter((placement) =>
        !racePlacementIdentityCandidates(placement).some((id) => rowIds.has(id))
      );
      await persistRacePlacements(nextPlacements, `${tournamentDisplayName(row)} race place cleared`);
    } catch (err) {
      showToast(err.message || "Could not clear race place", "error");
    } finally {
      setRaceSavingId(null);
    }
  };
  const clearRaceResults = async () => {
    if (!canEdit || racePlacements.length === 0) return;
    setRaceSavingId("reset");
    try {
      await persistRacePlacements([], "Race results cleared");
    } catch (err) {
      showToast(err.message || "Could not clear race results", "error");
    } finally {
      setRaceSavingId(null);
    }
  };
  const renderQueuePicker = (side) => {
    const selected = side === "A" ? queueTeamA : queueTeamB;
    const other = side === "A" ? queueTeamB : queueTeamA;
    const setSelected = side === "A" ? setQueueTeamA : setQueueTeamB;
    const accent = side === "A" ? "#0C69C8" : "#22A36F";
    return (
      <div className="admin-queue-side">
        <div className="admin-queue-side-head">
          <span style={{ background: accent }} />
          <strong>{isTeamTournamentFormat(tournament) ? "Team" : "Player"} {side}</strong>
          {selected && <em style={{ color: accent }}>{tournamentDisplayName(selected)}</em>}
        </div>
        <div className="admin-queue-chip-row">
          {stableSort(competitionRows).map((row) => {
            const isSelected = selected?.id === row.id;
            const isOther = other?.id === row.id;
            const disabled = isOther;
            return (
              <button
                type="button"
                key={row.id}
                className="admin-queue-chip"
                data-selected={isSelected ? "true" : "false"}
                disabled={disabled}
                style={isSelected ? { borderColor: accent, background: `${accent}18`, color: accent } : undefined}
                onClick={() => !disabled && setSelected(isSelected ? null : row)}
              >
                <AdminAvatar src={participantImage(row)} name={tournamentDisplayName(row)} />
                <span>{tournamentDisplayName(row)}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  const renderRacePlacementControl = (row) => {
    const placement = findRacePlacementForParticipant(racePlacements, row);
    const rowKey = row.id || getParticipantId(row);
    const saving = raceSavingId && String(raceSavingId).startsWith(String(rowKey));
    return (
      <div className="admin-player-actions-row" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
        <label style={{ display: "grid", gap: 5, minWidth: 118 }}>
          <span style={{ color: "#64748B", fontSize: 10, fontWeight: 900, textTransform: "uppercase" }}>Race place</span>
          <select
            value={placement?.place || ""}
            onChange={(event) => {
              const place = Number(event.target.value);
              if (place) setRacePlacement(row, place);
            }}
            disabled={!canEdit || !!saving}
            aria-label={`Set race place for ${tournamentDisplayName(row)}`}
            style={{ minHeight: 36, borderRadius: 10, border: "1px solid #D7E1EE", background: "#FFFFFF", color: "#0F172A", fontWeight: 900, padding: "0 10px" }}
          >
            <option value="">Place</option>
            {racePlaceOptions.map((place) => (
              <option key={place} value={place}>{getOrdinal(place)}</option>
            ))}
          </select>
        </label>
        {placement && (
          <button
            type="button"
            className="admin-secondary-btn admin-danger-btn"
            onClick={() => clearRacePlacement(row)}
            disabled={!canEdit || !!saving}
            aria-label={`Clear race place for ${tournamentDisplayName(row)}`}
          >
            {saving ? "Saving..." : "Clear"}
          </button>
        )}
      </div>
    );
  };
  const renderRaceOverview = () => (
    <>
      <Hero kicker={raceWinnerPlacement ? "Result live" : "Fitness race"} title={statusTitle} body={statusBody} />
      <Stats items={statItems} />
      {raceWinnerPlacement && (
        <div className="admin-blue-card" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <AdminAvatar src={raceWinner ? participantImage(raceWinner) : null} name={raceWinnerName} />
          <div className="admin-person-main">
            <strong>{raceWinnerName}</strong>
            <span>Winner</span>
          </div>
          <div className="admin-role-pill">1st</div>
        </div>
      )}
      <div className="admin-section-title"><span>Participants</span><span>{competitionRows.length}</span></div>
      <div className="admin-card-list">
        {competitionRows.length === 0 ? (
          <div className="admin-empty-card"><FontAwesomeIcon icon={faUser} /><strong>No racers yet</strong><span>Participants will appear here once they join.</span></div>
        ) : stableSort(competitionRows).map((row, index) => {
          const placement = findRacePlacementForParticipant(racePlacements, row);
          return (
            <ParticipantCard
              key={row.id || index}
              row={row}
              meta={placement ? getOrdinal(placement.place) : "No place recorded"}
            />
          );
        })}
      </div>
    </>
  );
  const renderRaceResult = () => (
    <>
      <Hero kicker="Race result" title={raceWinnerPlacement ? `${raceWinnerName} is 1st` : "Record race places"} body="Assign as many places as you need. First place powers the winner display; other places show beside each participant in the public race tree." />
      <Stats items={statItems} />
      <div className="admin-section-title">
        <span>Race places</span>
        <span>{racePlacements.length}/{raceMaxPlaces}</span>
      </div>
      <div className="admin-card-list">
        {competitionRows.length === 0 ? (
          <div className="admin-empty-card"><FontAwesomeIcon icon={faUser} /><strong>No racers yet</strong><span>Race places can be added once participants join.</span></div>
        ) : stableSort(competitionRows).map((row, index) => {
          const placement = findRacePlacementForParticipant(racePlacements, row);
          return (
            <ParticipantCard
              key={row.id || index}
              row={row}
              meta={placement ? getOrdinal(placement.place) : "No place recorded"}
              rightSlot={renderRacePlacementControl(row)}
            />
          );
        })}
      </div>
      {!canEdit && <div className="admin-muted" style={{ marginTop: 10 }}>Sign in as creator/admin to record race results.</div>}
      {racePlacements.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
          <button type="button" className="admin-secondary-btn admin-danger-btn" onClick={clearRaceResults} disabled={!canEdit || raceSavingId === "reset"}>
            {raceSavingId === "reset" ? "Clearing..." : "Clear race results"}
          </button>
        </div>
      )}
    </>
  );
  const renderOverview = () => (
    <>
      <Hero kicker={tournament?.tournament_finished ? "Finished" : "Live admin"} title={statusTitle} body={`${formatLabel}. ${statusBody}`} />
      <Stats items={statItems} />
      <div className="admin-section-title"><span>{hasSimulationRows ? "Simulation Standings" : "Standings"}</span><span>{competitionRows.length}</span></div>
      <div className="admin-card-list">
        {competitionRows.length === 0 ? <div className="admin-empty-card"><FontAwesomeIcon icon={faUser} /><strong>No players yet</strong><span>Create simulated entrants in Sim to test the format, or wait for participants to join.</span></div> : stableSort(competitionRows).map((row, index) => (
          <div className="admin-player-control-card" key={row.id || index}>
            <ParticipantCard row={row} index={index} attachedFooter />
            {renderPlayerControls(row)}
          </div>
        ))}
      </div>
    </>
  );
  const renderFormat = () => (
    <>
      <Hero kicker="Format" title={formatLabel} body="Choose the tournament format before scoring starts. Changing format resets scores, progression, queue entries, and bracket progress so the new rules apply cleanly." />
      <div className="admin-format-summary">
        <div className="admin-format-icon" aria-hidden="true"><FontAwesomeIcon icon={faTrophy} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="admin-format-kicker">Format</div>
          <div className="admin-format-current">{formatLabel}</div>
          <div className="admin-format-help">Public bracket, progression, and scoring rules follow this tournament format.</div>
          <div className="admin-format-help">{competitionRows.length} player slots · max {tournamentPackage.maxParticipants}</div>
        </div>
      </div>
      <div className="admin-format-list">
        {allowedFormats.map((fmt) => {
          const format = TOURNAMENT_FORMATS[fmt] || { label: formatTournamentFormatLabel(fmt) };
          const active = fmt === pageFormatKey;
          const rule = getFormatParticipantRule(fmt, competitionRows.length, tournamentPackage.maxParticipants);
          return (
            <button
              key={fmt}
              type="button"
              className="admin-format-option"
              data-selected={active ? "true" : "false"}
              aria-label={`${active ? "Current format" : "Switch to"} ${format.label}`}
              aria-pressed={active}
              disabled={!canEdit || formatSaving || active || !rule.valid}
              onClick={() => onChangeFormat(fmt)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="admin-format-title">{format.label}</div>
                <div className="admin-format-meta">{rule.valid ? rule.helper : rule.error}</div>
              </div>
              <div className="admin-format-state">{active ? "✓" : "↔"}</div>
            </button>
          );
        })}
      </div>
      <Stats items={[["Type", formatGameTypeLabel(tournament?.game_type || tournament?.gameType)], ["Mode", String(tournament?.game_mode || "Tournament").replace(/_/g, " ")], ["Capacity", hasSimulationRows ? competitionRows.length : (tournament?.spots_available || competitionRows.length || 0)], ["Live", liveDashboardEnabled ? "On" : "Off"]]} />
      {!canManageWebDashboard && <div className="admin-muted" style={{ marginTop: 10 }}>Sign in as creator/admin to change tournament settings.</div>}
    </>
  );
  const renderMatch = () => {
    const visiblePending = computedNextMatches.slice(0, 8);
    const winnersMatchups = visiblePending.filter((match) => match.metadata?.bracket !== "redemption" && match.metadata?.bracket !== "point");
    const redemptionMatchups = visiblePending.filter((match) => match.metadata?.bracket === "redemption");
    const pointMatchups = visiblePending.filter((match) => match.metadata?.bracket === "point");
    const pointSections = Array.from(pointMatchups.reduce((groups, match) => {
      const title = formatMatchBracketLabel(match);
      groups.set(title, [...(groups.get(title) || []), match]);
      return groups;
    }, new Map()).entries());
    let nextStartIndex = 0;
    const pendingSections = [
      winnersMatchups.length > 0 ? ["Winners bracket", winnersMatchups] : null,
      redemptionMatchups.length > 0 ? ["Redemption bracket", redemptionMatchups] : null,
      ...pointSections,
    ].filter(Boolean).map(([title, matches]) => {
      const startIndex = nextStartIndex;
      nextStartIndex += matches.length;
      return { title, matches, startIndex };
    });
    return (
      <>
        <Hero kicker="Match" title="Score and review games" body="Tap a game from Next Games, enter both scores, then save the result." />
        {visiblePending.length === 0 ? (
          <>
            <div className="admin-section-title"><span>Next games</span><span>0</span></div>
            <div className="admin-card-list"><div className="admin-empty-card"><FontAwesomeIcon icon={faTrophy} /><strong>No next games yet</strong><span>Add at least two active entrants or reset completed results to create another playable game.</span></div></div>
          </>
        ) : (
          <>
            {pendingSections.map(({ title, matches, startIndex }) => (
              <Fragment key={title}>{renderPendingMatchSection(title, matches, startIndex)}</Fragment>
            ))}
          </>
        )}
        <div className="admin-section-title"><span>Previous matches</span><span>{completedMatches.length}</span></div>
        <div className="admin-card-list">{completedMatches.length === 0 ? <div className="admin-empty-card"><FontAwesomeIcon icon={faPen} /><strong>No scores recorded</strong><span>Saved match results will appear here with both players and the final score.</span></div> : completedMatches.slice().reverse().map((match) => renderPreviousMatchCard(match))}</div>
      </>
    );
  };
  const renderQueue = () => (
    <>
      <Hero kicker="Queue" title={`${queueRows.length} waiting row${queueRows.length === 1 ? "" : "s"}`} body="Use the queue when two players should be matched but the game has not started." />
      <div className="admin-section-title"><span>Add to Queue</span></div>
      <div className="admin-queue-card">
        {renderQueuePicker("A")}
        {renderQueuePicker("B")}
        <button type="button" className="admin-primary-btn admin-queue-add" disabled={!queueTeamA || !queueTeamB || addingToQueue || !canEdit} onClick={addToQueue}>
          {addingToQueue ? "Adding..." : "Add Both to Queue"}
        </button>
        {!canEdit && <div className="admin-muted" style={{ marginTop: 8 }}>Sign in as creator/admin to manage the queue.</div>}
      </div>
      <div className="admin-section-title"><span>Queue</span><span>{queueRows.length}</span>{queueRows.length > 0 && <button type="button" className="admin-clear-link" onClick={clearQueue} disabled={!canEdit}>Clear All</button>}</div>
      <div className="admin-card-list">{queueRows.length === 0 ? <div className="admin-empty-card"><FontAwesomeIcon icon={faTrophy} /><strong>Queue is empty</strong><span>No waiting rows need action right now.</span></div> : queueRows.map((row) => {
        const participant = enrolledById.get(row.profile_id) || enrolledById.get(row.team_id) || enrolledById.get(row.participant_enrolled_id) || row;
        return <ParticipantCard key={row.id} row={participant} meta={`vs ${row.match_initiator_username ?? "?"} · ${row.arrived ? "Arrived" : "Waiting"}`} rightSlot={<button type="button" className="admin-row-delete" onClick={() => deleteQueueRow(row)} disabled={!canEdit} aria-label="Remove queue row">×</button>} />;
      })}</div>
    </>
  );
  const renderSim = () => (
    <>
      <Hero kicker="Sim" title="Simulation setup" body="Create simulated entrants to test format, score flow, queue behavior, and the bracket tree before running the event." />
      <div className="admin-sim-grid"><div className="admin-white-card"><div className="admin-stat"><strong>{simulatedRows.length}</strong><span>Simulated</span></div></div><div className="admin-white-card"><div className="admin-stat"><strong>{enrolled.length - simulatedRows.length}</strong><span>Real entrants</span></div></div></div>
      <label className="admin-section-title" htmlFor="admin-sim-count"><span>Entrants</span></label>
      <input id="admin-sim-count" className="admin-sim-input" value={simulationCount} onChange={(event) => setSimulationCount(event.target.value.replace(/\D/g, "").slice(0, 2))} inputMode="numeric" />
      <div className="admin-quick-row">{[4, 8, 16].map((count) => <button type="button" className="admin-secondary-btn" key={count} onClick={() => setSimulationCount(String(count))}>{count}</button>)}</div>
      <div className="admin-sim-actions">
        <button type="button" className="admin-primary-btn" onClick={onGenerateSimulation} disabled={!canEdit || simSaving}>{simSaving ? "Generating..." : "Generate Test Entrants"}</button>
        {simulatedRows.length > 0 && <button type="button" className="admin-secondary-btn admin-danger-btn" onClick={onClearSimulation} disabled={!canEdit || simSaving}>Clear Simulation</button>}
      </div>
      {!canEdit && <div className="admin-muted" style={{ marginTop: 10 }}>Sign in as creator/admin to generate or clear simulations.</div>}
      <div className="admin-section-title"><span>Test entrants</span><span>{simulatedRows.length}</span></div>
      <div className="admin-card-list">{simulatedRows.length === 0 ? <div className="admin-empty-card"><FontAwesomeIcon icon={faUser} /><strong>No simulated entrants yet</strong><span>Generate test entrants to preview the tournament flow.</span></div> : simulatedRows.map((row) => <ParticipantCard key={row.id} row={row} />)}</div>
    </>
  );
  const renderAdmins = () => (
    <>
      <Hero kicker="Admins" title="Tournament facilitators" body={isRaceTournament ? "Facilitators can open admin controls, refresh participants, and record race results." : "Facilitators can open admin controls, score games, manage queues, run simulations, and update tournament operations."} />
      <div className="admin-card-list">
        <PersonCard profile={creatorProfile} roleLabel="Owner" meta="Full owner access" />
        <div className="admin-white-card"><strong>Your role</strong><div className="admin-muted">{role}</div></div>
        <div className="admin-section-title" style={{ marginBottom: 0 }}><span>Facilitators</span><span>{adminRows.length}</span></div>
        {adminRows.length === 0 ? <div className="admin-empty-card"><FontAwesomeIcon icon={faUser} /><strong>No facilitators yet</strong><span>Add a trusted user when someone else needs to help run this event.</span></div> : adminRows.map((row) => <PersonCard key={row.id || row.profile_id} profile={row.profile} roleLabel={row.role || "admin"} meta="Can manage tournament operations" />)}
      </div>
    </>
  );
  return (
    <section id="admin" className="tournament-admin-console" aria-label="Tournament admin dashboard">
      <div className="tournament-admin-head"><button type="button" className="tournament-admin-back" onClick={() => window.history.replaceState(null, "", window.location.pathname)} aria-label="Close tournament admin dashboard">&lt;</button><div className="tournament-admin-title"><h2>{tournament?.tournament_name || "Dashboard"}</h2><p>{formatLabel}</p></div><div className="tournament-admin-actions"><button type="button" className="tournament-admin-action danger" onClick={onReset} disabled={!canEdit || resetting}>Reset</button><button type="button" className="tournament-admin-action" onClick={onRefresh}>Refresh</button></div></div>
      <div className="tournament-admin-tabs" role="tablist" aria-label="Tournament admin sections" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>{tabs.map(([key, label]) => <button key={key} type="button" role="tab" className="tournament-admin-tab" aria-selected={selectedTab === key} onClick={() => setActiveTab(key)}>{label}</button>)}</div>
      <div className="tournament-admin-body">
        {isRaceTournament ? (
          <>
            {selectedTab === "overview" && renderRaceOverview()}
            {selectedTab === "result" && renderRaceResult()}
            {selectedTab === "admins" && renderAdmins()}
          </>
        ) : (
          <>
            {selectedTab === "overview" && renderOverview()}
            {selectedTab === "format" && renderFormat()}
            {selectedTab === "match" && renderMatch()}
            {selectedTab === "queue" && renderQueue()}
            {selectedTab === "simulate" && renderSim()}
            {selectedTab === "admins" && renderAdmins()}
          </>
        )}
      </div>
    </section>
  );
}

/* ─── MATCH RESULTS SECTION ──────────────────────────────────────────── */
function MatchResultsSection({ matchResults, teamNameMap }) {
  const [activeTab, setActiveTab] = useState("all");

  const grouped = useMemo(() => {
    const group1 = matchResults.filter((r) => r.stage === 1);
    const group2 = matchResults.filter((r) => r.stage === 2);
    return { group1, group2 };
  }, [matchResults]);

  const displayResults = useMemo(() => {
    if (activeTab === "group") return grouped.group1;
    if (activeTab === "ko")    return grouped.group2;
    return matchResults;
  }, [activeTab, matchResults, grouped]);

  const getTeamName = (teamId) => teamNameMap[teamId] ?? teamId?.slice(0, 8) ?? "—";

  const sourceBadge = (source) => {
    const map = {
      scoresheet_a:  { label: "Group A",  color: C.gold },
      scoresheet_b:  { label: "Group B",  color: C.green },
      scoresheet_ko: { label: "Knockout", color: "#a78bfa" },
      matchmaking:   { label: "Live",     color: C.blue },
      dashboard:     { label: "Admin",    color: C.w35 },
    };
    return map[source] ?? { label: source ?? "—", color: C.w35 };
  };

  if (matchResults.length === 0) {
    return (
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 18, padding: "48px 32px", textAlign: "center",
      }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, margin: "0 auto 14px", background: C.goldDim, border: `1px solid ${C.goldMed}`, color: C.gold, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900 }}>
          RS
        </div>
        <div style={{ fontSize: 15, color: C.w55, fontWeight: 600, marginBottom: 6 }}>
          No match results yet
        </div>
        <div style={{ fontSize: 13, color: C.w35 }}>
          Save group or knockout scores above to populate this section.
        </div>
      </div>
    );
  }

  return (
    <div className="anim-3" style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 18, overflow: "hidden",
    }}>
      {/* Header */}
      <div className="match-results-header" style={{
        padding: "18px 24px", borderBottom: `1px solid ${C.border}`,
        background: "rgba(0,0,0,0.25)",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: C.white, marginBottom: 4 }}>
            Match Results
          </div>
          <div style={{ fontSize: 11, color: C.w35, fontWeight: 600 }}>
            {matchResults.length} result{matchResults.length !== 1 ? "s" : ""} recorded
          </div>
        </div>
        {/* Tab filters */}
        <div className="match-tab-row" style={{ display: "flex", gap: 6 }}>
          {[
            { key: "all",   label: `All (${matchResults.length})` },
            { key: "group", label: `Group (${grouped.group1.length})` },
            { key: "ko",    label: `KO (${grouped.group2.length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                background: activeTab === t.key ? C.goldMed : C.w08,
                color: activeTab === t.key ? "#000" : C.w55,
                fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                transition: "all 0.15s",
                whiteSpace: "nowrap",
                touchAction: "manipulation",
              }}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {/* Result rows */}
      <div className="match-results-body" style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
        {displayResults.map((r) => {
          const nameA    = getTeamName(r.team1_id ?? r.player1_profile_id);
          const nameB    = getTeamName(r.team2_id ?? r.player2_profile_id);
          const wIdA     = r.team1_id ?? r.player1_profile_id;
          const wIdB     = r.team2_id ?? r.player2_profile_id;
          const winnerId = r.winner_team_id ?? r.winner_profile_id;
          const aWon     = !r.is_draw && winnerId === wIdA;
          const bWon     = !r.is_draw && winnerId === wIdB;
          const badge    = sourceBadge(r.match_source);
          const stageLabel = r.stage === 1 ? "Group" : r.stage === 2 ? "KO" : `S${r.stage}`;

          return (
            <div
              key={r.id}
              className="result-row"
              style={{
                borderRadius: 12,
                background: "rgba(0,0,0,0.2)",
                border: `1px solid ${C.border}`,
                overflow: "hidden",
              }}
            >
              {/* Main row */}
              <div className="result-row-inner" style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
              }}>
                {/* Team A */}
                <div style={{ flex: "1 1 80px", minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
                  <TeamAvatar src={null} name={nameA} size={24} />
                  <span style={{
                    fontSize: 13, fontWeight: aWon ? 800 : 600,
                    color: aWon ? C.gold : r.is_draw ? C.w55 : C.w80,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{nameA}</span>
                  {aWon && <span style={{ fontSize: 11, flexShrink: 0 }}>🏆</span>}
                </div>

                {/* Score */}
                <div className="result-score-box" style={{
                  display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
                  padding: "6px 12px", borderRadius: 10,
                  background: C.navy2, border: `1px solid ${C.border2}`,
                }}>
                  <span style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 16, fontWeight: 700,
                    color: aWon ? C.gold : r.is_draw ? C.w55 : C.w80,
                    minWidth: 18, textAlign: "center",
                  }}>{r.participant1_score ?? "–"}</span>
                  <span style={{ color: C.w35, fontSize: 12, fontWeight: 700 }}>–</span>
                  <span style={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: 16, fontWeight: 700,
                    color: bWon ? C.gold : r.is_draw ? C.w55 : C.w80,
                    minWidth: 18, textAlign: "center",
                  }}>{r.participant2_score ?? "–"}</span>
                </div>

                {/* Team B */}
                <div style={{ flex: "1 1 80px", minWidth: 0, display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
                  {bWon && <span style={{ fontSize: 11, flexShrink: 0 }}>🏆</span>}
                  <span style={{
                    fontSize: 13, fontWeight: bWon ? 800 : 600,
                    color: bWon ? C.gold : C.w80,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    textAlign: "right",
                  }}>{nameB}</span>
                  <TeamAvatar src={null} name={nameB} size={24} />
                </div>
              </div>

              {/* Badges row - always below on mobile */}
              <div className="result-badges" style={{
                display: "flex", gap: 6, flexShrink: 0,
                padding: "0 16px 12px",
              }}>
                <span style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                  color: badge.color,
                  background: `${badge.color}18`,
                  borderRadius: 6, padding: "3px 8px",
                  border: `1px solid ${badge.color}33`,
                  whiteSpace: "nowrap",
                }}>{badge.label}</span>
                <span style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                  color: r.stage === 2 ? "#a78bfa" : C.gold,
                  background: r.stage === 2 ? "rgba(167,139,250,0.15)" : C.goldDim,
                  borderRadius: 6, padding: "3px 8px",
                  border: `1px solid ${r.stage === 2 ? "rgba(167,139,250,0.35)" : C.goldMed}`,
                  whiteSpace: "nowrap",
                }}>{stageLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── GROUP TABLE ────────────────────────────────────────────────────── */
function GroupTable({ label, group, accent, animClass, onSave, saving, initialScores, differenceLabel = "PD" }) {
  const sortedGroup = useMemo(() => stableSort(group), [group]);
  const prevInitialRef = useRef(null);

  const buildEmptyScores = useCallback(() => {
    const map = {};
    for (let i = 0; i < sortedGroup.length; i++)
      for (let j = i + 1; j < sortedGroup.length; j++)
        map[pairKey(sortedGroup[i].id, sortedGroup[j].id)] = { a: "", b: "" };
    return map;
  }, [sortedGroup]);

  const [scores, setScores] = useState(buildEmptyScores);

  useEffect(() => {
    if (!initialScores || Object.keys(initialScores).length === 0) return;
    const serialized = JSON.stringify(initialScores);
    if (prevInitialRef.current === serialized) return;
    prevInitialRef.current = serialized;
    setScores((prev) => {
      const next = { ...buildEmptyScores() };
      Object.entries(initialScores).forEach(([k, v]) => {
        if (v) next[k] = v;
      });
      return next;
    });
  }, [initialScores, buildEmptyScores]);

  const pairs = useMemo(() => {
    const result = [];
    for (let i = 0; i < sortedGroup.length; i++)
      for (let j = i + 1; j < sortedGroup.length; j++)
        result.push({ rowA: sortedGroup[i], rowB: sortedGroup[j] });
    return result;
  }, [sortedGroup]);

  const liveStats = useMemo(() => {
    const computed = recomputeGroup(sortedGroup, scores);
    return sortedGroup
      .map((row, i) => {
        const c  = computed[i];
        const pts = c.pts, gd = c.gd, pl = c.played.length;
        const wins   = Math.floor(pts / 3);
        const draws  = pts % 3;
        const losses = Math.max(0, pl - wins - draws);
        return {
          id:    row.team_id ?? row.id,
          rowId: row.id,
          name:  row.team_name ?? "—",
          img:   row.team_image ?? row.profile_img ?? null,
          pts, gd, pl, wins, draws, losses,
        };
      })
      .sort((a, b) => b.pts - a.pts || b.gd - a.gd || a.name.localeCompare(b.name));
  }, [sortedGroup, scores]);

  const handleScore = (key, side, val) =>
    setScores((prev) => ({ ...prev, [key]: { ...prev[key], [side]: val } }));

  const handleSave = () => onSave(recomputeGroup(sortedGroup, scores), scores, sortedGroup);

  const filledCount = pairs.filter(({ rowA, rowB }) => {
    const sc = scores[pairKey(rowA.id, rowB.id)];
    return sc && sc.a !== "" && sc.b !== "";
  }).length;

  return (
    <div className={animClass} style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 18, overflow: "hidden",
    }}>
      {/* Header */}
      <div className="group-header" style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "18px 24px",
        background: `linear-gradient(135deg,${C.blue}18,transparent)`,
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: accent, color: "#000",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'Sora',sans-serif",
          fontSize: 22, fontWeight: 900, flexShrink: 0,
          boxShadow: `0 4px 12px ${accent}40`,
        }}>{label}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 19, fontWeight: 800, color: C.white, marginBottom: 4 }}>
            Group {label}
          </div>
          <div style={{ fontSize: 11, color: C.w35, fontWeight: 600, letterSpacing: 0.5 }}>
            Round Robin · Top 2 advance
          </div>
        </div>
        {saving && <Spinner size={16} />}
      </div>

      {/* Standings Table */}
      <div>
        {/* Column headers */}
        <div className="standing-header-grid" style={{
          display: "grid",
          gridTemplateColumns: "36px minmax(120px, 1fr) 32px 32px 32px 32px 44px 48px",
          padding: "10px 20px",
          borderBottom: `1px solid ${C.border}`,
          background: "rgba(0,0,0,0.15)",
        }}>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.w35, textAlign: "center" }}>#</div>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.w35 }}>Team</div>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.w35, textAlign: "center" }}>P</div>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.w35, textAlign: "center" }}>W</div>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.w35, textAlign: "center" }}>D</div>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.w35, textAlign: "center" }}>L</div>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.w35, textAlign: "center" }}>{differenceLabel}</div>
          <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: C.gold, textAlign: "center" }}>PTS</div>
        </div>

        {liveStats.map((s, idx) => {
          const advanced = idx < 2;
          const gdColor  = s.gd > 0 ? C.green : s.gd < 0 ? C.red : C.w55;
          return (
            <div key={s.id} className="team-row standing-grid" style={{
              display: "grid",
              gridTemplateColumns: "36px minmax(120px, 1fr) 32px 32px 32px 32px 44px 48px",
              padding: "14px 20px",
              borderBottom: idx < liveStats.length - 1 ? `1px solid ${C.border}` : "none",
              borderLeft: `4px solid ${advanced ? accent : "transparent"}`,
              background: advanced ? `${accent}08` : "transparent",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: idx === 0 ? C.gold : idx === 1 ? C.blue : C.w15,
                  color: idx <= 1 ? "#000" : C.w35,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 900, flexShrink: 0,
                }}>{idx + 1}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <TeamAvatar src={s.img} name={s.name} size={24} />
                <span style={{
                  fontSize: 13, fontWeight: 700, color: C.w80,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  lineHeight: 1.3,
                }}>{s.name}</span>
                {advanced && <span style={{ fontSize: 10, color: accent, fontWeight: 800, flexShrink: 0 }}>Advanced</span>}
              </div>
              <Cell>{s.pl}</Cell>
              <Cell>{s.wins}</Cell>
              <Cell>{s.draws}</Cell>
              <Cell>{s.losses}</Cell>
              <Cell color={gdColor} bold>{s.pl > 0 ? (s.gd > 0 ? `+${s.gd}` : s.gd) : "–"}</Cell>
              <Cell color={C.gold} bold>{s.pts}</Cell>
            </div>
          );
        })}
      </div>

      {/* Score Inputs */}
      <div className="group-score-section" style={{
        padding: "20px 24px",
        borderTop: `1px solid ${C.border}`,
        background: "rgba(0,0,0,0.25)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 16, flexWrap: "wrap", gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ color: C.gold, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: .8 }}>Tournament</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.8, textTransform: "uppercase", color: C.w35 }}>
                Match Scores
              </div>
              <div style={{ fontSize: 10, color: C.w55, fontWeight: 600 }}>
                {filledCount} of {pairs.length} matches entered
              </div>
            </div>
          </div>
          {/* Read-only indicator when no save access */}
          {!onSave && (
            <div style={{
              fontSize: 10, fontWeight: 700, color: C.w35,
              background: C.w08, borderRadius: 6, padding: "4px 10px",
              border: `1px solid ${C.border}`,
            }}>👁 View only</div>
          )}
        </div>

        {pairs.map(({ rowA, rowB }) => {
          const key  = pairKey(rowA.id, rowB.id);
          const sc   = scores[key] ?? { a: "", b: "" };
          const nameA = rowA.team_name ?? "?";
          const nameB = rowB.team_name ?? "?";
          const ga   = parseInt(sc.a), gb = parseInt(sc.b);
          const hasResult = sc.a !== "" && sc.b !== "" && !isNaN(ga) && !isNaN(gb);

          return (
            <div key={key} className="match-score-row" style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
              borderRadius: 12,
              marginBottom: 12,
              background: hasResult ? "rgba(12,105,200,0.08)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${hasResult ? C.goldMed : C.border}`,
              transition: "all 0.2s",
            }}>
              {/* Team A */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <TeamAvatar isTeam mode={rowA.image_display_mode} src={rowA.team_image ?? null} name={nameA} size={24} />
                <span style={{
                  fontSize: 13, fontWeight: 700,
                  color: hasResult && ga > gb ? C.gold : C.w80,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{nameA}</span>
              </div>

              {/* Score inputs */}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ScoreInput value={sc.a} onChange={(v) => handleScore(key, "a", v)} disabled={!onSave} />
                <span style={{ color: C.w35, fontWeight: 700, fontSize: 13 }}>–</span>
                <ScoreInput value={sc.b} onChange={(v) => handleScore(key, "b", v)} disabled={!onSave} />
              </div>

              {/* Team B */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end", minWidth: 0 }}>
                <span style={{
                  fontSize: 13, fontWeight: 700,
                  color: hasResult && gb > ga ? C.gold : C.w80,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  textAlign: "right",
                }}>{nameB}</span>
                <TeamAvatar isTeam mode={rowB.image_display_mode} src={rowB.team_image ?? null} name={nameB} size={24} />
              </div>
            </div>
          );
        })}

        {/* Save button — only shown when user has edit access */}
        {onSave && (
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={saving || filledCount === 0}
            style={{
              marginTop: 8, width: "100%", padding: "14px 20px",
              borderRadius: 12, border: "none",
              cursor: filledCount > 0 ? "pointer" : "not-allowed",
              background: filledCount > 0 ? `linear-gradient(135deg,${C.gold},${C.gold2})` : C.w08,
              color: filledCount > 0 ? "#000" : C.w35,
              fontFamily: "'Sora',sans-serif",
              fontSize: 15, fontWeight: 900, letterSpacing: 1.2,
              textTransform: "uppercase",
              opacity: filledCount > 0 ? 1 : 0.5,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              boxShadow: filledCount > 0 ? `0 4px 16px ${C.gold}40` : "none",
              transition: "all 0.2s",
              touchAction: "manipulation",
            }}
          >
            {saving
              ? <><Spinner size={16} color="#000" /> Saving…</>
              : <><span style={{ fontSize: 16 }}></span> Save Group {label} Results</>}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── KO STAGE ───────────────────────────────────────────────────────── */
function KOStage({ tournament, groupA, groupB, onSaveKO, koScores, setKoScores, savingKO }) {
  const statsA   = useMemo(() => deriveStats(groupA), [groupA]);
  const statsB   = useMemo(() => deriveStats(groupB), [groupB]);
  const finished = !!tournament?.tournament_finished;

  const finalA = statsA[0] ?? null;
  const finalB = statsB[0] ?? null;
  const thirdA = statsA[1] ?? null;
  const thirdB = statsB[1] ?? null;
  const fifthA = statsA[2] ?? null;
  const fifthB = statsB[2] ?? null;

  const getWinner = (key, teamA, teamB) => {
    const sc = koScores[key];
    if (!sc || sc.a === "" || sc.b === "") return null;
    const ga = parseInt(sc.a), gb = parseInt(sc.b);
    if (isNaN(ga) || isNaN(gb)) return null;
    if (ga > gb) return { name: teamA?.name, side: "a" };
    if (gb > ga) return { name: teamB?.name, side: "b" };
    return { name: "Draw", side: null };
  };

  const handleKO = (key, side, val) =>
    setKoScores((prev) => ({ ...prev, [key]: { ...prev[key], [side]: val } }));

  const standings = useMemo(() => {
    const resolve = (key, teamA, teamB) => {
      const sc = koScores[key];
      if (!sc || sc.a === "" || sc.b === "") return { winner: null, loser: null };
      const ga = parseInt(sc.a), gb = parseInt(sc.b);
      if (isNaN(ga) || isNaN(gb)) return { winner: null, loser: null };
      if (ga > gb) return { winner: teamA, loser: teamB };
      if (gb > ga) return { winner: teamB, loser: teamA };
      return { winner: null, loser: null };
    };
    const fin   = resolve("final", finalA, finalB);
    const third = resolve("third", thirdA, thirdB);
    const fifth = resolve("fifth", fifthA, fifthB);
    return [
      { pos: 1, label: "Champion",  icon: "🏆", team: fin.winner,   color: C.gold },
      { pos: 2, label: "Runner-up", icon: "🥈", team: fin.loser,    color: C.w55 },
      { pos: 3, label: "3rd Place", icon: "🥉", team: third.winner, color: "#CD7F32" },
      { pos: 4, label: "4th Place", icon: "4️⃣", team: third.loser,  color: C.w35 },
      { pos: 5, label: "5th Place", icon: "5️⃣", team: fifth.winner, color: C.w35 },
      { pos: 6, label: "6th Place", icon: "6️⃣", team: fifth.loser,  color: C.w35 },
    ];
  }, [koScores, finalA, finalB, thirdA, thirdB, fifthA, fifthB]);

  const anyResult = standings.some((s) => s.team !== null);

  return (
    <div className="anim-3" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {anyResult && (
        <div style={{
          background: C.card, border: `1px solid ${C.goldMed}`,
          borderRadius: 18, overflow: "hidden",
        }}>
          <div style={{
            padding: "14px 20px", borderBottom: `1px solid ${C.border}`,
            background: C.goldDim, display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>🏆</span>
            <div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 800, color: C.gold }}>
                Final Standings
              </div>
              <div style={{ fontSize: 10, color: C.w35, fontWeight: 600 }}>
                {finished ? "Tournament complete" : "Updates live as you enter scores below"}
              </div>
            </div>
          </div>
          <div style={{ padding: "12px 20px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {standings.map(({ pos, label, icon, team }) => (
              <div key={pos} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px", borderRadius: 10,
                background: pos === 1 ? "rgba(12,105,200,0.08)" : C.w08,
                border: `1px solid ${pos === 1 ? C.goldMed : C.border}`,
                opacity: team ? 1 : 0.35,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                  background: pos === 1 ? C.goldMed : C.w15,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Sora',sans-serif",
                  fontSize: 13, fontWeight: 900, color: pos === 1 ? C.gold : C.w55,
                }}>{pos}</div>
                {team ? (
                  <TeamAvatar isTeam src={team.img ?? null} name={team.name} size={28} />
                ) : (
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: C.w08, border: `1px dashed ${C.border2}`, flexShrink: 0,
                  }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: C.w35, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>
                    {label}
                  </div>
                  <div style={{
                    fontSize: 14, fontWeight: 700, color: pos === 1 ? C.gold : C.w80,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {team?.name ?? "—"}
                  </div>
                </div>
                {pos === 1 && team && <span style={{ fontSize: 20 }}>🏆</span>}
                {pos === 2 && team && <span style={{ fontSize: 20 }}>🥈</span>}
                {pos === 3 && team && <span style={{ fontSize: 20 }}>🥉</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 18, overflow: "hidden",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 20px", borderBottom: `1px solid ${C.border}`,
          background: C.goldDim,
        }}>
          <div style={{ fontSize: 22 }}>⚡</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 800, color: C.gold }}>
              Knockout Matches
            </div>
            <div style={{ fontSize: 10, color: C.w35, fontWeight: 600, letterSpacing: 0.5 }}>
              1st A vs 1st B · 2nd A vs 2nd B · 3rd A vs 3rd B
            </div>
          </div>
          {savingKO && <Spinner size={14} />}
          {finished && (
            <div style={{
              background: C.goldDim, border: `1px solid ${C.goldMed}`,
              borderRadius: 20, padding: "4px 12px",
              fontSize: 11, fontWeight: 800, color: C.gold,
            }}>Complete</div>
          )}
          {/* Read-only indicator when no save access */}
          {!onSaveKO && (
            <div style={{
              fontSize: 10, fontWeight: 700, color: C.w35,
              background: C.w08, borderRadius: 6, padding: "4px 10px",
              border: `1px solid ${C.border}`,
            }}>👁 View only</div>
          )}
        </div>

        <div className="ko-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, padding: "20px 24px" }}>
          <KOCard
            label="5th Place" icon="🎖"
            teamA={fifthA} teamB={fifthB}
            scKey="fifth" koScores={koScores} onChange={onSaveKO ? handleKO : null}
            winner={getWinner("fifth", fifthA, fifthB)}
          />
          <KOCard
            label="Final" icon="⭐" isFinal
            teamA={finalA} teamB={finalB}
            scKey="final" koScores={koScores} onChange={onSaveKO ? handleKO : null}
            winner={getWinner("final", finalA, finalB)}
          />
          <KOCard
            label="3rd Place" icon="🥉"
            teamA={thirdA} teamB={thirdB}
            scKey="third" koScores={koScores} onChange={onSaveKO ? handleKO : null}
            winner={getWinner("third", thirdA, thirdB)}
          />
        </div>

        {/* Save button — only shown when user has edit access */}
        {onSaveKO && (
          <div className="ko-save-btn-wrap" style={{ padding: "0 24px 24px" }}>
            <button
              className="save-btn"
              onClick={onSaveKO}
              disabled={savingKO}
              style={{
                width: "100%", padding: "14px 20px", borderRadius: 12, border: "none",
                cursor: "pointer",
                background: `linear-gradient(135deg,${C.gold},${C.gold2})`,
                color: "#000",
                fontFamily: "'Sora',sans-serif",
                fontSize: 15, fontWeight: 900, letterSpacing: 1.2,
                textTransform: "uppercase",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                boxShadow: `0 4px 16px ${C.gold}40`,
                transition: "all 0.2s",
                touchAction: "manipulation",
              }}
            >
              {savingKO
                ? <><Spinner size={16} color="#000" /> Saving…</>
                : <><span style={{ fontSize: 16 }}></span> Save & Publish Knockout Results</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function KOCard({ label, icon, isFinal, teamA, teamB, scKey, koScores, onChange, winner }) {
  const sc      = koScores[scKey] ?? { a: "", b: "" };
  const pending = !teamA || !teamB;
  const disabled = pending || !onChange;

  return (
    <div style={{
      background: isFinal ? C.goldDim : C.w08,
      border: `1px solid ${isFinal ? C.goldMed : C.border}`,
      borderRadius: 16, padding: "16px",
      opacity: pending ? 0.45 : 1,
      transition: "opacity 0.3s",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase",
        color: isFinal ? C.gold : C.w35,
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 2,
      }}>
        <span style={{ fontSize: 14 }}>{icon}</span> {label}
      </div>

      {winner && (
        <div style={{
          background: isFinal ? "rgba(12,105,200,0.25)" : C.w08,
          border: `1px solid ${isFinal ? C.goldMed : C.border2}`,
          borderRadius: 10, padding: "8px 12px",
          fontSize: 13, fontWeight: 800,
          color: isFinal ? C.gold : C.green,
          display: "flex", alignItems: "center", gap: 8,
          overflow: "hidden",
        }}>
          {isFinal ? "🏆" : "🥇"}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {winner.name}
          </span>
        </div>
      )}

      {[
        { team: teamA, side: "a", badge: "A" },
        { team: teamB, side: "b", badge: "B" },
      ].map(({ team, side, badge }) => (
        <div key={side} style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 10px", borderRadius: 10,
          background: "rgba(0,0,0,0.3)", border: `1px solid ${C.border}`,
        }}>
          <div style={{
            width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
            background: side === "a" ? C.gold : C.green,
            color: "#000", fontSize: 9, fontWeight: 900,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{badge}</div>
          <TeamAvatar isTeam src={team?.img ?? null} name={team?.name ?? "?"} size={22} />
          <span style={{
            flex: 1, fontSize: 12, fontWeight: 700,
            color: team ? C.w80 : C.w35,
            fontStyle: team ? "normal" : "italic",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {team?.name ?? "Awaiting…"}
          </span>
        </div>
      ))}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 4 }}>
        <ScoreInput size="lg" value={sc.a} onChange={(v) => onChange && onChange(scKey, "a", v)} disabled={disabled} />
        <span style={{ color: C.w35, fontWeight: 700, fontSize: 14 }}>vs</span>
        <ScoreInput size="lg" value={sc.b} onChange={(v) => onChange && onChange(scKey, "b", v)} disabled={disabled} />
      </div>
    </div>
  );
}

/* ─── STATS BANNER ───────────────────────────────────────────────────── */
function StatsBanner({ enrolled, tournament, matchResults }) {
  const total  = enrolled.length;
  const played = matchResults.length;
  const stage2 = (tournament?.stage_two_participants ?? []).length;

  return (
    <div className="stats-grid stats-banner" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 32 }}>
      {[
        { label: "Teams",          value: total,  code: "TM" },
        { label: "Results Saved",  value: played, code: "RS", accent: true },
        { label: "Advanced to KO", value: stage2, code: "KO" },
        { label: "Status", value: tournament?.tournament_finished ? "Done" : "Live", code: "LV", gold: true },
      ].map((s) => (
        <div key={s.label} style={{
          background: C.card, border: `1px solid ${s.gold || s.accent ? C.goldMed : C.border}`,
          borderRadius: 14, padding: "14px 16px",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: s.gold || s.accent ? C.goldDim : C.navy2, border: `1px solid ${s.gold || s.accent ? C.goldMed : C.border2}`, color: s.gold || s.accent ? C.gold : C.w80, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, flexShrink: 0 }}>
            {s.code}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 22, fontWeight: 900,
              fontFamily: "'Sora',sans-serif",
              color: s.gold || s.accent ? C.gold : C.white,
            }}>{s.value}</div>
            <div style={{ fontSize: 10, color: C.w55, fontWeight: 900, letterSpacing: 0.5, textTransform: "uppercase", whiteSpace: "nowrap" }}>{s.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── AUTH MODAL ─────────────────────────────────────────────────────── */
function SignInModal({ C, onClose, showToast }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) {
      setError("Please fill in all fields.");
      showToast("Please fill in all fields.", "error");
      return;
    }
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;
      showToast("Signed in!", "success");
      onClose();
    } catch (err) {
      setError(err?.message || "Login failed.");
      showToast(err?.message || "Login failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(6,16,31,0.95)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "0 16px",
    }}>
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 16, padding: "32px 24px",
        maxWidth: 400, width: "100%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        position: "relative",
      }}>
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 12, right: 12,
            background: "none", border: "none", color: C.w35,
            cursor: "pointer", fontSize: 20, padding: 4,
          }}
        >×</button>
        <div style={{ fontSize: 36, marginBottom: 16, textAlign: "center" }}>🔐</div>
        <div style={{
          fontFamily: "'Sora',sans-serif",
          fontSize: 18, fontWeight: 900, color: C.white, marginBottom: 8, textAlign: "center",
        }}>Sign In</div>
        <div style={{
          fontSize: 12, color: C.w55, marginBottom: 20, lineHeight: 1.5, textAlign: "center",
        }}>
          Sign in with the same ProAm account that created this tournament to open the dashboard.
        </div>
        <form onSubmit={handleSignIn} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            autoComplete="email"
            style={{
              width: "100%", padding: "14px 16px",
              borderRadius: 10, border: `1px solid ${C.border2}`,
              background: C.navy2, color: C.white,
              fontSize: 15, fontWeight: 600,
              fontFamily: "'Space Mono', monospace",
            }}
          />
          <div style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              style={{
                width: "100%", padding: "14px 16px", paddingRight: 44,
                borderRadius: 10, border: `1px solid ${C.border2}`,
                background: C.navy2, color: C.white,
                fontSize: 15, fontWeight: 600,
                fontFamily: "'Space Mono', monospace",
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", color: C.w35,
                cursor: "pointer", fontSize: 14, padding: 4,
              }}
            >
              {showPassword ? "👁" : "👁‍🗨"}
            </button>
          </div>
          {error && (
            <div style={{ fontSize: 11, color: C.red, fontWeight: 600 }}>{error}</div>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%", padding: "14px 20px",
              borderRadius: 10, border: "none",
              background: loading ? C.w08 : `linear-gradient(135deg,${C.gold},${C.gold2})`,
              color: loading ? C.w35 : "#000",
              fontFamily: "'Sora',sans-serif",
              fontSize: 15, fontWeight: 900, letterSpacing: 1.2,
              textTransform: "uppercase",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              touchAction: "manipulation",
            }}
          >
            {loading ? <><Spinner size={16} color="#000" /> Signing in…</> : "Sign In"}
          </button>
        </form>
        <div style={{
          marginTop: 16, fontSize: 11, color: C.w35, fontWeight: 600, textAlign: "center",
        }}>
          Don't have an account? Contact admin to register
        </div>
      </div>
    </div>
  );
}

/* ─── HEADER COMPONENT ───────────────────────────────────────────────── */
function Header({ adminUrl, showSettings, onEnterFullscreen }) {
  const navigate = useNavigate();

  return (
    <header className="public-live-topbar">
      <button
        type="button"
        className="public-live-brand"
        onClick={() => navigate("/tournaments/live")}
        aria-label="Open live tournaments"
      >
        <div className="public-live-brand-mark">
          <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
        </div>
        <span style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: 14, fontWeight: 700, color: "#0F1629",
        }}>
          ProAm <span style={{ color: "#0C69C8" }}>Tournaments</span>
        </span>
      </button>

      <div className="header-actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          className="public-live-icon-button"
          onClick={onEnterFullscreen}
          aria-label="Present tournament full screen"
          title="Present full screen"
        >
          <FontAwesomeIcon icon={faExpand} style={{ fontSize: 12 }} />
        </button>
        {showSettings && (
          <button
            type="button"
            className="public-live-icon-button"
            onClick={() => navigate(adminUrl || `${window.location.pathname}?admin=1#admin`)}
            aria-label="Open tournament settings"
          >
            <FontAwesomeIcon icon={faGear} style={{ fontSize: 12 }} />
          </button>
        )}
      </div>
    </header>
  );
}

/* ─── MAIN ───────────────────────────────────────────────────────────── */
export default function FootballFiesta() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tournamentName } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { isDark } = useTournamentTheme();
  const routeTournamentName = useMemo(
    () => decodeURIComponent(tournamentName || "").trim(),
    [tournamentName]
  );
  const [activeTournamentId, setActiveTournamentId] = useState(null);

  const [enrolled,      setEnrolled]      = useState([]);
  const [tournament,    setTournament]    = useState(null);
  const [matchResults,  setMatchResults]  = useState([]);
  const [engineMatches, setEngineMatches] = useState([]);
  const [queueRows,     setQueueRows]     = useState([]);
  const [adminRows,     setAdminRows]     = useState([]);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [loadError,     setLoadError]     = useState("");
  const [savingA,       setSavingA]       = useState(false);
  const [savingB,       setSavingB]       = useState(false);
  const [savingKO,      setSavingKO]      = useState(false);
  const [resetting,     setResetting]     = useState(false);

  const [groupAScores,  setGroupAScores]  = useState(null);
  const [groupBScores,  setGroupBScores]  = useState(null);
  const [koScores,      setKoScores]      = useState({
    final: { a: "", b: "" },
    third: { a: "", b: "" },
    fifth: { a: "", b: "" },
  });

  const [toast, setToast] = useState(null);
  const toastRef = useRef(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [accessPromptDismissed, setAccessPromptDismissed] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [simulationCount, setSimulationCount] = useState("8");
  const [simulationSaving, setSimulationSaving] = useState(false);
  const [formatSaving, setFormatSaving] = useState(false);
  const [matchSavingId, setMatchSavingId] = useState(null);
  const [participantSavingId, setParticipantSavingId] = useState(null);
  const [isFullscreenPresentation, setIsFullscreenPresentation] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const liveArenaRef = useRef(null);

  // Access control state
  const [accessCode,       setAccessCode]       = useState("");
  const [hasAccess,        setHasAccess]        = useState(false);
  const [isCreator,        setIsCreator]        = useState(false);
  const [isApprovedAdmin,  setIsApprovedAdmin]  = useState(false);
  const [tournamentAccess, setTournamentAccess] = useState(getDefaultTournamentAccess());
  const [authCheckComplete, setAuthCheckComplete] = useState(false);

  // Derived: can the current session edit?
  const isProTournament = isProTournamentPackage(tournament);
  const liveDashboardEnabled = readLiveDashboardEnabled(tournament);
  const canEdit = (isCreator || isApprovedAdmin || hasAccess) && !!user && hasTournamentEntitlement(tournamentAccess, "tournaments.dashboard.basic");
  const canManageWebDashboard = !!user && (isCreator || isApprovedAdmin) && isProTournament && (hasTournamentTier(tournamentAccess, "pro") || isProTournament);
  const adminUrl = `${location.pathname}?admin=1#admin`;
  const wantsAdmin = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("admin") === "1" || location.hash === "#admin";
  }, [location.search, location.hash]);
  const formattedDate = useMemo(() => {
    if (!tournament?.date) return null;
    try {
      return new Date(tournament.date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return null;
    }
  }, [tournament?.date]);
  const formattedTime = useMemo(() => {
    const time = tournament?.start_time || tournament?.time || tournament?.starts_at;
    if (!time) return null;
    if (typeof time === "string" && /^\d{2}:\d{2}/.test(time)) return time.slice(0, 5);
    try {
      return new Date(time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return String(time);
    }
  }, [tournament?.start_time, tournament?.time, tournament?.starts_at]);
  const isRaceTournamentPage = isFitnessRaceTournament(tournament);
  const pageFormatKey = useMemo(() => {
    if (isRaceTournamentPage) return FITNESS_RACE_FORMAT_KEY;
    const config = parseConfigObject(tournament?.format_config);
    return tournament?.format_key || config.format_key || (String(tournament?.game_mode || "").toLowerCase() === "point_based" ? "round_robin" : "single_elimination");
  }, [isRaceTournamentPage, tournament?.format_config, tournament?.format_key, tournament?.game_mode]);
  const pageGameType = isRaceTournamentPage ? FITNESS_RACE_GAME_TYPE : formatGameTypeLabel(tournament?.game_type || tournament?.gameType);
  const tournamentPackage = useMemo(() => {
    const config = parseConfigObject(tournament?.format_config);
    const key = tournament?.package_key || config.package_key || "tournaments_free";
    return TOURNAMENT_PACKAGE_OPTIONS[key] || TOURNAMENT_PACKAGE_OPTIONS.tournaments_free;
  }, [tournament?.format_config, tournament?.package_key]);
  const allowedFormats = useMemo(() => {
    const formats = Array.isArray(tournamentPackage?.formats) && tournamentPackage.formats.length ? tournamentPackage.formats : ["single_elimination"];
    return formats.filter((format) => TOURNAMENT_FORMATS[format]);
  }, [tournamentPackage]);
  const simulatedRows = useMemo(() => enrolled.filter(isSimulationRow), [enrolled]);
  const hasSimulationRows = simulatedRows.length > 0;
  const competitionRows = hasSimulationRows ? simulatedRows : enrolled;
  const competitionRowIds = useMemo(() => new Set(competitionRows.map((row) => row.id).filter(Boolean)), [competitionRows]);
  const isTeamTournament = isTeamTournamentFormat(tournament);
  const entrantLabel = competitionRows.length === 1
    ? isRaceTournamentPage ? "racer" : isTeamTournament ? "team" : "player"
    : isRaceTournamentPage ? "racers" : isTeamTournament ? "teams" : "players";
  const publicEntrantLabel = hasSimulationRows
    ? `${competitionRows.length} simulated ${entrantLabel}`
    : `${competitionRows.length} ${entrantLabel} enrolled`;

  const showToast = useCallback((msg, type = "success") => {
    clearTimeout(toastRef.current);
    setToast({ msg, type });
    toastRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const enterFullscreenPresentation = useCallback(async () => {
    const target = liveArenaRef.current;
    if (!target?.requestFullscreen) {
      showToast("Fullscreen is not available in this browser", "error");
      return;
    }
    try {
      await target.requestFullscreen();
      setIsFullscreenPresentation(true);
    } catch (err) {
      showToast(err?.message || "Could not enter fullscreen", "error");
    }
  }, [showToast]);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreenPresentation(document.fullscreenElement === liveArenaRef.current);
    };
    document.addEventListener("fullscreenchange", syncFullscreenState);
    return () => document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  useEffect(() => {
    setAccessPromptDismissed(false);
  }, [user?.id, tournament?.id]);

  // teamId → name map
  const teamNameMap = useMemo(() => {
    const map = {};
    competitionRows.forEach((e) => {
      const tid = e.team_id ?? e.profile_id ?? e.id;
      if (tid) map[tid] = e.team_name ?? e.name ?? tid.slice(0, 8);
    });
    return map;
  }, [competitionRows]);

  const buildScoresFromResults = useCallback((results, groupEnrolled) => {
    const scores = {};
    for (let i = 0; i < groupEnrolled.length; i++) {
      for (let j = i + 1; j < groupEnrolled.length; j++) {
        const rowA = groupEnrolled[i];
        const rowB = groupEnrolled[j];
        const key = pairKey(rowA.id, rowB.id);
        const match = results.find((r) => {
          const t1 = r.team1_id;
          const t2 = r.team2_id;
          const aId = rowA.team_id ?? rowA.id;
          const bId = rowB.team_id ?? rowB.id;
          return (t1 === aId && t2 === bId) || (t1 === bId && t2 === aId);
        });
        if (match) {
          const aId = rowA.team_id ?? rowA.id;
          const isAFirst = match.team1_id === aId;
          scores[key] = {
            a: String(isAFirst ? match.participant1_score : match.participant2_score),
            b: String(isAFirst ? match.participant2_score : match.participant1_score),
          };
        } else {
          scores[key] = { a: "", b: "" };
        }
      }
    }
    return scores;
  }, []);

  const buildKoScoresFromResults = useCallback((results, statsA, statsB) => {
    const koScores = {
      final: { a: "", b: "" },
      third: { a: "", b: "" },
      fifth: { a: "", b: "" },
    };
    const koMappings = [
      { key: "final", teamA: statsA[0], teamB: statsB[0] },
      { key: "third", teamA: statsA[1], teamB: statsB[1] },
      { key: "fifth", teamA: statsA[2], teamB: statsB[2] },
    ];
    for (const { key, teamA, teamB } of koMappings) {
      if (!teamA || !teamB) continue;
      const match = results.find((r) => {
        const t1 = r.team1_id;
        const t2 = r.team2_id;
        const aId = teamA.id;
        const bId = teamB.id;
        return (t1 === aId && t2 === bId) || (t1 === bId && t2 === aId);
      });
      if (match) {
        const aId = teamA.id;
        const isAFirst = match.team1_id === aId;
        koScores[key] = {
          a: String(isAFirst ? match.participant1_score : match.participant2_score),
          b: String(isAFirst ? match.participant2_score : match.participant1_score),
        };
      }
    }
    return koScores;
  }, []);

  /* ── FETCH ── */
  const fetchData = useCallback(async () => {
    setLoadError("");
    try {
      const isUuid = /^[0-9a-fA-F-]{36}$/.test(routeTournamentName);
      const tournamentQuery = supabase.from("tournaments").select("*");
      let t = null;
      let e2 = null;
      if (routeTournamentName) {
        if (isUuid) {
          const result = await tournamentQuery.eq("id", routeTournamentName).maybeSingle();
          t = result.data;
          e2 = result.error;
        } else {
          const bySlug = await tournamentQuery.eq("public_slug", routeTournamentName).maybeSingle();
          t = bySlug.data;
          e2 = bySlug.error;
          if (!e2 && !t) {
            const byName = await supabase
              .from("tournaments")
              .select("*")
              .eq("tournament_name", routeTournamentName)
              .maybeSingle();
            t = byName.data;
            e2 = byName.error;
          }
        }
      } else {
        throw new Error("Open a tournament from the live tournaments directory.");
      }
      if (e2) throw e2;
      if (!t?.id) throw new Error("Tournament not found");
      setActiveTournamentId(t.id);
      if (Number(t.engine_version) === 2) {
        setTournament(t);
        return;
      }

      const [
        { data: enr,  error: e1 },
        { data: tmr,  error: e3 },
        { data: engineRows, error: e4 },
        { data: queueData, error: e5 },
        { data: adminsData, error: e6 },
      ] = await Promise.all([
        supabase.from("tournament_enrolled").select("*").eq("tournament_id", t.id),
        supabase
          .from("tournament_match_results")
          .select("*")
          .eq("tournament_id", t.id)
          .order("played_at", { ascending: true }),
        supabase
          .from("tournament_matches")
          .select("*")
          .eq("tournament_id", t.id)
          .order("created_at", { ascending: true }),
        supabase
          .from("matchmaking_queue")
          .select("*")
          .eq("tournament_id", t.id),
        supabase
          .from("tournament_admins")
          .select("*")
          .eq("tournament_id", t.id),
      ]);
      if (e1) throw e1;
      if (e3) throw e3;
      if (e4) throw e4;
      if (e5) throw e5;
      if (e6) throw e6;
      const tournamentCreator = t.creator || t.organizer_profile_id || t.created_by || t.creator_id;
      const enrolledMemberProfileIds = (enr || []).flatMap((row) =>
        parseMemberList(row.members)
          .map((member) => member?.profile_id || member?.profileId || member?.id)
          .filter(Boolean)
      );
      const profileIds = [...new Set([
        tournamentCreator,
        ...(adminsData || []).map((row) => row.profile_id),
        ...(enr || []).map((row) => row.profile_id),
        ...(enr || []).map((row) => row.team_captain_id),
        ...enrolledMemberProfileIds,
      ].filter(Boolean))];
      const { data: profileRows, error: profileError } = profileIds.length
        ? await supabase.from("profiles").select("id, username, name, surname, profile_img, email").in("id", profileIds)
        : { data: [], error: null };
      if (profileError) throw profileError;
      const profilesById = new Map((profileRows || []).map((profile) => [profile.id, profile]));
      const enrichedEnrolled = (enr || []).map((row) => {
        const profile = row.profile_id ? profilesById.get(row.profile_id) : null;
        const captainProfile = row.team_captain_id ? profilesById.get(row.team_captain_id) : null;
        const profileName = profileDisplayName(profile, "");
        const members = parseMemberList(row.members).map((member) => {
          const memberId = member?.profile_id || member?.profileId || member?.id;
          const memberProfile = memberId ? profilesById.get(memberId) : null;
          return {
            ...member,
            id: member?.id || memberId,
            profile_id: member?.profile_id || memberId,
            username: member?.username ?? memberProfile?.username ?? null,
            name: member?.name ?? memberProfile?.name ?? null,
            surname: member?.surname ?? memberProfile?.surname ?? null,
            profile_img: member?.profile_img ?? memberProfile?.profile_img ?? null,
          };
        });
        return {
          ...row,
          profile,
          captain_profile: captainProfile || null,
          captain_profile_img: row.captain_profile_img ?? captainProfile?.profile_img ?? null,
          members,
          username: row.username ?? profile?.username ?? null,
          name: row.name ?? (profileName || null),
          profile_img: row.profile_img ?? profile?.profile_img ?? null,
        };
      });
      const enrichedAdmins = (adminsData || []).map((row) => ({
        ...row,
        profile: row.profile_id ? profilesById.get(row.profile_id) || null : null,
      }));
      const enrolledByRowId = new Map(enrichedEnrolled.map((row) => [row.id, row]));
      const legacyMatchIds = new Set((tmr || []).map((row) => row.tournament_match_id).filter(Boolean));
      const engineResults = (engineRows || [])
        .filter((match) => match.status === "completed" && !legacyMatchIds.has(match.id))
        .map((match) => {
          const p1 = enrolledByRowId.get(match.participant1_enrolled_id);
          const p2 = enrolledByRowId.get(match.participant2_enrolled_id);
          const winner = enrolledByRowId.get(match.winner_enrolled_id);
          const stage = Number(match.metadata?.stage ?? match.round_number ?? 1);
          return {
            id: `engine-${match.id}`,
            tournament_id: match.tournament_id,
            tournament_match_id: match.id,
            team1_id: p1 ? (p1.team_id ?? p1.profile_id ?? p1.id) : null,
            team2_id: p2 ? (p2.team_id ?? p2.profile_id ?? p2.id) : null,
            player1_profile_id: null,
            player2_profile_id: null,
            winner_team_id: winner ? (winner.team_id ?? winner.profile_id ?? winner.id) : null,
            winner_profile_id: null,
            participant1_score: match.participant1_score,
            participant2_score: match.participant2_score,
            is_draw: match.participant1_score === match.participant2_score,
            stage,
            fixture_round: match.round_number ?? match.metadata?.bracket_round ?? null,
            metadata: { ...(match.metadata || {}), stage },
            match_source: match.source || "engine",
          };
        });
      const mergedResults = [...(tmr || []), ...engineResults];
      setEnrolled(enrichedEnrolled);
      setMatchResults(mergedResults);
      if (engineRows) setEngineMatches(engineRows);
      if (queueData) setQueueRows(queueData);
      setAdminRows(enrichedAdmins);
      setCreatorProfile(tournamentCreator ? profilesById.get(tournamentCreator) || null : null);
      if (t) {
        setTournament(t);
        const sortedEnrolled = stableSort(getCompetitionRows(enrichedEnrolled));
        const groupA = sortedEnrolled.slice(0, 3);
        const groupB = sortedEnrolled.slice(3, 6);
        setGroupAScores(buildScoresFromResults(mergedResults, groupA));
        setGroupBScores(buildScoresFromResults(mergedResults, groupB));
        const statsA = deriveStats(groupA);
        const statsB = deriveStats(groupB);
        setKoScores(buildKoScoresFromResults(mergedResults, statsA, statsB));
      }
    } catch (err) {
      const message = err.message ?? "Failed to load tournament.";
      setTournament(null);
      setEnrolled([]);
      setMatchResults([]);
      setEngineMatches([]);
      setQueueRows([]);
      setAdminRows([]);
      setCreatorProfile(null);
      setLoadError(message);
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, buildScoresFromResults, buildKoScoresFromResults, routeTournamentName]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!wantsAdmin || loading || loadError) return;
    window.setTimeout(() => {
      document.getElementById("admin")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  }, [wantsAdmin, loading, loadError]);

  /* ── REALTIME ── */
  useEffect(() => {
    if (!activeTournamentId) return undefined;
    const ch = supabase
      .channel(`ff-live-${activeTournamentId}`)
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_enrolled",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, (payload) => {
        if (payload.eventType === "UPDATE")
          setEnrolled(prev => prev.map(e => e.id === payload.new.id ? { ...e, ...payload.new } : e));
        else if (payload.eventType === "INSERT")
          setEnrolled(prev => prev.some(e => e.id === payload.new.id) ? prev : [...prev, payload.new]);
        else if (payload.eventType === "DELETE")
          setEnrolled(prev => prev.filter(e => e.id !== payload.old.id));
      })
      .on("postgres_changes", {
        event: "UPDATE", schema: "public", table: "tournaments",
        filter: `id=eq.${activeTournamentId}`,
      }, (payload) => {
        setTournament(prev => ({ ...prev, ...payload.new }));
        const matchScores = payload.new.match_scores || {};
        setKoScores(matchScores.ko || {});
        setGroupAScores(matchScores.groups?.a || {});
        setGroupBScores(matchScores.groups?.b || {});
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_match_results",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_matches",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "matchmaking_queue",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "pending_tournaments",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_admins",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_referees",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_teams",
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_stages",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_groups",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "tournament_group_members",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .on("postgres_changes", {
        event: "*", schema: "public", table: "active_matches",
        filter: `tournament_id=eq.${activeTournamentId}`,
      }, () => {
        fetchData();
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [activeTournamentId, fetchData]);

  /* ── SPLIT GROUPS ── */
  const { groupA, groupB } = useMemo(() => {
    if (competitionRows.length === 0) return { groupA: [], groupB: [] };
    return splitGroups(competitionRows);
  }, [competitionRows]);

  /* ── AUTH: reset access when user logs out ── */
  useEffect(() => {
    if (!user) {
      // User has logged out — clear all edit privileges immediately
      setHasAccess(false);
      setIsCreator(false);
      setIsApprovedAdmin(false);
      setTournamentAccess(getDefaultTournamentAccess());
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!user?.id) return;
      try {
        const access = await loadTournamentAccess(user.id);
        if (mounted) setTournamentAccess(access);
      } catch {
        if (mounted) setTournamentAccess(getDefaultTournamentAccess());
      }
    };
    load();
    return () => { mounted = false; };
  }, [user?.id]);

  /* ── CHECK CREATOR & HANDLE ACCESS CODE ── */
  useEffect(() => {
    const hash = window.location.hash;
    const codeMatch = hash?.match(/code=([a-zA-Z0-9]+)/);
    if (codeMatch && user) {
      const code = codeMatch[1].toLowerCase();
      if (code === "tud") {
        setHasAccess(true);
      }
    }

    if (authLoading || !tournament) return;

    if (!user) {
      setIsCreator(false);
      setHasAccess(false);
      setAuthCheckComplete(true);
      return;
    }

    const tournamentCreator = tournament.creator || tournament.organizer_profile_id || tournament.created_by || tournament.creator_id;
    const currentUser = user.id;
    setIsCreator(!!(currentUser && tournamentCreator && currentUser === tournamentCreator));
    supabase
      .from("tournament_admins")
      .select("id, role")
      .eq("tournament_id", tournament.id)
      .eq("profile_id", currentUser)
      .maybeSingle()
      .then(({ data }) => {
        setIsApprovedAdmin(!!data);
        setAuthCheckComplete(true);
      })
      .catch(() => {
        setIsApprovedAdmin(false);
        setAuthCheckComplete(true);
      });
  }, [authLoading, user, tournament, navigate]);

  /* ── SAVE GROUP ── */
  const saveGroup = useCallback(async (computedStats, rawScores, sortedGroupRows, setSaving, groupLabel) => {
    // Auth guard
    if (!user) {
      showToast("You must be signed in to save results", "error");
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        computedStats.map(({ id, pts, gd, played }) =>
          supabase.from("tournament_enrolled")
            .update({ points: pts, point_difference: gd, played })
            .eq("id", id)
        )
      );

      const matchSource = `scoresheet_${groupLabel.toLowerCase()}`;
      await supabase
        .from("tournament_match_results")
        .delete()
        .eq("tournament_id", activeTournamentId)
        .eq("match_source", matchSource);

      const inserts = [];
      for (let i = 0; i < sortedGroupRows.length; i++) {
        for (let j = i + 1; j < sortedGroupRows.length; j++) {
          const rowA = sortedGroupRows[i];
          const rowB = sortedGroupRows[j];
          const key  = pairKey(rowA.id, rowB.id);
          const sc   = rawScores[key];
          if (!sc || sc.a === "" || sc.b === "") continue;
          const ga = parseInt(sc.a), gb = parseInt(sc.b);
          if (isNaN(ga) || isNaN(gb)) continue;
          const teamIdA = rowA.team_id ?? null;
          const teamIdB = rowB.team_id ?? null;
          inserts.push({
            tournament_id:       activeTournamentId,
            match_type:          "team",
            team1_id:            teamIdA,
            team2_id:            teamIdB,
            winner_team_id:      ga > gb ? teamIdA : gb > ga ? teamIdB : null,
            participant1_score:  ga,
            participant2_score:  gb,
            is_draw:             ga === gb,
            stage:               1,
            match_source:        matchSource,
          });
        }
      }
      if (inserts.length > 0) {
        const { error: insErr } = await supabase.from("tournament_match_results").insert(inserts);
        if (insErr) throw insErr;
      }

      showToast(`Group ${groupLabel} results saved`);
      await fetchData();
    } catch (err) {
      showToast(err.message ?? "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }, [showToast, fetchData, user, activeTournamentId]);

  const handleSaveA = useCallback(
    (computed, raw, sortedRows) => saveGroup(computed, raw, sortedRows, setSavingA, "A"),
    [saveGroup]
  );
  const handleSaveB = useCallback(
    (computed, raw, sortedRows) => saveGroup(computed, raw, sortedRows, setSavingB, "B"),
    [saveGroup]
  );

  /* ── SAVE KO ── */
  const handleSaveKO = useCallback(async () => {
    // Auth guard
    if (!user) {
      showToast("You must be signed in to save results", "error");
      return;
    }
    setSavingKO(true);
    try {
      const statsA      = deriveStats(groupA);
      const statsB      = deriveStats(groupB);
      const allEnrolled = [...groupA, ...groupB];

      const getResult = (key) => {
        const sc = koScores[key];
        if (!sc || sc.a === "" || sc.b === "") return null;
        const ga = parseInt(sc.a), gb = parseInt(sc.b);
        if (isNaN(ga) || isNaN(gb)) return null;
        return { scoreA: ga, scoreB: gb };
      };

      const finalA = statsA[0], finalB = statsB[0];
      const thirdA = statsA[1], thirdB = statsB[1];
      const fifthA = statsA[2], fifthB = statsB[2];

      const koMatchDefs = [
        { key: "final", teamA: finalA, teamB: finalB },
        { key: "third", teamA: thirdA, teamB: thirdB },
        { key: "fifth", teamA: fifthA, teamB: fifthB },
      ];

      await supabase
        .from("tournament_match_results")
        .delete()
        .eq("tournament_id", activeTournamentId)
        .eq("match_source", "scoresheet_ko");

      const koInserts = [];
      for (const { key, teamA, teamB } of koMatchDefs) {
        const result = getResult(key);
        if (!teamA || !teamB || !result) continue;
        const { scoreA, scoreB } = result;
        const rowA = allEnrolled.find((r) => (r.team_id ?? r.id) === teamA.id);
        const rowB = allEnrolled.find((r) => (r.team_id ?? r.id) === teamB.id);
        const teamIdA = rowA?.team_id ?? null;
        const teamIdB = rowB?.team_id ?? null;
        koInserts.push({
          tournament_id:      activeTournamentId,
          match_type:         "team",
          team1_id:           teamIdA,
          team2_id:           teamIdB,
          winner_team_id:     scoreA > scoreB ? teamIdA : scoreB > scoreA ? teamIdB : null,
          participant1_score: scoreA,
          participant2_score: scoreB,
          is_draw:            scoreA === scoreB,
          stage:              2,
          match_source:       "scoresheet_ko",
        });
      }
      if (koInserts.length > 0) {
        const { error: insErr } = await supabase.from("tournament_match_results").insert(koInserts);
        if (insErr) throw insErr;
      }

      const finalResult = getResult("final");
      const stage3 = [finalA?.id, finalB?.id].filter(Boolean);
      const stage2 = [finalA?.id, finalB?.id, thirdA?.id, thirdB?.id].filter(Boolean);

      await supabase.from("tournaments").update({
        stage_two_participants:   stage2,
        stage_three_participants: stage3,
        ...(finalResult ? { tournament_finished: true } : {}),
      }).eq("id", activeTournamentId);

      for (const { key, teamA, teamB } of koMatchDefs) {
        const result = getResult(key);
        if (!teamA || !teamB || !result) continue;
        const { scoreA, scoreB } = result;
        const rowA = allEnrolled.find((r) => (r.team_id ?? r.id) === teamA.id);
        const rowB = allEnrolled.find((r) => (r.team_id ?? r.id) === teamB.id);
        if (!rowA || !rowB) continue;
        const idA  = rowA.team_id ?? rowA.id;
        const idB  = rowB.team_id ?? rowB.id;
        const ptsA = scoreA > scoreB ? 3 : scoreA === scoreB ? 1 : 0;
        const ptsB = scoreB > scoreA ? 3 : scoreA === scoreB ? 1 : 0;
        await Promise.all([
          supabase.from("tournament_enrolled").update({
            points: (rowA.points ?? 0) + ptsA,
            point_difference: (rowA.point_difference ?? 0) + (scoreA - scoreB),
            played: [...new Set([...(rowA.played ?? []), idB])],
          }).eq("id", rowA.id),
          supabase.from("tournament_enrolled").update({
            points: (rowB.points ?? 0) + ptsB,
            point_difference: (rowB.point_difference ?? 0) + (scoreB - scoreA),
            played: [...new Set([...(rowB.played ?? []), idA])],
          }).eq("id", rowB.id),
        ]);
      }

      showToast("Knockout results saved");
      await fetchData();
    } catch (err) {
      showToast(err.message ?? "KO save failed", "error");
    } finally {
      setSavingKO(false);
    }
  }, [koScores, groupA, groupB, showToast, fetchData, user, activeTournamentId]);

  /* ── RESET TOURNAMENT ── */
  const handleResetTournament = useCallback(() => {
    setShowResetConfirm(true);
  }, []);

  const confirmReset = useCallback(async () => {
    if (!user) {
      showToast("You must be signed in to reset", "error");
      return;
    }
    setShowResetConfirm(false);
    setResetting(true);
    try {
      await supabase.from("tournament_match_results").delete().eq("tournament_id", activeTournamentId);
      await supabase.from("tournament_matches").delete().eq("tournament_id", activeTournamentId);
      await supabase.from("tournament_group_members").delete().eq("tournament_id", activeTournamentId);
      await supabase.from("tournament_groups").delete().eq("tournament_id", activeTournamentId);
      await supabase.from("tournament_stages").delete().eq("tournament_id", activeTournamentId);
      await supabase.from("matchmaking_queue").delete().eq("tournament_id", activeTournamentId);
      await supabase.from("active_matches").delete().eq("tournament_id", activeTournamentId);
      const resetPromises = enrolled.map((team) =>
        supabase.from("tournament_enrolled").update({
          points: 0,
          point_difference: 0,
          played: [],
          knocked_out: false,
          knocked_out2: false,
          status: 0,
        }).eq("id", team.id)
      );
      await Promise.all(resetPromises);
      await supabase.from("tournaments").update({
        stage_two_participants: [],
        stage_three_participants: [],
        grand_final_participants: [],
        losers_bracket: {},
        tournament_finished: false,
        match_scores: {},
      }).eq("id", activeTournamentId);

      showToast("Tournament reset successfully", "success");
      await fetchData();
    } catch (err) {
      showToast(err.message ?? "Failed to reset tournament", "error");
    } finally {
      setResetting(false);
    }
  }, [enrolled, showToast, fetchData, user, activeTournamentId]);

  const cancelReset = useCallback(() => { setShowResetConfirm(false); }, []);

  const changeTournamentFormat = useCallback(async (nextFormatKey) => {
    if (!canEdit || !tournament?.id) {
      showToast("Creator/admin access is required to change format", "error");
      return;
    }
    if (tournament.status && tournament.status !== "pending") {
      showToast("Tournament format is locked after approval.", "error");
      return;
    }
    if (nextFormatKey === pageFormatKey) return;
    if (!allowedFormats.includes(nextFormatKey)) {
      showToast(`${tournamentPackage.name} does not include ${TOURNAMENT_FORMATS[nextFormatKey]?.label || "that format"}`, "error");
      return;
    }
    const nextFormat = TOURNAMENT_FORMATS[nextFormatKey] || TOURNAMENT_FORMATS.single_elimination;
    const rule = getFormatParticipantRule(nextFormatKey, competitionRows.length, tournamentPackage.maxParticipants);
    if (!rule.valid) {
      showToast(rule.error, "error");
      return;
    }
    const confirmed = window.confirm(`Switch to ${nextFormat.label}? Existing scores, progression assignments, queue entries, and bracket progress will reset so the new format can work correctly.`);
    if (!confirmed) return;
    setFormatSaving(true);
    try {
      await Promise.all([
        supabase.from("tournament_enrolled").update({
          points: 0,
          point_difference: 0,
          played: [],
          knocked_out: false,
          knocked_out2: false,
          status: 0,
        }).eq("tournament_id", tournament.id),
        supabase.from("tournaments").update({
          game_mode: nextFormat.mode,
          format_key: nextFormatKey,
          format_config: {
            ...(parseConfigObject(tournament?.format_config)),
            format_key: nextFormatKey,
            package_key: tournamentPackage.key,
            mode: nextFormat.mode,
            config: nextFormat.config || {},
          },
          stage_two_participants: [],
          stage_three_participants: [],
          grand_final_participants: [],
          losers_bracket: {},
          tournament_finished: false,
          match_scores: {},
        }).eq("id", tournament.id),
        supabase.from("tournament_match_results").delete().eq("tournament_id", tournament.id),
        supabase.from("tournament_matches").delete().eq("tournament_id", tournament.id),
        supabase.from("matchmaking_queue").delete().eq("tournament_id", tournament.id),
      ]);
      showToast(`Format updated to ${nextFormat.label}`, "success");
      await fetchData();
    } catch (err) {
      showToast(err.message || "Could not change tournament format", "error");
    } finally {
      setFormatSaving(false);
    }
  }, [allowedFormats, canEdit, competitionRows.length, fetchData, pageFormatKey, showToast, tournament, tournamentPackage]);

  const generateSimulation = useCallback(async () => {
    if (!canEdit || !tournament?.id) {
      showToast("Creator/admin access is required to generate simulations", "error");
      return;
    }
    const count = Math.max(2, Math.min(16, parseInt(simulationCount, 10) || 8));
    setSimulationSaving(true);
    try {
      await supabase.from("matchmaking_queue").delete().eq("tournament_id", tournament.id);
      await supabase.from("tournament_match_results").delete().eq("tournament_id", tournament.id);
      await supabase.from("tournament_matches").delete().eq("tournament_id", tournament.id);
      await supabase.from("tournament_enrolled").delete().eq("tournament_id", tournament.id).eq("is_simulated", true);
      await supabase.from("tournaments").update({
        stage_two_participants: [],
        stage_three_participants: [],
        grand_final_participants: [],
        losers_bracket: {},
        tournament_finished: false,
        match_scores: {},
      }).eq("id", tournament.id);

      const { data: avatarRows } = await supabase.from("avatars").select("url").order("id", { ascending: true });
      const avatarUrls = (avatarRows || []).map((row) => row.url).filter(Boolean);
      const isTeam = isTeamTournamentFormat(tournament);
      const seed = `${Date.now()}`;
      const rows = Array.from({ length: count }).map((_, index) => {
        const n = index + 1;
        const avatarUrl = avatarUrls.length ? avatarUrls[index % avatarUrls.length] : null;
        const displayName = isTeam ? `Test Team ${n}` : `Test Player ${n}`;
        return {
          tournament_id: tournament.id,
          team_id: `sim-${seed}-${n}`,
          team_name: displayName,
          team_image: avatarUrl,
          profile_img: avatarUrl,
          members: isTeam ? [{ name: `Player ${n}A`, profile_img: avatarUrl }, { name: `Player ${n}B`, profile_img: avatarUrl }] : [],
          team_type: isTeam ? "simulation_team" : "simulation_individual",
          is_simulated: true,
          simulation_seed: seed,
          knocked_out: false,
          knocked_out2: false,
          status: 0,
          points: 0,
          point_difference: 0,
          played: [],
          is_open: false,
        };
      });
      const { error } = await supabase.from("tournament_enrolled").insert(rows);
      if (error) throw error;
      showToast("Simulation ready", "success");
      await fetchData();
    } catch (err) {
      showToast(err.message || "Could not generate simulation", "error");
    } finally {
      setSimulationSaving(false);
    }
  }, [canEdit, fetchData, showToast, simulationCount, tournament]);

  const clearSimulation = useCallback(async () => {
    if (!canEdit || !tournament?.id) {
      showToast("Creator/admin access is required to clear simulations", "error");
      return;
    }
    setSimulationSaving(true);
    try {
      await supabase.from("matchmaking_queue").delete().eq("tournament_id", tournament.id);
      await supabase.from("tournament_match_results").delete().eq("tournament_id", tournament.id);
      await supabase.from("tournament_matches").delete().eq("tournament_id", tournament.id);
      await supabase.from("tournament_enrolled").delete().eq("tournament_id", tournament.id).eq("is_simulated", true);
      await fetchData();
      showToast("Simulation cleared", "success");
    } catch (err) {
      showToast(err.message || "Could not clear simulation", "error");
    } finally {
      setSimulationSaving(false);
    }
  }, [canEdit, fetchData, showToast, tournament]);

  const saveAdminMatchResult = useCallback(async (match, rawScoreA, rawScoreB) => {
    if (!canEdit || !tournament?.id) {
      showToast("Creator/admin access is required to save results", "error");
      return false;
    }
    const scoreA = parseInt(rawScoreA, 10);
    const scoreB = parseInt(rawScoreB, 10);
    if (Number.isNaN(scoreA) || Number.isNaN(scoreB)) {
      showToast("Enter valid scores for both players", "error");
      return false;
    }
    const gameMode = String(tournament?.game_mode || "").toLowerCase();
    const formatKey = pageFormatKey;
    if (gameMode === "knockout" && scoreA === scoreB) {
      showToast("Knockout games need a winner", "error");
      return false;
    }
    const rowA = competitionRows.find((row) => row.id === match.participant1_enrolled_id);
    const rowB = competitionRows.find((row) => row.id === match.participant2_enrolled_id);
    if (!rowA || !rowB) {
      showToast("Could not find both players for this game", "error");
      return false;
    }
    const winnerRow = scoreA === scoreB ? null : scoreA > scoreB ? rowA : rowB;
    const stage = Number(match.metadata?.stage ?? match.round_number ?? 1);
    const bracketRound = Number(match.metadata?.bracket_round ?? match.round_number ?? 1);
    const matchType = rowA.team_id || rowB.team_id ? "team" : "individual";
    setMatchSavingId(match.id);
    try {
      let savedMatchId = match.id;
      if (match.is_computed) {
        const { data: insertedMatch, error: insertMatchError } = await supabase
          .from("tournament_matches")
          .insert({
            tournament_id: tournament.id,
            round_number: bracketRound,
            match_number: Math.max(1, Math.floor(Date.now() % 2147483647)),
            participant1_enrolled_id: rowA.id,
            participant2_enrolled_id: rowB.id,
            participant1_score: scoreA,
            participant2_score: scoreB,
            winner_enrolled_id: winnerRow?.id ?? null,
            status: "completed",
            source: "dashboard",
            metadata: {
              ...(match.metadata || {}),
              stage,
              bracket_round: bracketRound,
              format_key: formatKey,
            },
          })
          .select("id")
          .single();
        if (insertMatchError) throw insertMatchError;
        savedMatchId = insertedMatch.id;
      } else {
        const { error: updateError } = await supabase
          .from("tournament_matches")
          .update({
          participant1_score: scoreA,
          participant2_score: scoreB,
          winner_enrolled_id: winnerRow?.id ?? null,
          status: "completed",
          source: match.source || "dashboard",
          metadata: {
            ...(match.metadata || {}),
            stage,
            bracket_round: bracketRound,
            format_key: formatKey,
          },
        })
        .eq("id", match.id);
        if (updateError) throw updateError;
      }

      const canWriteLegacyResult = matchType === "team"
        ? isUuidValue(rowA.team_id) && isUuidValue(rowB.team_id) && (!winnerRow?.team_id || isUuidValue(winnerRow.team_id))
        : isUuidValue(rowA.profile_id) && isUuidValue(rowB.profile_id) && (!winnerRow?.profile_id || isUuidValue(winnerRow.profile_id));
      if (canWriteLegacyResult) {
        await supabase.from("tournament_match_results").delete().eq("tournament_match_id", savedMatchId);
        const { error: resultError } = await supabase.from("tournament_match_results").insert({
          tournament_id: tournament.id,
          tournament_match_id: savedMatchId,
          match_type: matchType,
          team1_id: matchType === "team" ? rowA.team_id : null,
          team2_id: matchType === "team" ? rowB.team_id : null,
          player1_profile_id: matchType === "individual" ? rowA.profile_id : null,
          player2_profile_id: matchType === "individual" ? rowB.profile_id : null,
          winner_team_id: matchType === "team" ? winnerRow?.team_id ?? null : null,
          winner_profile_id: matchType === "individual" ? winnerRow?.profile_id ?? null : null,
          participant1_score: scoreA,
          participant2_score: scoreB,
          is_draw: scoreA === scoreB,
          stage,
          match_source: "dashboard",
        });
        if (resultError) throw resultError;
      }

      const sourceMatches = match.is_computed
        ? [...engineMatches, {
          ...match,
          id: savedMatchId,
          participant1_score: scoreA,
          participant2_score: scoreB,
          winner_enrolled_id: winnerRow?.id ?? null,
          status: "completed",
          metadata: { ...(match.metadata || {}), stage, bracket_round: bracketRound, format_key: formatKey },
        }]
        : engineMatches;
      const nextMatches = sourceMatches
        .map((row) => row.id === match.id ? {
          ...row,
          participant1_score: scoreA,
          participant2_score: scoreB,
          winner_enrolled_id: winnerRow?.id ?? null,
          status: "completed",
          metadata: { ...(row.metadata || {}), stage, bracket_round: bracketRound, format_key: formatKey },
        } : row)
        .filter((row) =>
          row.status === "completed" &&
          row.participant1_enrolled_id &&
          row.participant2_enrolled_id &&
          (!hasSimulationRows || (competitionRowIds.has(row.participant1_enrolled_id) && competitionRowIds.has(row.participant2_enrolled_id)))
        )
        .sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
      const nextRows = competitionRows.map((row) => ({
        ...row,
        points: 0,
        point_difference: 0,
        played: [],
        knocked_out: false,
        knocked_out2: false,
        status: 0,
      }));
      const rowMap = new Map(nextRows.map((row) => [row.id, row]));
      for (const savedMatch of nextMatches) {
        const a = rowMap.get(savedMatch.participant1_enrolled_id);
        const b = rowMap.get(savedMatch.participant2_enrolled_id);
        if (!a || !b) continue;
        const sa = Number(savedMatch.participant1_score);
        const sb = Number(savedMatch.participant2_score);
        const identityA = getParticipantId(a);
        const identityB = getParticipantId(b);
        if (String(tournament?.game_mode || "").toLowerCase() === "point_based") {
          a.points = (a.points ?? 0) + (sa > sb ? 3 : sa === sb ? 1 : 0);
          b.points = (b.points ?? 0) + (sb > sa ? 3 : sb === sa ? 1 : 0);
          a.point_difference = (a.point_difference ?? 0) + (sa - sb);
          b.point_difference = (b.point_difference ?? 0) + (sb - sa);
          a.played = [...(a.played ?? []), identityB];
          b.played = [...(b.played ?? []), identityA];
          continue;
        }
        if (sa === sb) continue;
        const win = sa > sb ? a : b;
        const lose = sa > sb ? b : a;
        const winnerIdentity = sa > sb ? identityA : identityB;
        const loserIdentity = sa > sb ? identityB : identityA;
        const isDouble = formatKey === "double_elimination";
        const isRedemption = isDouble && !!(a.knocked_out || b.knocked_out);
        const isInitialWinnersLoss = isDouble && !isRedemption && (lose.status ?? 0) === 0;
        const isLaterWinnersLoss = isDouble && !isRedemption && (lose.status ?? 0) > 0;
        win.status = isRedemption ? (win.status ?? 0) - 1 : (win.status ?? 0) + 1;
        win.played = [...(win.played ?? []), loserIdentity];
        lose.status = isRedemption ? (lose.status ?? 0) - 1 : isDouble ? (isInitialWinnersLoss ? -1 : (lose.status ?? 0)) : (lose.status ?? 0) - 1;
        lose.knocked_out = true;
        lose.knocked_out2 = isRedemption || isLaterWinnersLoss ? true : (lose.knocked_out2 ?? false);
        lose.played = [...(lose.played ?? []), winnerIdentity];
      }
      const aliveRows = String(tournament?.game_mode || "").toLowerCase() === "knockout"
        ? nextRows.filter((row) => formatKey === "double_elimination" ? !row.knocked_out2 : !row.knocked_out && !row.knocked_out2)
        : [];
      const tournamentFinished = aliveRows.length === 1 && nextMatches.length > 0;
      await Promise.all([
        ...nextRows.map((row) => supabase
          .from("tournament_enrolled")
          .update({
            points: row.points ?? 0,
            point_difference: row.point_difference ?? 0,
            played: row.played ?? [],
            knocked_out: row.knocked_out ?? false,
            knocked_out2: row.knocked_out2 ?? false,
            status: row.status ?? 0,
          })
          .eq("id", row.id)),
        supabase.from("tournaments").update({ tournament_finished: tournamentFinished }).eq("id", tournament.id),
      ]);
      showToast("Match result saved");
      await fetchData();
      return true;
    } catch (err) {
      showToast(err.message || "Could not save match result", "error");
      return false;
    } finally {
      setMatchSavingId(null);
    }
  }, [canEdit, competitionRowIds, competitionRows, engineMatches, fetchData, hasSimulationRows, pageFormatKey, showToast, tournament]);

  const updateAdminParticipantStatus = useCallback(async (row, updates) => {
    if (!canEdit || !row?.id) {
      showToast("Creator/admin access is required to change player status", "error");
      return;
    }
    setParticipantSavingId(row.id);
    try {
      const payload = {};
      if (Object.prototype.hasOwnProperty.call(updates, "status")) payload.status = updates.status;
      if (Object.prototype.hasOwnProperty.call(updates, "knocked_out")) payload.knocked_out = updates.knocked_out;
      if (Object.prototype.hasOwnProperty.call(updates, "knocked_out2")) payload.knocked_out2 = updates.knocked_out2;
      const { error } = await supabase.from("tournament_enrolled").update(payload).eq("id", row.id);
      if (error) throw error;
      showToast("Player status updated");
      await fetchData();
    } catch (err) {
      showToast(err.message || "Could not update player status", "error");
    } finally {
      setParticipantSavingId(null);
    }
  }, [canEdit, fetchData, showToast]);

  const handleLoginClick  = useCallback(() => { setShowSignInModal(true); }, []);

  /* ── LOGOUT — reset access state then refresh page ── */
  const handleLogoutClick = useCallback(async () => {
    await supabase.auth.signOut();
    setHasAccess(false);
    setIsCreator(false);
    setIsApprovedAdmin(false);
    showToast("Logged out", "success");
    // Brief delay so toast is visible, then hard refresh to clear all state
    setTimeout(() => { window.location.reload(); }, 900);
  }, [showToast]);

  const handleSubmitCode = () => {
    if (!user) {
      showToast("You must be logged in to edit", "error");
      return;
    }
    const code = accessCode.toLowerCase().trim();
    if (code === "tud") {
      setHasAccess(true);
      showToast("Access granted", "success");
    } else {
      showToast("Invalid code", "error");
    }
  };

  /* ── RENDER ── */
  if (Number(tournament?.engine_version) === 2) return <ConfigurableTournament key={tournament.id} tournament={tournament} />;
  return (
    <>
      <style>{GLOBAL_CSS}{tournamentThemeVars(isDark)}</style>
      <div ref={liveArenaRef} className={`public-live-arena ${isFullscreenPresentation ? "tv-fullscreen" : ""}`} style={{ position: "relative" }}>
        <div className="public-live-tv-exit-hint">Press Esc to exit fullscreen</div>

        {loading && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 500,
            background: "rgba(10,29,68,0.92)", backdropFilter: "blur(4px)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 16,
          }}>
            <Spinner size={38} />
            <span role="status" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }}>Loading tournament</span>
          </div>
        )}

        {/* Auth check overlay */}
        {!authCheckComplete && !loading && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 600,
            background: "rgba(10,29,68,0.96)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Spinner size={38} />
          </div>
        )}

        <div className="tournament-shell">

          <Header adminUrl={adminUrl} showSettings={!!user && (isCreator || isApprovedAdmin)} onEnterFullscreen={enterFullscreenPresentation} />

          {loadError && !loading && (
            <section className="public-live-stage">
              <div className="app-tree-shell" role="alert">
                <div className="public-live-state">
                  <div>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 30, fontWeight: 900, color: "#FFFFFF", marginBottom: 10 }}>
                      Bracket failed to load
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.62)", fontSize: 15, fontWeight: 700, marginBottom: 18 }}>
                      Please refresh or try again.
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.46)", fontSize: 12, fontWeight: 700, marginBottom: 18 }}>
                      {loadError}
                    </div>
                    <button type="button" onClick={fetchData} style={{
                      border: "1px solid rgba(255,255,255,0.18)",
                      background: "rgba(255,255,255,0.12)",
                      color: "#FFFFFF",
                      borderRadius: 999,
                      padding: "11px 18px",
                      fontSize: 12,
                      fontWeight: 900,
                      cursor: "pointer",
                    }}>
                      Retry
                    </button>
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => navigate("/tournaments/live")} style={{
                marginTop: 16,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.10)",
                color: "#FFFFFF",
                borderRadius: 999,
                padding: "10px 14px",
                fontSize: 12,
                fontWeight: 900,
                cursor: "pointer",
              }}>
                View Current Tournaments
              </button>
            </section>
          )}

          {!loadError && (
          <main className="public-live-stage">
            <div className="public-live-title-block">
              <div className="public-live-kicker">TOURNAMENT</div>
              <h1>{tournament?.tournament_name || "Tournament"}</h1>
              {tournament?.id && (
                <nav className="tournament-app-actions" aria-label="Join this tournament">
                  <a href={`snowsproam://tournament/${encodeURIComponent(tournament.id)}`}>Open tournament in app</a>
                  <a href="https://linktr.ee/snowsproam">Get the app</a>
                </nav>
              )}
              <div className="public-live-meta">
                <span>{publicEntrantLabel}</span>
                {formattedDate && (
                  <>
                    <span>·</span>
                    <span>{formattedDate}</span>
                  </>
                )}
                {formattedTime && (
                  <>
                    <span>·</span>
                    <span>{formattedTime}</span>
                  </>
                )}
              </div>
              <div className="public-live-detail-row" aria-label="Tournament details">
                {isRaceTournamentPage ? (
                  <>
                    <div className="public-live-chip"><span>Sport</span>{tournament?.sport || "Fitness"}</div>
                    <div className="public-live-chip"><span>Format</span>{FITNESS_RACE_GAME_TYPE}</div>
                  </>
                ) : (
                  <>
                    <div className="public-live-chip"><span>Type</span>{pageGameType}</div>
                    <div className="public-live-chip"><span>Format</span>{formatTournamentFormatLabel(pageFormatKey)}</div>
                    {tournament?.game_mode && (
                      <div className="public-live-chip"><span>Mode</span>{String(tournament.game_mode).replace(/_/g, " ")}</div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="public-live-bracket-header">
              <div className="bracket-section-label">
                <span />
                {isRaceTournamentPage ? "RACE RESULT" : "BRACKET"}
              </div>
              <div className="bracket-helper">
                {isRaceTournamentPage ? "Follow participants and final race places in real time." : "Follow each round from seed to final in real time."}
              </div>
            </div>

            <AppTournamentTree
              enrolled={competitionRows}
              matchResults={matchResults}
              tournament={tournament}
              onParticipantOpen={setSelectedParticipant}
            />

            {wantsAdmin && (
              <div style={{ marginTop: 22 }}>
                <TournamentAdminConsole
                  tournament={tournament}
                  enrolled={enrolled}
                  matchResults={matchResults}
                  engineMatches={engineMatches}
                  queueRows={queueRows}
                  adminRows={adminRows}
                  creatorProfile={creatorProfile}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  canEdit={canEdit}
                  canManageWebDashboard={canManageWebDashboard}
                  isCreator={isCreator}
                  isApprovedAdmin={isApprovedAdmin}
                  liveDashboardEnabled={liveDashboardEnabled}
                  onRefresh={fetchData}
                  onReset={() => setShowResetConfirm(true)}
                  resetting={resetting}
                  simulationCount={simulationCount}
                  setSimulationCount={setSimulationCount}
                  onGenerateSimulation={generateSimulation}
                  onClearSimulation={clearSimulation}
                  simSaving={simulationSaving}
                  pageFormatKey={pageFormatKey}
                  allowedFormats={allowedFormats}
                  tournamentPackage={tournamentPackage}
                  onChangeFormat={changeTournamentFormat}
                  formatSaving={formatSaving}
                  onSubmitMatch={saveAdminMatchResult}
                  matchSavingId={matchSavingId}
                  onUpdateParticipantStatus={updateAdminParticipantStatus}
                  participantSavingId={participantSavingId}
                  showToast={showToast}
                  userId={user?.id}
                />
              </div>
            )}
          </main>
          )}
        </div>

        {toast && <Toast msg={toast.msg} type={toast.type} />}
        <ParticipantDetailModal
          participant={selectedParticipant}
          tournament={tournament}
          onClose={() => setSelectedParticipant(null)}
        />

        {/* Access Code Modal — only shown to logged-in users who aren't creator/haven't entered code */}
      {false && user && !hasAccess && !isCreator && authCheckComplete && !accessPromptDismissed && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(6,16,31,0.95)", backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 16px",
          }}>
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 16, padding: "32px 24px",
              maxWidth: 400, width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
              textAlign: "center",
              position: "relative",
            }}>
              <button
                type="button"
                aria-label="Close dashboard access modal"
                onClick={() => setAccessPromptDismissed(true)}
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  border: `1px solid ${C.border2}`,
                  background: C.w08,
                  color: C.w55,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 13 }} />
              </button>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                margin: "0 auto 16px",
                background: C.goldDim,
                border: `1px solid ${C.goldMed}`,
                color: C.gold,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <FontAwesomeIcon icon={faPen} style={{ fontSize: 16 }} />
              </div>
              <div style={{
                fontFamily: "'Sora',sans-serif",
                fontSize: 18, fontWeight: 900, color: C.white, marginBottom: 8,
              }}>Dashboard Access</div>
              <div style={{
                fontSize: 12, color: C.w55, marginBottom: 20, lineHeight: 1.5,
              }}>
                If this is your tournament, sign in with the same ProAm account used in the app. Otherwise enter an admin access code.
              </div>
              <input
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitCode()}
                placeholder="Enter code..."
                autoFocus
                style={{
                  width: "100%", padding: "14px 16px",
                  borderRadius: 10, border: `1px solid ${C.border2}`,
                  background: C.navy2, color: C.white,
                  fontSize: 15, fontWeight: 600, textAlign: "center",
                  fontFamily: "'Space Mono', monospace",
                  marginBottom: 16,
                  textTransform: "uppercase",
                }}
              />
              <button
                onClick={handleSubmitCode}
                style={{
                  width: "100%", padding: "14px 20px",
                  borderRadius: 10, border: "none",
                  background: `linear-gradient(135deg,${C.gold},${C.gold2})`,
                  color: "#000",
                  fontFamily: "'Sora',sans-serif",
                  fontSize: 15, fontWeight: 900, letterSpacing: 1.2,
                  textTransform: "uppercase",
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                Submit
              </button>
              <button
                onClick={() => supabase.auth.signOut().then(() => { setHasAccess(false); setIsCreator(false); })}
                style={{
                  marginTop: 12,
                  background: "none", border: "none",
                  color: C.w35, fontSize: 12, cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                View as guest instead
              </button>
            </div>
          </div>
        )}

        {showSignInModal && !user && (
          <SignInModal C={C} onClose={() => setShowSignInModal(false)} showToast={showToast} />
        )}

        {showResetConfirm && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 16px",
          }}>
            <div style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 16, padding: "32px 24px",
              maxWidth: 420, width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
              textAlign: "center",
            }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 12, fontWeight: 900, color: C.red, textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Warning</div>
              <div style={{
                fontFamily: "'Sora',sans-serif",
                fontSize: 20, fontWeight: 900, color: C.white, marginBottom: 12,
              }}>Reset Tournament?</div>
              <div style={{ fontSize: 13, color: C.w55, marginBottom: 24, lineHeight: 1.5 }}>
                This will delete all scores, results, and standings. This action cannot be undone.
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <button
                  onClick={cancelReset}
                  style={{
                    padding: "12px 24px", borderRadius: 10, border: "none",
                    background: C.w08, color: C.w55,
                    fontSize: 14, fontWeight: 700, cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >Cancel</button>
                <button
                  onClick={confirmReset}
                  style={{
                    padding: "12px 24px", borderRadius: 10, border: "none",
                    background: C.red, color: C.white,
                    fontSize: 14, fontWeight: 700, cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >Yes, Reset</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
